import { run } from '/shared/engine.js';
import { outlineHulls, outlineParams } from '/shared/outline.js';
import { applyTutorialLook, paperSurface, tutorialParams } from '/shared/tutorial.js';
import { addLights, buildWorld, placeLights, srgbToLinear } from '/shared/world.js';
import {
  FILES, KING_STEPS, NAMES, VALUE,
  colorOf, describeFinish, describeNet, fileOf, finishNet, mateNet, material, rankOf, replay,
  squareControl, squareName, typeOf,
} from './chess.js';
import { FIGURE_FORM, figureParts } from './figures.js';
import { FLAT_PATHS } from './flat-men.js';
import { closingAz, codaOf, endingOf, phaseFraming } from './framing.js';
import { GAMES, SETS, captureName, gameById, gameNumber } from './games.js';
import { KNIGHT_MODEL } from './knight-model.js';
import { ROOK_MODEL } from './rook-model.js';
import { createSound, soundEvents } from './sound.js';
import { EVALS, MATE } from './evals.js';
import {
  coldOpenExtra, coldOpenTimeline, reelEvents, reelMoment, reelTimeline, reelWords, sourceTime,
} from './reel.js';
import { buildTimeline, SETTLE } from './timeline.js';
import { analysisLength, analysisNeeds, createVoiceOver, sayEvents } from './voiceover.js';

/**
 * THE DIAGRAM MEN, RASTERISED ONCE BEFORE THE FIRST BUILD — the chess-app's 2D
 * set (`chess-app/piece-sets/cburnett.js`), for `flatSet: 'diagram'`, which is
 * what the Shorts and Reels are drawn with so the video's flat board is the
 * site's flat board.
 *
 * An SVG becomes pixels only through an `<img>`, which is asynchronous, and a
 * frame has to be a pure function of its index — so it is done here, at module
 * load, before `run()` builds anything, and a capture's first frame already has
 * them. Only on the studio page (`/animations/...`): the site never asks the
 * scene for this set, because its 2D board is SVG of its own (`flat-board.js`),
 * and the path to the drawings is the repo's rather than the built site's.
 * Missing, the flat board falls back to the traced men.
 *
 * The black men are Burnett's own black drawings, as on the site — see
 * `buildDiagramMen` in `flat-board.js` for why.
 */
const DIAGRAM_PX = 512;
/** The studio page, where the videos are made — as against the site, which runs this scene too. */
const STUDIO = typeof location !== 'undefined' && location.pathname.startsWith('/animations/');
const DIAGRAM_MEN = STUDIO
  ? await loadDiagramMen().catch((err) => {
    console.warn('diagram men unavailable, flat board falls back to the traced set', err);
    return null;
  })
  : null;

/**
 * A SHORT OR A REEL IS ALWAYS ON THE 2D BOARD, whatever the panel, a saved view
 * or the URL says: one phase on its own (`clip`) or the retention reel (`reel`)
 * is drawn flat with the diagram men, and without the trays, which from straight
 * above in a 9:16 frame land half off the picture. Written into the parameters
 * rather than read beside them, so everything downstream that asks
 * `dimension === '2d'` gets the same answer and the panel shows what is drawn.
 *
 * The studio page only, where the videos are made. The site renders phases and
 * reels of its own and has a 2D board of its own (`flat-board.js`), and which
 * one it shows is the visitor's choice.
 */
function verticalOnFlat(p) {
  if (!DIAGRAM_MEN || !(p.reel || p.clip > 0)) return;
  p.dimension = '2d';
  p.flatSet = 'diagram';
  p.tray = false;
}

async function loadDiagramMen() {
  const { CBURNETT } = await import('../../piece-sets/cburnett.js');
  const out = {};
  await Promise.all(['k', 'q', 'r', 'b', 'n', 'p'].flatMap((type) => ['w', 'b'].map(async (side) => {
    const markup = CBURNETT[`${side}${type}`];
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45" width="${DIAGRAM_PX}" height="${DIAGRAM_PX}">${markup}</svg>`;
    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = DIAGRAM_PX;
    canvas.getContext('2d').drawImage(img, 0, 0, DIAGRAM_PX, DIAGRAM_PX);
    out[`${side}${type}`] = canvas;
  })));
  return out;
}

/**
 * A famous chess game, played out on a board.
 *
 * `Game` picks which one. `chess.js` holds the position and the move generator,
 * `games.js` holds the games — the score, the set it is played on, what is said
 * about each move, and how the game divides into phases. This file is the telling
 * of whichever one is chosen:
 * what the camera does, what is written on the screen, and how long each move is
 * allowed to breathe.
 *
 * ONE SCENE, A LIST OF GAMES
 * It was built for the Immortal Game and every part of it turned out to be about
 * chess rather than about that game — the generator, the timing by weight, the
 * mating net, the trays, the strike scaled by the value of the piece taken. So
 * another game is a data entry in `games.js` and nothing else, and there is no
 * per-game code anywhere in this file. The one hand-written sentence that used to
 * be here, describing how the mate worked, is generated by `describeNet` now.
 *
 * And the entry keeps getting shorter. Everything about a game that can be worked
 * out from the game is worked out in `framing.js`: where to stand for each phase,
 * which end of the board the closing shot looks at, what to say about a move
 * nobody has written about yet. A game needs its score, its facts, a sentence
 * saying what it is known for and a title; the camera work and the phase list are
 * optional, and `scripts/new-game.mjs` turns a PGN into the rest.
 *
 * THE CAMERA HOLDS STILL
 * A board is a thing being READ, and the shot list that suited it least was the
 * obvious one: a key per half-move, each asking for a slightly different angle,
 * so the frame was never once still across three minutes. `Hold the frame` is the
 * threshold under which an ask is dropped rather than softened, and what is left
 * is the swing to the mating side, a scripted shot where a move has earned one,
 * and a re-aim when the play has left the frame. Measured over the ten games:
 * 398 camera moves became 109, and the share of the running time on which the
 * camera is doing anything at all went from 77 per cent to 22. See
 * `buildCameraTrack`, which is where every one of those decisions is made.
 *
 * IT IS A TIMELINE, NOT A LOOP
 * `loopable: false`, for 206's reason exactly: a game of chess has an end, and
 * frame 0 is a position that never comes back. So there is no seam to hide and
 * no repeat to stretch the video with.
 *
 * IT OPENS ON THE TITLE IT IS PUBLISHED UNDER
 * The first seconds used to be the title card — the event, the date, the two
 * names and the opening: everything a viewer needs to know, and nothing that
 * makes them stay. So the video opens on its own YouTube title instead, held in
 * the MIDDLE of the frame for three seconds while the camera settles onto the
 * board, and the corner card comes up behind it once it is gone. `Opening title`
 * is the seconds it is held for, and 0 opens on the card the way this scene did
 * first.
 *
 * The first move follows two seconds later, whatever the hold and whatever the
 * length: the lead-in is the hold plus that settle and nothing else. It used to
 * be a share of the length, which at three minutes meant seven seconds of a
 * still board between the title going and 1.e4 — see `timeline.js`.
 *
 * It is set the way the card is — the event and the date in mono above it, the
 * two names under it, all three centred — because that composition is what the
 * rest of the video is written in, and an opening in a different voice is an
 * opening that belongs to a different video. What changes is the PLACE and the
 * SIZE: the middle of the frame at a size that reads on a phone held at arm's
 * length, rather than the corner at a size that reads next to a chessboard.
 *
 * That is one string doing both jobs on purpose. Somebody arrives having clicked
 * on "The Immortal Game (1851): A Bishop, Both Rooks and the Queen — Given Away",
 * and the first thing the video says is that they are in the right place. It is
 * `ytTitle` in `games.js` rather than in the publishing sheet with the rest of
 * the editorial copy, because it is the piece of it the render draws — the sheet
 * publishes the same string as the title of the upload. See `openerAt`.
 *
 * IT SAYS WHERE THE BOARD LIVES
 * `Website in the corner` writes one line in the top right of the frame, and
 * it is the only writing here that is not about the game. The reason it is on the
 * FRAME rather than in the description is that a video is watched somewhere this
 * project does not own: on a repost, in an embed, muted in a feed, with the
 * channel name cropped off. The mark is the part of it that travels, so somebody
 * who meets the Immortal Game on a stranger's page still learns where the board
 * it was played on can be found.
 *
 * It is a text parameter with no toggle beside it, on `Opening title`'s
 * argument: off is the empty string and nobody wants two controls to say it. It
 * runs for the whole video INCLUDING the opening title — a mark that comes and
 * goes is not a mark — and it is drawn on its own plane for that reason, since
 * the top band belongs to the move card and is suppressed while the title is up.
 *
 * The watch page turns it off (`APP_DEFAULTS` in `chess-app/watch/player.js`):
 * the site does not need telling its own address, and it is the one surface where
 * a visitor is already there. `?video=1` on that page still renders what the
 * upload renders, mark and all, which is what keeps `scripts/check-player.mjs`
 * comparing like with like. See `drawSite`.
 *
 * LENGTH IS THE ONLY TIME CONTROL, AND THE GAME FILLS IT
 * There were two for a while — the engine's `Length` and a `Game time` in
 * minutes — which is two dials for one quantity, and the panel showed "300s"
 * directly above "3min". Now `Length` is the whole of it: the title card, how
 * long each piece takes to travel, how long the board sits still afterwards and
 * the hold on the mate are all divided out of it, so the game finishes inside
 * the video at every setting and the mate always lands the same few seconds
 * before the last frame.
 *
 * `Emphasis` is the other pacing control and it changes no total: it only decides
 * how UNEQUALLY the middle is divided. Four moves in the Immortal Game are the
 * reason anyone watches it — 11.Rg1, 18.Bd6, 22.Qf6+ and the mate — and a
 * metronome rushes every one, so each move carries a weight (see `games.js`) and
 * the clock is split by weight rather than by count. At `Emphasis` 0 they all get
 * the same slice, which is worth seeing once to understand what the setting buys.
 *
 * THE MATE IS SHOWN, NOT ASSERTED
 * The last shot holds on a mating net drawn from `mateNet()`: the checking
 * bishop, a line from each guarding piece to the square it takes away, and the
 * two flight squares blocked by Black's own pieces. Every line in that picture
 * is generated from the final position, so the claim on the card — three minor
 * pieces, and Black's own bishop and pawn helping — is checked rather than
 * captioned. `scripts/check-chess.mjs` runs the same code under node.
 *
 * THE BOARD IS A CANVAS, THE PIECES ARE NOT
 * Squares, coordinates, move highlights, the arrow and the mating net are all
 * drawn in 2D into one texture on the board's top face. They are flat things on
 * a flat surface, so 2D is not an approximation — and it means the whole helper
 * layer costs ONE repaint per half-move rather than 64 meshes and a per-frame
 * update. The Staunton pieces are turned solids (`LatheGeometry`) because a
 * Staunton piece is a turned solid — including the knight and the rook, whose
 * bases are turned with the rest of them. What stands on those two bases is not:
 * a horse's head and a slotted crown are carvings, and both are scanned ones,
 * decimated. They are the only parts of the set that are not generated. See
 * `knightHead` and `rookCrown`.
 *
 * TWO SETS OF MEN
 * `Piece style` plays the same game with Staunton pieces or with carved figures
 * — a bearded king with a sword, a queen in a gown, a churchman in a mitre, a
 * horse, a tower, a rank of footsoldiers — which live in `figures.js` and are
 * built out of bodies rather than out of profiles. Both sets stand on the same
 * footprint and reach the same heights, so nothing else in this file knows which
 * is on the board: `formOf` answers for either, and the only other place the
 * difference shows is which way a piece faces (see `buildPieces`).
 *
 * THREE SETS OUT OF ONE SET OF SHAPES
 * `Piece finish` plays the same men in lacquered wood, in polished marble or in
 * frosted glass. Wood and marble each carry a grain painted into a canvas at
 * load and multiplied over the set colours — see the surfaces section — so they
 * are materials rather than two shades of the same plastic. Glass is a material
 * MODEL rather than a set of numbers: transmissive glass needs the scene drawn a
 * second time for the men to refract it, and it is frosted by default because a
 * clear set at this size is a rim and nothing else. So it is a switch, all three
 * sets of materials are built at setup and swapped per frame, and the colour of a
 * glass man is what the glass ABSORBS rather than what it is painted with —
 * which is what makes the dark half of a glass set smoked rather than black.
 * See `buildPieces`.
 *
 * DETERMINISM
 * `update()` is a pure function of the frame. The position after k half-moves is
 * `positions[k]`, computed once at setup — 46 boards of 64 entries, so there is
 * nothing to checkpoint and nothing to bake.
 */

// ---------------------------------------------------------------- dimensions

/** A square is 1 unit. Everything else is in squares. */
const BORDER = 0.55;
const BOARD = 8 + 2 * BORDER;
const TEX = 2048;
const TOP = 0.02;            // the playing surface, just above the slab

// ---- the 2D board: see `placeFlatCamera` and `flatGeometry`.
const FLAT_FOV = 6;          // degrees; long enough that nothing shows a side
const FLAT_NEAR = 20;        // tight round the board, which sits ~90 units down,
const FLAT_FAR = 2000;       // so a token's face and its ink never fight for depth
// How tall each man stands on his square, in squares — of what is SHOWN of him.
// The knight is exactly the rook's height, so the two stand level top and bottom
// — each is centred on its square, so equal heights are equal edges. The king,
// queen and bishop are cut (below) and then brought up to stand with them, in
// the order of rank a set is read by.
const FLAT_HEIGHT = { k: 0.92, q: 0.80, b: 0.84, n: 0.86, r: 0.86, p: 0.82 };
// Where the tall three are cut, as a y in the drawing's own pixels: through the
// join under the lowest ring of the collar, where its rounded underside meets the
// stem. Everything below — the stem, the bell and the foot — goes.
const FLAT_CUT = { k: 407, q: 467, b: 579 };
const FLAT_DEPTH = 0.06;     // the token's thickness, before its bevel
const FLAT_BEVEL = 0.012;
// How far below the middle of a tall frame the 2D board stands, in squares:
// enough that the far nameplate clears the move card at the top of a Short or
// Reel and leaves the corner free for the website. The studio's only — the
// site's flat board is laid out by its page (`flat-board.js`), and its clicks
// are mapped through this camera.
const FLAT_DROP = 1.4;
const FLAT_INK_DENSITY = 900; // the ink texture's pixels per square
const FLAT_INK_WEIGHT = 5;   // extra stroke on the drawing's lines, in its own pixels
const FLAT_OUTLINE_PX = 3.5; // the drawing's outline band, drawn again along a cut
/** Texture pixels per square. Same temporal-dead-zone reason as FIT_POINTS. */
const PX = TEX / BOARD;

/**
 * Degrees a second the camera may turn, averaged across a move — see
 * `buildCameraTrack`. Up here for the same temporal-dead-zone reason as the two
 * above it: `run()` renders a frame during its own call, and that frame builds
 * the camera track.
 */
const MAX_SLEW = 32;

/**
 * What `Hold the frame` means on each channel, per degree of it.
 *
 * One dial and four slacks, because the four channels are one decision -- "is
 * this worth a camera move?" -- asked in four units, and nobody wants to balance
 * four sliders against each other to answer it.
 *
 * The ratios are what make the four comparable, and they come off the picture
 * rather than off a preference: at the default 22, that is 22 degrees of azimuth,
 * 8.8 of elevation, 8 per cent of zoom and 2.4 squares of aim, and those are all
 * about the same amount of CHANGE ON SCREEN. Elevation gets less than half of
 * azimuth's because it changes the board's SHAPE rather than turning it -- a flat
 * board projects `sin(el)` as tall as it is wide, so nine degrees near 40 is a
 * tenth of the board's height on screen, where nine degrees of azimuth is a
 * slight turn of something the same size.
 *
 * Up here with `MAX_SLEW` rather than beside `buildCameraTrack`, for the
 * temporal-dead-zone reason the rest of this block is: `run()` renders a frame
 * during its own call, and that frame bakes the camera track.
 */
const HOLD = { az: 1, el: 0.4, zoom: 0.0036, aim: 0.11 };

/**
 * How many half-moves a re-aim looks at.
 *
 * A re-frame aims at where the play IS, not at the move that tripped it: the
 * mean of the next few moves' midpoints, clipped to the end of the phase. One
 * move's midpoint is a poor thing to commit a held frame to -- it can be an edge
 * pawn on its way somewhere else, and then the frame is wrong for the four moves
 * that follow and holds there. Four is enough to catch a combination and short
 * enough that the mean is still somewhere in particular.
 *
 * Clipped at the phase because a phase boundary is where the play moves
 * elsewhere; averaging across one would aim at the space between two subjects.
 */
const AIM_WINDOW = 4;

/**
 * How fast the aim may travel, in board squares a second.
 *
 * The companion to `MAX_SLEW`, on the quantity `cameraSway` calls shunt: an aim
 * that moves does not turn the camera at all, it slides the whole board across
 * the frame, so the slew limit on azimuth and elevation cannot see it. In BAKED
 * squares -- `Follow the move` scales the aim at read time, and at its default
 * the slide on screen is a fifth of this.
 */
const MAX_AIM_SLEW = 3;

/**
 * How many azimuths a steady fit is measured over, from 0 to 90 degrees.
 *
 * A quarter turn is enough because the board and everything fitted on it is
 * four-fold symmetric — see `steadyFit`. Up here for the temporal-dead-zone
 * reason the rest of this block is.
 */
const STEADY_STEPS = 31;

/**
 * Gravity on a spark, in squares per second squared. Tuned by eye against the
 * square rather than taken from the world: a chess square is 57 mm and nothing
 * about this is meant to be life-size. Up here for the temporal-dead-zone
 * reason the three above it are.
 */
const SPARK_G = 7.5;

/**
 * Seconds a checked king takes to breathe in and out again.
 *
 * Exactly periodic, and the websites depend on it being so: a page parked on a
 * check turns the glow over this loop rather than turning the playhead, so the
 * seam where it wraps must not be there to be seen. See `phase` in
 * `buildKingHeat`.
 *
 * Up here for the temporal-dead-zone reason the four above it are: `run()`
 * renders a frame from inside its own call, that frame runs `setup()`, and
 * `setup()` builds the glow.
 */
const FIRE_LOOP = 4;

/**
 * Seconds after the mating move lands, for each beat of the closing shot, and
 * how long the whole of it takes.
 *
 * Up here for the same temporal-dead-zone reason as everything else in this
 * block, and this one is not hypothetical: `setup()` publishes `MATE_SHOT` as
 * `ctx.closingShot` for the two websites, `run()` renders a frame from inside
 * its own call, and that frame runs `setup()`.
 *
 * The last of them is a number two other files need — `chess-app/play/live-game.js`
 * rests a finished board on it and `chess-app/watch/player.js` gives the step
 * button that much tail on a mating move — which is why it is published rather
 * than written down three times.
 *
 * THE FALL USED TO TAKE 1.1 SECONDS AND WAS MISSED. That is the whole reason
 * these numbers are what they are. The video's own closing framing swings round
 * to look along the mate, and inside that shot 1.1 seconds was enough — but
 * `chess-app/watch/player.js` holds whichever camera preset the visitor is on
 * for the whole game, and from the default three-quarter view a king who goes
 * over at the far edge of the board, in his own colour, between his own rook and
 * the piece that mated him, in a second and a bit, while the room is dimming
 * over the top of him, is a thing a viewer can watch and not see. Reported as
 * "the king didn't fall down after checkmate", on a game where it measurably
 * did.
 *
 * So the fall gets 1.9 seconds instead of 1.1 — see `toppleKing`, which spends
 * them on a longer hesitation, a real landing and a rock — the dim is pushed
 * back off it so the two are not competing, and the shot holds a full second on
 * a fallen king at the end rather than a tenth of one. Which is the ordering the
 * beats were always meant to have: one thing at a time, and the thing you are
 * meant to be looking at last.
 *
 * `MATE_SHOT` HAS TO STAY UNDER `OUTRO.min` IN `timeline.js`, which is 6. The
 * hold on the mate is the outro, a short render gets exactly six seconds of it,
 * and a closing shot longer than the hold is a shot with its last beat off the
 * end of the video. 5.5 is what is left of that with half a second to spare.
 */
const MATE_NET = [0.15, 1.55];
const MATE_FALL = [1.55, 3.45];
const MATE_DIM = [2.55, 4.45];
const MATE_SHOT = 5.5;

/**
 * Where taken pieces are set down: two columns beside each end of the board,
 * `TRAY_COL` apart, with rows `TRAY_ROW` down the board.
 *
 * TWO columns rather than one long row, and that is a fact about where the
 * camera stands rather than about tidiness. This shot lives at azimuth 0 or 180
 * — behind one player or the other — so a row running down the side of the board
 * is seen END ON, and six pieces at any spacing whatsoever collapse into one
 * white sausage. Staggering alternate captures across two columns puts a gap
 * between neighbours in the direction the camera is actually looking.
 *
 * Rows are 1.75 apart because a lying queen is 1.51 long: the spacing is the
 * longest piece plus room to see the gap, not a number that looked right.
 */
const TRAY_X = 4 + BORDER + 0.40;
const TRAY_COL = 0.82;
const TRAY_ROW = 1.75;

/**
 * The nameplates lying on the table, in squares.
 *
 * EVERY NUMBER HERE IS THE FRAME'S RATHER THAN A DESIGNER'S, and that is the
 * whole story of this block. Neither fit holds anything outside the board —
 * `FIT_POINTS` stops at the slab's corners and `HELD_POINTS` deliberately stops
 * short even of those — so a plate is furniture standing in the margin the shots
 * happen to leave, and a nameplate that is out of frame is worse than no
 * nameplate at all: the picture then has an empty space where a name should be.
 *
 * SO THE GAP IS THE LARGEST ONE THAT STILL FITS, and it was measured rather than
 * chosen. Projecting a plate's four corners into the frame at each angle the
 * watch page's buttons set — which is the fit a held camera gets, and the one
 * that matters, since the plates are the page's rather than the video's:
 *
 *      gap      3/4     White    Black    Side     Top
 *      0.10     0.82     0.84     0.84    0.91     0.81
 *      0.35     0.87     0.90     0.90    0.95     0.85
 *      0.55     0.91     0.96     0.96    0.98     0.88
 *      0.75     0.95     1.01     1.01    1.02     0.91
 *
 * (Distance from the middle of the frame, where 1.00 is its edge.) The last row
 * hangs off three of the five angles; 0.55 is inside all of them, and it is what
 * this uses. Nothing about the camera had to move to make room for it, which is
 * the point: turning the plates on does not reframe the board.
 *
 * AND THE GAP HAS TO BE THAT BIG, WHICH IS THE MEN'S DOING. A plate at 0.10 was
 * clear of the frame at every angle and invisible at the one that matters: the
 * far plate lay in the shadow of the back rank. A piece on it at the 3/4 shot's
 * 44 degrees hides the table for its own height over the tangent of that — about
 * 1.6 squares past its base, so out to 5.1 — and a plate whose near edge is
 * inside that is a plate behind the men. 4.55 + 0.55 puts the near edge at 5.10,
 * which is where it comes out from behind them.
 *
 * THE COMPOSED SHOT IS TIGHTER, AND IT IS NOT PROMISED. Sampling 80 frames of the
 * video's own camera across a game, the near plate's NAME is off the bottom of
 * the frame on 76 of them, by up to 0.12 — that shot aims below the board's
 * middle to leave the foot of the frame for the score strip, which is exactly
 * where the near plate now is. At 0.10 it survived all but the closing push-in,
 * so this is what the move out cost, and it is worth it: the composed shot is
 * what `Auto` and a capture ask for, and neither has these on unless asked
 * (`names` is off in the scene). Every angle a visitor can press has them whole.
 * It is the same bargain the trays have — see `FIT_POINTS` — and if it ever needs
 * closing, the fix is not this gap: it is that the shot leaves room for a strip
 * that is switched off, and `lookY` in `placeCamera` is where that is decided.
 *
 * ONE LINE OF TYPE, AND THAT IS ALSO THE FRAME'S DOING. A 0.72-deep plate has
 * room for a name at 0.36 of a square — a shade larger than the coordinates
 * printed on the board's own edge, which are 0.30 and legible from every one of
 * those angles. It does not have room for that AND a second line: the second
 * line would land at about 0.11, a third of the coordinates' size, which at this
 * distance is a grey smudge.
 *
 * SO A PLATE SAYS A NAME AND NOTHING ELSE. Which side it is is said by which end
 * of the board it is lying at, which is the whole reason it is furniture instead
 * of a caption. Whose move it is was tried here and taken back out: the way to
 * say it without words is to light one plate and dim the other, and the key light
 * already lights the near end of the table more brightly than the far one, so a
 * dimmed near plate and a lit far one are the same two pictures. A cue that can
 * be read backwards is worse than no cue, and there was never any shortage of
 * places saying whose move it is — the card, the score strip, and the watch
 * page's own nameplates, which have room for the word and use it.
 *
 * 0.09 thick, and it casts: a plaque with no shadow under it is a decal printed
 * on the table. Its far corner is 6.3 from the middle of the board, inside the
 * key light's shadow box, which holds 9.5 in its own frame — see the note there.
 */
const PLATE = { w: 4.6, d: 0.72, h: 0.09, gap: 0.55 };
/** Where a plate's middle sits: clear of the slab, along the players' axis. */
const PLATE_Z = BOARD / 2 + PLATE.gap + PLATE.d / 2;
/**
 * Texture pixels per square on a plate.
 *
 * Everything `paintName` draws is then written in SQUARES, the same unit the
 * geometry above is in, so the type cannot drift from the plaque it is printed
 * on when either changes. 260 against the board's own 225 (`PX`), because a
 * plate is a smaller surface seen at the same distance and a name is finer work
 * than a chequerboard.
 */
const PLATE_PX = 260;

/**
 * What the camera has to keep in frame: the corners of the slab at the floor,
 * and the top of a king over the whole playing area.
 *
 * The trays are deliberately NOT here. Including them was tried and costs a
 * third of the board's size on screen at the low camera angles this game's big
 * moves are shot from, because at those elevations the width is what binds and
 * the trays are the widest thing on the table. The board is the subject; the
 * heaps of taken pieces are furniture that drifts off the edge of the frame when
 * the shot goes low, and the material line in the strip carries the same
 * information at every moment anyway.
 *
 * Declared up here with the other dimensions rather than beside the function
 * that reads it: `run()` renders a frame during its own call, so a `const`
 * written below it is still in its temporal dead zone when the first frame draws.
 */
const FIT_POINTS = (() => {
  const h = BOARD / 2;
  const pts = [];
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) pts.push([sx * h, 0, sz * h]);
  }
  // And the top of a king, over the whole playing area. Fitting the slab's
  // corners alone is what the first pass did, and it clipped the crowns off the
  // back rank every time: the tall pieces are mid-EDGE rather than in a corner,
  // so no corner of the board describes where a king's cross ends up on screen.
  for (const x of [-4, 0, 4]) {
    for (const z of [-4, 0, 4]) pts.push([x, 1.75, z]);
  }
  return pts;
})();

/**
 * What a HAND-HELD camera frames for, which is less than an authored shot does.
 *
 * The squares and the tops of the kings, and NOT the slab's corners. A steady
 * fit has to hold every azimuth at once (see `steadyFit`), and the slab is the
 * widest thing on the table across a diagonal — holding its corners as well cost
 * a third of the board's size on screen for the sake of two triangles of wooden
 * border with nothing on them. So at oblique angles the corners of the frame are
 * allowed to leave, and the position never is.
 *
 * Up here with `FIT_POINTS` and for the same reason, which took a `Held view`
 * arriving on the URL to expose: `run()` draws a frame during its own call, and
 * that frame reaches `steadyFit` the moment the camera boots into a held view
 * rather than onto the shot list — at which point a `const` written below the
 * call is still in its temporal dead zone.
 */
const HELD_POINTS = (() => {
  const pts = [];
  for (const x of [-4, 4]) {
    for (const z of [-4, 4]) pts.push([x, TOP, z]);
  }
  for (const x of [-4, 0, 4]) {
    for (const z of [-4, 0, 4]) pts.push([x, 1.75, z]);
  }
  return pts;
})();

/**
 * How big each piece is, in squares.
 *
 * `base` is the radius of the foot, `lathe` how tall the turned part is and
 * `total` the whole piece. These are close to Staunton tournament proportions
 * — king 1.7 squares tall on a base 0.76 wide, pawn 0.95 — and the first pass at
 * this scene had every one of them at a bit over half that, which looked
 * plausible in isolation and read as draughts the moment it was rendered. A
 * chess piece is TALLER THAN ITS SQUARE, and that is most of what makes a board
 * look like a board rather than a tiled floor with markers on it.
 */
/**
 * How much fatter the knight's head is than the carving it came from, across only.
 *
 * The scan is of a real piece, and a real knight's head is close to a slab: it
 * measures 0.46 across against 0.70 nose to poll. Standing a slab on a turned body
 * leaves the two obviously separate — a blade cutting through a round collar, with
 * a hard crease where they intersect instead of one shape growing out of another.
 * Fattening the head until its section is nearer round is what closes that seam.
 *
 * ONE AXIS, AND IT IS X. The model faces down Z, so X is across the horse — cheek
 * to cheek — which is the one direction with no silhouette in it. It is also the
 * direction the old extruded head picked freely as its `depth`, for the same
 * reason. Y is the height `FORM` sets. Z is nose to poll, and stretching THAT is
 * the classic tell of a bad set: it is the aspect ratio a knight is judged on.
 */
const THICKNESS = 1.35;

/**
 * THE LIFTED MOVE, in shares of the move's own time. See `liftedMove`.
 *
 * Up here rather than beside it for `MEN_FORM`'s reason: `run()` draws a frame,
 * and moves the men, before the module body reaches the pieces section.
 */
const LIFT = {
  up: 0.22, // rising off the square
  // Held up there before it sets off, IN SECONDS: a piece picked up, shown, and
  // only then carried. A share of the move would be no pause at all on a quick
  // one and four seconds on a slow one; a second is a second. On a move too
  // short to hold it and still travel, `startMax` is what gives: the pause is
  // whatever is left under that. See `liftWindow`.
  pause: 1,
  startMax: 0.6, // the latest it may set off, as a share of the move
  glideEnd: 0.88, // and when it is over the new square
  down: [0.74, 0.94], // coming down onto the new square
  bounce: 0.012, // the small rebound as it lands, in squares
  height: 0.2, // how high it is carried, in squares, plus `perSquare` for each crossed
  perSquare: 0.015,
  knight: 0.45, // a knight goes over what is in its way
  lean: 0.14, // how far it leans into the glide at full speed, in radians
  settle: 0.05, // and how far it rocks on landing
  strike: 0.72, // when a capture's strike lands: over the square, just before touchdown
};

/**
 * THE GLOW UNDER THE MOVING PIECE. See `drawMoveRing`.
 */
const RING = {
  radius: 0.44, // outer edge, in squares: inside its own square
  white: '#ffc24a', // warm gold for White
  black: '#4fa8ff', // cool blue for Black
  in: 0.2, // share of the move spent fading in
  fade: 0.5, // seconds it takes to go after landing
  pulse: 0.65, // seconds the landing pulse takes to spread and go
  spread: 1.9, // how far the pulse spreads, as a multiple of the radius
  lifted: 0.9, // how much wider the glow gets per square the piece is lifted
};

/**
 * THE GHOST LEFT ON THE SQUARE A PIECE CAME FROM. See `drawGhosts`.
 */
const GHOST = {
  opacity: 0.55, // how solid it is while the piece is still on its way
  // Seconds one ghost takes to go, once the piece has settled on its square —
  // the whole trail holds until then, so the move is seen whole before it
  // clears. Then it goes in the order it was laid down, the square it came from
  // first and the last one dropped last, over `stagger` seconds end to end, so
  // the trail rubs out from the back and the move keeps its direction to the
  // end. Longest a trail can be on screen after the move: `stagger` + `fade`.
  fade: 0.4,
  stagger: 0.45,
  // How far it is tinted towards its side's glow (`RING`), and how much it
  // glows in it. Untinted, a pale piece at half strength on a pale square was
  // all but invisible; tinted, it also ties the two ends of the move together.
  tint: 0.5,
  glow: 0.35,
};

const FORM = {
  p: { base: 0.34, lathe: 0.95, total: 0.95 },
  // The rook's lathe stops well short of its total, which the other five do not.
  // Its crown is no longer eight boxes standing on a flat top but a scanned
  // crenellation, and a real one is a quarter of the piece rather than the
  // stub `cap` allowed — so the turned body ends under it, narrow enough to run
  // up INSIDE the crown and plug its open bottom. See `rookCrown`.
  r: { base: 0.36, lathe: 0.76, total: 1.06 },
  n: { base: 0.35, lathe: 0.58, total: 1.15 },
  b: { base: 0.35, lathe: 1.24, total: 1.32 },
  q: { base: 0.37, lathe: 1.40, total: 1.51 },
  k: { base: 0.38, lathe: 1.46, total: 1.70 },
};

/**
 * Turning profiles: radius against height, as a lathe operator would read them
 * — base flare, collar, a concave stem, a ring, then whatever the piece is
 * known by.
 *
 * SHAPE ONLY. Both axes are normalised by the profile's own extent and then
 * scaled to the `FORM` above, so the numbers here carry no units and a profile
 * can be nudged by eye without anything else having to agree with it. They are
 * resampled through a spline before revolving, so the silhouette is a curve
 * rather than a chain of facets — a chess piece IS its silhouette, and a lathe
 * of fifteen straight segments reads as a stack of tins.
 */
const PROFILE = {
  // The collar this ends on is narrower than the other five, and it is not a
  // style choice: the carved head that stands on it meets it at a neck of about
  // 0.12, and a collar wider than the neck shows as a flat plate with a horse
  // growing out of a hole in it. They have to agree — see `knightHead`.
  n: [
    [0, 0], [0.30, 0], [0.32, 0.035], [0.30, 0.06], [0.24, 0.095], [0.20, 0.135],
    [0.185, 0.19], [0.16, 0.225], [0.11, 0.25], [0, 0.25],
  ],
  p: [
    [0, 0], [0.28, 0], [0.30, 0.03], [0.29, 0.05], [0.24, 0.075], [0.16, 0.10],
    [0.125, 0.16], [0.118, 0.235], [0.145, 0.27], [0.175, 0.285], [0.125, 0.30],
    [0.135, 0.325], [0.16, 0.37], [0.165, 0.415], [0.135, 0.475], [0.075, 0.51], [0, 0.52],
  ],
  // Truncated where the old one flared out to meet its flat top, and closed with
  // a taper instead. The flare has moved into the model — it is part of the same
  // carving as the crenellation above it — and leaving both would put the turned
  // one inside the scanned one. The points below the cut are untouched, and the
  // new extent (0.3678) is the same fraction of `lathe` that 0.47 was of 0.92, so
  // the base is the shape it always was rather than the old one squashed.
  r: [
    [0, 0], [0.31, 0], [0.33, 0.035], [0.31, 0.06], [0.25, 0.09], [0.205, 0.13],
    [0.19, 0.20], [0.19, 0.29], [0.14, 0.34], [0.125, 0.3883], [0, 0.3883],
  ],
  b: [
    [0, 0], [0.30, 0], [0.32, 0.035], [0.30, 0.065], [0.23, 0.10], [0.165, 0.15],
    [0.135, 0.24], [0.15, 0.32], [0.20, 0.365], [0.205, 0.395], [0.145, 0.415],
    [0.165, 0.45], [0.205, 0.52], [0.195, 0.60], [0.145, 0.665], [0.075, 0.715], [0, 0.735],
  ],
  q: [
    [0, 0], [0.32, 0], [0.34, 0.04], [0.32, 0.07], [0.25, 0.11], [0.175, 0.17],
    [0.14, 0.30], [0.155, 0.40], [0.20, 0.455], [0.205, 0.49], [0.15, 0.515],
    [0.175, 0.55], [0.225, 0.63], [0.235, 0.695], [0.20, 0.735], [0.13, 0.755], [0, 0.76],
  ],
  k: [
    [0, 0], [0.33, 0], [0.35, 0.04], [0.33, 0.075], [0.26, 0.12], [0.185, 0.185],
    [0.15, 0.35], [0.165, 0.46], [0.215, 0.52], [0.22, 0.555], [0.16, 0.58],
    [0.185, 0.62], [0.24, 0.71], [0.25, 0.79], [0.215, 0.835], [0.14, 0.855], [0, 0.86],
  ],
};

/**
 * THE THIRD SET: THE CLUB STAUNTON, TRACED.
 *
 * `FORM` and `PROFILE` above are a Staunton reduced to what a 1920-wide frame
 * needs of it. This is a specific set — the boxwood and rosewood club set in the
 * reference photograph — and none of the numbers below were chosen. They come
 * off a line tracing of that photograph, both rows of it, rasterised and read
 * one unit of height at a time: half the traced width at each height, the two
 * rows averaged (they agree to about one unit), half the drawn line taken off,
 * and the run of samples thinned to the fewest points that stay within 0.7 of
 * it. The unit is the tracing scaled so the king is 520 tall; `CLUB_PX` turns
 * it into squares.
 *
 * WHY TRACED, WHICH IS THE SECOND CORRECTION. The first table was drawn by eye
 * and had the parts in the wrong places. The second was read off the photograph
 * by hand and still had them twenty units out: the king's two collar rings sat
 * too low, his crown started too low, his foot was missing the ring above its
 * disc, and the pawn was 44% of the king where the tracing makes it 54. A dozen
 * hand-read points cannot hold a profile to one unit; fifty traced ones can, and
 * that is the difference between a set that resembles this one and this one.
 *
 * NOT THE OTHER SETS' HEIGHTS, WHICH IS A DECISION. `FORM` and `FIGURE_FORM`
 * share their heights, and this set does not: queen 79% of the king, bishop 73,
 * knight 60, rook 55, pawn 54. That staircase is part of what makes it look like
 * itself, so it is kept. It is safe to: every size in this file is read through
 * `formOf`, the king is still 1.70 and still the tallest thing on the board, and
 * the one place that reads a dimension straight off `FORM` — the fallen king's
 * rest, in `toppleKing` — reads a foot within a hundredth of this one.
 *
 * WHAT IS NOT TURNED. The knight's head is the scanned carving the other sets
 * use, which is what is in the photograph. The king's cross is the traced outline
 * of the cross itself, cut out flat. The rook's battlements and the queen's
 * coronet points are cut rings — see `clubGeometry`.
 *
 * THE ONE THING IN THE PHOTOGRAPH THAT IS NOT HERE is the curved slot in the
 * bishop's mitre. It is a CUT, and there is no boolean in the pipeline to take
 * material away with.
 */
const CLUB_PX = 1.70 / 520;

/**
 * Foot radius, turned height and total height. `base` and `lathe` are the
 * widest and highest numbers in the piece's own profile, `total` the tracing's
 * height for the piece; change a profile and these move with it.
 */
const CLUB_FORM = {
  p: { base: 81.9 * CLUB_PX, lathe: 278.6 * CLUB_PX, total: 278.6 * CLUB_PX },
  r: { base: 91.1 * CLUB_PX, lathe: 248 * CLUB_PX, total: 286.2 * CLUB_PX },
  n: { base: 90.1 * CLUB_PX, lathe: 100 * CLUB_PX, total: 313 * CLUB_PX },
  b: { base: 90 * CLUB_PX, lathe: 381.7 * CLUB_PX, total: 381.7 * CLUB_PX },
  q: { base: 98.9 * CLUB_PX, lathe: 408.6 * CLUB_PX, total: 408.6 * CLUB_PX },
  k: { base: 107.9 * CLUB_PX, lathe: 449 * CLUB_PX, total: 520 * CLUB_PX },
};

/**
 * The three sets' dimension tables, by the name the parameter uses.
 *
 * A map rather than a chain of ternaries, because there are three of them now and
 * the next one should cost a line here rather than an edit in four places. The
 * fallback is Staunton for the same reason `SETS` falls back to its first entry:
 * a `?men=` somebody typed wrong should draw a board, not throw.
 *
 * Up here beside `CLUB_FORM` for `SAFE_TOP`'s reason: it was declared down by
 * `formOf` and the preview died on "Cannot access 'MEN_FORM' before
 * initialization", because `run()` draws a frame, and lays the men down, before
 * the module body reaches the pieces section.
 */
const MEN_FORM = { staunton: FORM, figures: FIGURE_FORM, club: CLUB_FORM };

/**
 * The turnings: [half-width, height] in traced units, foot upward, used exactly
 * as written — joined by straight segments, not a spline, because at fifty
 * points a spline has nothing left to smooth and would overshoot every step the
 * collars make.
 *
 * Where each stops: the pawn, bishop and queen are turned to the top, finial and
 * all, the queen's rim dropped to where her coronet's notches bottom out. The
 * king stops on the seat of his cross, the rook under its battlements, and the
 * knight at the top of its bell, where the head stands.
 */
const CLUB_PROFILE = {
  k: [
    [0, 0], [55, 0], [55, 2], [91.4, 6], [101.6, 9], [104.9, 12], [107.9, 18], [107.9, 35],
    [106.8, 40], [101.1, 43], [98, 46], [102.2, 56], [103, 62], [102.5, 67], [99.7, 74], [95.4, 80],
    [80.2, 93], [72.6, 102], [69.5, 112], [69.1, 122], [60, 127], [57.1, 133], [45.4, 168], [39.1, 196],
    [36, 216], [34.3, 250], [35.6, 291], [36, 292], [55, 296], [59.1, 299], [61.3, 304], [61.2, 308],
    [59.3, 312], [49.9, 318], [51.1, 322], [49.5, 327], [41, 331], [39.7, 333], [39.5, 337], [43.6, 343],
    [40, 350], [41.9, 361], [47.1, 378], [61.3, 415], [62.2, 421], [61.2, 424], [58.3, 427], [50.9, 431],
    [39, 435], [18.3, 439], [17.6, 444], [14.4, 449], [0, 449],
  ],
  q: [
    [0, 0], [55.5, 0], [55.5, 2], [87.2, 6], [94.6, 9], [98.9, 17], [98.9, 35], [98.6, 37],
    [96.1, 40], [90, 44], [94.9, 58], [93.3, 69], [87.8, 77], [73.6, 89], [67, 96], [63.5, 104],
    [63.1, 115], [61.3, 117], [54, 120], [49.8, 128], [40.7, 153], [35.2, 176], [31.7, 197], [29.9, 215],
    [30.2, 256], [30.6, 257], [46.3, 259], [50.2, 261], [53.7, 265], [54.6, 269], [52.7, 275], [46.1, 281],
    [45.8, 288], [43.2, 291], [37.4, 294], [36, 296], [36, 299], [38.2, 302], [38.8, 305], [35.3, 311],
    [36, 317], [40, 330], [51.2, 358], [44, 360], [27.8, 372], [22.9, 377], [11.5, 383], [14, 393],
    [13.2, 398], [10, 404], [4.6, 408], [0, 408.6],
  ],
  b: [
    [0, 0], [52.6, 0], [52.6, 2], [81.3, 6], [86.5, 9], [90, 17], [89.5, 31], [88.6, 34],
    [82.5, 39], [82.4, 41], [86, 48], [87, 58], [83.1, 67], [78.1, 72], [64.7, 82], [61.2, 86],
    [58.7, 92], [57.8, 101], [51.7, 105], [48, 106], [43.9, 112], [35.7, 129], [30.2, 147], [27.7, 163],
    [28, 191], [34.9, 194], [44.2, 196], [48.5, 199], [51.2, 204], [50.1, 211], [42.6, 218], [43.3, 226],
    [40.1, 230], [33.7, 233], [31.6, 238], [35.3, 245], [31.8, 251], [31.4, 254], [40.9, 276], [47.1, 298],
    [48, 309], [47.3, 319], [43.6, 330], [38.5, 338], [28.1, 347], [13.2, 355], [17, 364], [17, 370],
    [13.4, 377], [6.5, 381], [0, 381.7],
  ],
  n: [
    [0, 0], [48.6, 0], [48.6, 2], [61.6, 3], [83.2, 7], [87.2, 10], [90.1, 17], [89.2, 32],
    [82.2, 39], [86.3, 49], [85.7, 58], [80, 67], [60.7, 82], [58.7, 86], [57.8, 94], [56, 98],
    [60.3, 100], [0, 100],
  ],
  r: [
    [0, 0], [50.5, 0], [50.5, 2], [63.1, 3], [83.5, 7], [88.2, 10], [91.1, 17], [90.4, 33],
    [82.9, 40], [87.8, 53], [86.6, 63], [81.9, 71], [69.4, 83], [66, 88], [65.6, 99], [58.1, 106],
    [55.9, 110], [48.9, 143], [45.2, 175], [43.6, 210], [46.1, 215], [47.4, 222], [54.6, 228], [55, 230],
    [53, 235], [56.2, 240], [56.5, 248], [0, 248],
  ],


  p: [
    [0, 0], [47.9, 0], [47.9, 2], [70.7, 5], [75.1, 6], [79.3, 9], [81.9, 16], [81.5, 30],
    [76.4, 35], [75.9, 37], [79, 44], [79, 51], [75.6, 58], [62.5, 70], [59, 80], [49.9, 84],
    [44.1, 93], [35.3, 113], [29.3, 136], [26.7, 165], [45.2, 170], [47.1, 171], [48.3, 174], [48, 177],
    [46.1, 180], [23.9, 200], [36.2, 214], [40.9, 223], [43.5, 237], [41.6, 250], [36.9, 260], [27, 270],
    [17.2, 275], [4.3, 278], [0, 278.6],
  ],
};

/**
 * The right half of the king's cross, [half-width, height], from inside his seat
 * to the top. Mirrored and cut out flat in `clubGeometry`.
 */
const CLUB_CROSS = [
    [14.4, 446], [14.4, 449], [20.8, 452], [23.2, 455], [21.3, 460], [14, 468], [18.2, 470], [23.5, 471],
    [23.9, 486], [12.6, 488], [23.5, 505], [24, 508], [19.4, 510], [6.1, 512], [4.5, 517], [1.2, 520],
];

/**
 * How far above the set's colour a white vein in a dark marble is allowed to go.
 *
 * The dark stone's picture is painted with its body at 1/this and its veins at
 * full, and the material's colour is multiplied up by this to put the body back
 * where the set asked for it — so a vein comes out seven times lighter than the
 * stone around it, which on a near-black piece is the difference between black
 * marble and black. See `marbleShade`, and `darkStone` for the one case where
 * the multiply will not fit.
 *
 * Up here with the other module constants rather than down beside the shading it
 * belongs to, because `run()` is called from the top of this file: setup, and
 * therefore `buildPieces`, has already gone by the time the module body reaches
 * the surfaces section, and a `const` declared there is still in the temporal
 * dead zone when the men are built.
 */
/**
 * How far down a 9:16 frame the platform's own controls reach, as a fraction.
 *
 * 220 of 1920 on a Facebook or Instagram Reel — the back arrow, the sound row and
 * whatever the app puts beside them. YouTube Shorts is kinder at about 120. The
 * stricter of the two is the one worth designing to, because the same file goes
 * to both. Approximate by nature and the apps move it, so this is a margin rather
 * than a measurement.
 *
 * UP HERE FOR `DARK_STONE_GAIN`'S REASON, and it was declared down beside
 * `drawCard` first and the capture died on it: "Cannot access 'SAFE_TOP' before
 * initialization". `run()` is called from the top of this file, so setup — and the
 * first `drawHud` with it — has gone by before the module body reaches the
 * drawing section, and a `const` declared there is still in the temporal dead
 * zone. A page error rather than a wrong pixel, which is the good version of this
 * mistake.
 */
const SAFE_TOP = 220 / 1920;

/**
 * How much of a wide frame's width the writing gets, with the board taking the
 * rest.
 *
 * A landscape frame used to carry the card over the top left of the board and
 * the strip across its near edge, and both of them are readable there only
 * because of the two-axis scrims in `drawCard` and `drawStrip` — white type over
 * cream squares in full key light needs the picture darkened under it, and
 * darkening the picture is a cost paid on every frame of the video. Given the
 * room, not paying it is better: the board takes the left of the frame and
 * everything written sits in a column beside it, over nothing.
 *
 * A THIRD, ASKED FOR AS A THIRD. This was `width - height` first, which on 16:9
 * left the board a square and the column the other 44% — a tidy accident of that
 * one aspect ratio and nothing else. A fraction says the same thing on every
 * frame the layout is allowed on, and it is the number to change to move the
 * seam: 640 pixels of a 1920 frame, with 1280 x 1080 left for the board. The
 * board's region is therefore no longer square, which costs nothing — `fitDistance`
 * frames for whatever region it is handed, and a slightly wide one holds the
 * board larger than a square one did.
 */
const PANEL_FRACTION = 1 / 3;

/**
 * How much wider than tall a frame has to be before the column is worth having.
 *
 * At 1.5 the two thirds left for the board are exactly square; below it they are
 * TALLER than they are wide, and a board framed for a portrait region inside a
 * landscape frame is a worse picture than the old writing-over-the-board layout
 * on the same frame. So that is the floor, and under it a landscape frame keeps
 * the old layout — as 9:16 does whatever this says. See `frameLayout`.
 */
const PANEL_MIN_ASPECT = 1.5;

/**
 * Where down its band the panel's score list holds the move being played.
 *
 * The list is the WHOLE game and it scrolls, so something has to say which row
 * the playhead sits on. Three quarters down, which is a compromise between the
 * two things the list is for. A viewer following the game wants to see where the
 * position came from, which argues for the current move near the bottom and the
 * band full of moves already played; a list that is going to scroll has to LOOK
 * like one, which it does not if nothing is ever visible below the highlight.
 *
 * It is also the anti-spoiler line, and `scoreWindow` — which the strip over the
 * board still uses — is where the argument is written down: the strip shows the
 * move being played and the three before it, and nothing after, because this
 * game is watched for moves nobody sees coming. A list of the whole score cannot
 * keep that, and the panel is not trying to; what it does instead is show two
 * rows of it against seven behind, and set them faint. See `drawPanel`.
 */
const SCORE_ANCHOR = 0.75;

/**
 * The panel's own colour, as a string the fades can reuse.
 *
 * Opaque, and named rather than written twice: the score list's soft edges are
 * this colour painted back over the rows, and a fade that is a shade off the
 * ground it fades into draws a band instead of disappearing.
 */
const GROUND = '#0a0d12';

/**
 * Which of the three shapes a frame is, and where the board goes in it.
 *
 * `portrait` and `panel` are mutually exclusive and a frame may be neither, in
 * which case it is the landscape-over-the-board layout that was here first.
 * `board` is the region the CAMERA is fitted to rather than the whole frame,
 * which is the one thing every reader of this needs: in panel mode it is the
 * left `1 - PANEL_FRACTION` of the width at full height, and `fitDistance`
 * frames the board for THAT while the renderer still draws the whole width.
 */
function frameLayout(ctx) {
  const { width: W, height: H } = ctx;
  const portrait = H > W;
  // `?? true` for the frame `run()` draws during its own call, which reaches
  // `fitDistance` before `ctx.params` is the coerced object.
  const wanted = ctx.params?.panel ?? true;
  const panel = !!wanted && !portrait && W >= H * PANEL_MIN_ASPECT;
  // Rounded to whole pixels, because it is also where the overlay canvas is cut
  // and a plane an eighth of a pixel wider than its canvas resamples the type.
  const boardW = panel ? Math.round(W * (1 - PANEL_FRACTION)) : W;
  return {
    portrait,
    panel,
    // What the camera is framed for: the board's share, or the whole frame when
    // there is no column.
    board: { w: boardW, h: H },
    // The column, as a fraction of the frame, left edge to right edge.
    panelX: panel ? boardW / W : 1,
  };
}

/**
 * What the description card calls each set of men.
 *
 * Beside the parameter rather than inside the sentence that prints it, so the
 * three names are in one place and a fourth set cannot ship with a card that
 * calls it "carved figures" because that was the else branch.
 */
const MEN_WORDS = {
  staunton: 'Staunton men',
  figures: 'carved figures',
  club: 'a carved club set',
};

const DARK_STONE_GAIN = 7;


/**
 * The audio track's command and the file it writes: the config's `audio`, which
 * the capture command pastes between its two lines, and the audio-analysis button,
 * which is the same command with `Analysis` and the voice forced on.
 */
function audioFor(v) {
  const flags = [
    `--duration ${v.duration}`,
    ...(v.analysis ? ['--analysis'] : []),
    `--opener ${v.opener}`,
    `--clip ${v.clip}`,
    `--emphasis ${v.emphasis}`,
    ...(v.narration > 0 ? ['--narrate'] : []),
    // A reel's track is timed to the reel — the open, the rewind, the pause —
    // and without the flag the script renders the plain timeline at the same
    // length, which matches the frames for the first second and nothing after.
    // The variant is spelled out because `--reel` changes the script's default
    // name, and the encode line below expects this file.
    ...(v.reel ? ['--reel', `--variant ${v.game}`] : []),
    // Same reason: the open, the rewind and the pause move every sound after them.
    ...(v.coldOpen && !v.reel ? ['--cold-open'] : []),
    ...(v.story && !v.reel ? ['--story'] : []),
  ];
  return {
    command: `node scripts/make-chess-audio.mjs ${v.game} ${flags.join(' ')}`,
    file: `output/audio/207-famous-chess-games--${v.game}.wav`,
  };
}

run({
  id: '207',
  title: 'Famous chess games',

  width: 1920,
  height: 1080,
  fps: 30,
  duration: 180,
  maxDuration: 900,
  // A game has a last move. See the header.
  loopable: false,
  background: 0x080a0e,
  fov: 34,
  near: 0.05,
  far: 200,

  /**
   * `Analysis` makes the length the narration's: every move, headline and note
   * said in full, and the video as long as that takes. The `Length` dial is then
   * ignored — see `analysisTimeline` in `timeline.js`.
   */
  lengthFor: (v) => {
    if (!v.analysis) return null;
    const g = gameById(v.game);
    const span = clipSpan(g, v.clip);
    const said = analysisLength(g, replay(g.moves), {
      emphasis: v.emphasis, from: span.from, to: span.to, opener: g.ytTitle ? v.opener : 0,
    });
    // The cold open's seconds go on top: the analysis is still said whole.
    return coldOpenOn(v) ? said + Math.ceil(coldOpenExtra(g, replay(g.moves).plies)) : said;
  },

  /**
   * How the upload gets its sound, for the studio's "Copy capture command".
   *
   * `capture.js` renders frames and hears nothing, so the knocks and the voice
   * the `Sound` group plays through WebAudio are studio-only until this runs —
   * see `sound` and `narration` below, which both say so and neither of which
   * could do anything about it. What was missing was the step in between: the
   * button handed back a capture and an encode, both correct, and the video came
   * out silent with nothing anywhere saying a track had to be rendered first.
   * `shared/engine.js` puts whatever this returns between the two.
   *
   * EVERY TIMING FLAG IS PASSED EXPLICITLY, not only the ones that differ from a
   * default. The track is timed off `buildTimeline`, so it is as long as the
   * video and in step with it only if it is handed the same four numbers the
   * capture was; `make-chess-audio.mjs` has its own defaults and they are not
   * this scene's, so leaving one out is how a track ends up in step with a render
   * nobody asked for. They are cheap to write and silent to get wrong.
   *
   * THE KNOCKS ARE UNCONDITIONAL AND THE VOICE IS A FLAG, which is that script's
   * rule rather than a choice made here: a board being played is part of the
   * picture, and a voice is a decision about what kind of video this is. So
   * `--narrate` follows the `Narration` slider — dial it up in the studio and
   * the command says the game out loud, leave it at 0 and the command is the wood
   * alone. The slider is a gain and this is a yes or no, which is the whole of
   * why only its sign is read.
   *
   * The variant defaults to the game's own id, so the file is named for the game
   * rather than for the folder — which is why `encode.js` is handed the path
   * rather than left to look for `<folder>.wav` and find nothing.
   */
  audio: audioFor,

  // Captures are named for the game: `--variant 007-adolf-anderssen-vs-lionel-kieseritzky`
  // gives output/video/207-famous-chess-games--007-adolf-anderssen-vs-lionel-kieseritzky.mp4,
  // so each game's render sits in its own file instead of replacing the last one.
  variant: (v) => captureName(gameById(v.game)),

  params: {
    // ---- which game ------------------------------------------------------
    // Not live and not cosmetic: it changes the score, so it changes everything
    // derived from the score, which is the whole scene.
    game: {
      type: 'select', label: 'Game', group: 'Game', default: GAMES[0].id,
      options: GAMES.map((g) => ({ value: g.id, label: `${gameNumber(g)}. ${g.title} · ${g.date}` })),
    },

    // ---- timing ---------------------------------------------------------
    // There is no second time control: the engine's `Length` is the whole of it
    // and the game is divided out of it. See the header.
    //
    // How unequally that division falls. 0 is a metronome; it never changes the
    // total, only who gets the seconds.
    emphasis: {
      type: 'range', label: 'Emphasis', group: 'Timing',
      min: 0, max: 1.6, step: 0.05, default: 1,
    },
    // The most a piece takes to travel, in seconds — a ceiling, since a short
    // slot gives it less. A second is the videos' pace; the website reads a game
    // slower and asks for more. Not live: it re-times every move.
    travel: {
      type: 'range', label: 'Piece travel', group: 'Timing',
      min: 0.4, max: 5, step: 0.05, default: 1,
    },

    // Seconds the video's own title is held over the board before the title
    // card. One dial rather than a toggle and a duration, on `Strike size`'s
    // argument: off is a length of zero, and nobody wants to set two controls to
    // say it.
    //
    // It does not take a bite out of the lead-in, it IS the lead-in: the intro is
    // this plus the two-second settle, so turning it up moves the first move
    // later and turning it down moves the first move sooner, and the gap between
    // the writing going and the first piece moving is two seconds either way.
    // That is also why it stopped being `live`, which it was for as long as it
    // changed only what was drawn: it re-divides the timeline, like `clip`,
    // and a card held for a second longer over moves timed for the old hold is a
    // render nothing in it agrees with. See `openerHold` and `introFor` in
    // `timeline.js`.
    opener: {
      type: 'range', label: 'Opening title', group: 'Timing',
      min: 0, max: 6, step: 0.1, default: 3,
    },

    /**
     * Which phase to render on its own. 0 is the whole game, which is every
     * render this scene did before this existed.
     *
     * A NUMBER RATHER THAN A LIST OF PHASE NAMES, because the phases belong to
     * the game and `game` is a control of its own: a select here would have to
     * be repopulated every time that one moved, and would name phases of the
     * Immortal Game while the Opera Game was loaded. Out of range is the whole
     * game, so 6 is safe to leave dialled in while stepping through games that
     * have four phases — see `clipSpan`.
     *
     * Not live: it re-divides the timeline, which is what a bake is for.
     */
    clip: {
      type: 'range', label: 'Phase alone (0 = whole game)', group: 'Timing',
      min: 0, max: 6, step: 1, default: 0,
    },

    /**
     * The retention cut for Shorts and Reels: it opens on the payoff, rewinds,
     * stops for "find the move" before the peak, and ends where it opened. It
     * picks its own ply range from the game's notes, so `clip` is ignored while
     * it is on, and so is `opener` — there is no title card. See `reel.js`.
     *
     * Not live: it re-times everything.
     */
    reel: {
      type: 'toggle', label: 'Retention reel (cold open, rewind, puzzle)', group: 'Timing',
      default: false,
    },
    /**
     * The long cut's cold open: the payoff of the game's big move first, under a
     * question, then a rewind to the title card and the game as usual — with a
     * "find the move" pause before the big move. The whole game only (`clip` 0),
     * and off under `Retention reel`, which is its own cut. See
     * `coldOpenTimeline` in `reel.js`.
     *
     * Not live: it re-times everything.
     */
    coldOpen: {
      type: 'toggle', label: 'Cold open (payoff first, then the game)', group: 'Timing',
      default: false,
    },
    /**
     * Story pacing: a half-move with nothing written about it is played quickly
     * and said nothing over, and the moves with notes share the time that frees
     * so their paragraphs are said. See `QUIET` in `timeline.js`. Ignored under
     * `Analysis`, which says everything anyway. Not live: it re-times everything.
     */
    story: {
      type: 'toggle', label: 'Speed through quiet moves', group: 'Timing',
      default: false,
    },
    /**
     * The reel's thumbnail instead of its captions: the side to play, what there
     * is to win, and a ring on the piece it wins. For one frame — the puzzle
     * position — rendered as a still by `/api/chess-thumb/` on the dev server.
     */
    cover: {
      type: 'toggle', label: 'Thumbnail overlay (reel)', group: 'Timing',
      default: false, live: true,
    },

    // ---- helpers --------------------------------------------------------
    /**
     * Whether a wide frame gives the board a square and the writing a column.
     *
     * NOT `live`, ALONE AMONG THE HELPERS, and that is what it is a parameter
     * for rather than something inferred. The obvious rule is "use the column
     * when there is writing to put in it" — read `card`, `moveList` and
     * `progressBar` and switch on those. All three are `live`, which means the
     * studio changes them WITHOUT a rebuild, and the column is not something a
     * frame can change its mind about between frames: it is the camera's
     * projection and the size of every overlay plane, both fixed when the scene
     * is built. Inferred, toggling the score strip would leave the board framed
     * for a square with nothing beside it until something else forced a rebuild.
     *
     * So it is asked for, and asking rebuilds. `PANEL_MIN_ASPECT` still governs
     * — a square or portrait frame has no column to give — and the website turns
     * this off in `APP_DEFAULTS`, because a page that has its own move list beside
     * the canvas wants the whole canvas for the board. `?video=1` drops that
     * default with the rest and gets the upload's layout back.
     */
    panel: {
      type: 'toggle', label: 'Writing beside the board (wide frames)',
      group: 'Helpers', default: true,
    },
    card: { type: 'toggle', label: 'Move card', group: 'Helpers', default: true, live: true },
    moveList: { type: 'toggle', label: 'Score strip', group: 'Helpers', default: true, live: true },
    progressBar: { type: 'toggle', label: 'Progress bar', group: 'Helpers', default: true, live: true },
    squares: { type: 'toggle', label: 'Mark the move', group: 'Helpers', default: true, live: true },
    arrow: { type: 'toggle', label: 'Move arrow', group: 'Helpers', default: true, live: true },
    coords: { type: 'toggle', label: 'Coordinates', group: 'Helpers', default: true, live: true },
    net: { type: 'toggle', label: 'Explain the mate', group: 'Helpers', default: true, live: true },
    tray: { type: 'toggle', label: 'Captured pieces beside the board', group: 'Helpers', default: true, live: true },
    // The engine's verdict, as a bar in the column: White's share from the left
    // and the score at its end. Stockfish at a fixed depth, worked out once by
    // `scripts/make-chess-evals.mjs` and stored in `evals.js`, so a frame never
    // waits on an engine and two renders never disagree. A score is a fact about
    // the position, not commentary. Landscape's column only; it takes the line
    // the material balance had, and the two heaps of taken men stay under it.
    evalBar: { type: 'toggle', label: 'Evaluation bar', group: 'Helpers', default: false, live: true },
    // The one helper that is not writing laid over the picture but a thing on the
    // table — see `buildNames`. Live: the geometry is built either way and the
    // parameter only decides whether it is drawn.
    //
    // ON BY DEFAULT: the cards name the players at the start and the winner at
    // the end, and for everything between, the plates are the only thing in the
    // picture saying whose men are whose. The watch page asks for them too — see
    // `APP_DEFAULTS` in `chess-app/watch/player.js`. `?names=0` takes them off.
    names: { type: 'toggle', label: 'Nameplates on the table', group: 'Helpers', default: true, live: true },

    // FORCES — which side controls each square, painted on the board. See
    // `paintForces`. Off by default, in the studio and on the site alike: it is a
    // way of LOOKING at a position, switched on when it is wanted.
    forces: {
      type: 'select', label: 'Forces on the board', group: 'Helpers', default: 'off', live: true,
      options: [
        { value: 'off', label: 'Off' },
        { value: 'both', label: 'Both sides' },
        { value: 'w', label: 'White only' },
        { value: 'b', label: 'Black only' },
      ],
    },

    // The website the video is for. Last in the group because it is the one
    // helper that is not about the game being played — see the header for why it
    // is on the frame at all, and `drawSite` for where it lands.
    //
    // Live: it is a string painted onto a canvas, and `paintOverlay` repaints
    // only when the string changes. Empty is off.
    site: {
      type: 'text', label: 'Website in the corner', group: 'Helpers',
      default: 'chesszz.com', maxLength: 32, live: true,
    },

    // ---- taking a piece --------------------------------------------------
    // One knob for the whole strike, because the three parts of it are one
    // event and nobody wants to balance them against each other. 0 turns it
    // off and leaves the piece being lifted quietly off the board, which is
    // what this scene did first.
    strike: { type: 'range', label: 'Strike size', group: 'Capture', min: 0, max: 2, step: 0.05, default: 2, live: true },
    spark: { type: 'color', label: 'Spark colour', group: 'Capture', default: '#ffb257', live: true },
    shockwave: { type: 'toggle', label: 'Shockwave ring', group: 'Capture', default: true, live: true },

    // ---- check and mate --------------------------------------------------
    // One knob for the glow, for the reason `Strike size` is one: the surface,
    // the halo, the pool and the spill are one thing seen four ways and nobody
    // wants to balance them against each other. 0 leaves a checked king exactly
    // as he was — marked on the board and nowhere else — which is what this
    // scene did before there was any light on him at all.
    glow: { type: 'range', label: 'Check glow', group: 'Check', min: 0, max: 2, step: 0.05, default: 2, live: true },
    // Not the spark colour, and no longer a shade off it: a strike is a flash of
    // contact and this is a lamp, and the lamp is red because check is the one
    // thing on the board that is a warning. The orange it used to be sat close
    // enough to the spark that a capture and a check read as the same event.
    glowColour: { type: 'color', label: 'Glow colour', group: 'Check', default: '#ff0000', live: true },
    // The three beats at the end — the net closing, the king going over, the
    // room falling away. Off is the ending this scene had before: the net drawn
    // all at once over a board nobody has touched. See the header over
    // `mateShot` for why it is not a fire.
    closing: { type: 'toggle', label: 'The closing shot', group: 'Check', default: true, live: true },

    // ---- the set --------------------------------------------------------
    // Named sets rather than five colour pickers, which is what the rest of the
    // project does with a palette — and `game` means the set the chosen game
    // brings with it, so each game is played on its own board. The five pickers
    // below are still here and are what `custom` uses; the option is labelled so
    // that is visible in the panel rather than being a trap.
    board: {
      type: 'select', label: 'Board', group: 'Set', default: 'game', live: true,
      options: [
        { value: 'game', label: 'the game’s own' },
        ...Object.entries(SETS).map(([value, set]) => ({ value, label: set.label })),
        { value: 'custom', label: 'custom (the colours below)' },
      ],
    },
    lightSquare: { type: 'color', label: 'Light squares', group: 'Set', default: '#d9c69f', live: true },
    darkSquare: { type: 'color', label: 'Dark squares', group: 'Set', default: '#6d4a33', live: true },
    frame: { type: 'color', label: 'Frame', group: 'Set', default: '#2b2018', live: true },
    whiteMen: { type: 'color', label: 'White pieces', group: 'Set', default: '#efe4cd', live: true },
    blackMen: { type: 'color', label: 'Black pieces', group: 'Set', default: '#1c2028', live: true },

    // Which men are on the board. Whole sets rather than a style slider
    // between them, because they are not two points on one axis: a Staunton
    // piece is turned on a lathe and a figure is carved, and `figures.js` builds
    // its men out of bodies and heads and hats instead of out of profiles.
    //
    // NOT live, and it is the only thing in this group that is not: the switch
    // rebuilds six geometries and thirty-two meshes. Everything else here is a
    // colour written onto a material that already exists.
    //
    // The club set is the default: it is still a Staunton, so it is READ as a
    // game score — a knight at a glance is a knight, where a carved figure's head
    // is a shape to work out — and it is the particular set in the reference
    // photograph rather than the reduced one, which was asked for as the look of
    // the board. The standard set and the figures are one click away; the figures
    // are what a viewer stops scrolling for, so they are the choice for a render
    // whose job is to earn the click rather than to carry the game.
    men: {
      type: 'select', label: 'Piece style', group: 'Set', default: 'club',
      options: [
        { value: 'figures', label: 'carved figures' },
        { value: 'club', label: 'club Staunton (the carved set)' },
        { value: 'staunton', label: 'Staunton (the standard set)' },
      ],
    },

    // ---- material -------------------------------------------------------
    // All live: the materials outlive a frame, so nothing here needs a rebuild.
    //
    // These four replace a single `Gloss` slider that drove roughness and
    // metalness together off one number. It sounded tidier and was worse in both
    // directions — it could not reach a matt boxwood set OR a polished one,
    // because the two properties do not move together on any real chess set, and
    // it named neither of the things it was actually setting.
    //
    // A tournament set is lacquered wood: rough enough not to mirror the room,
    // barely metallic at all — roughness ~0.46 against metalness ~0.06, which is
    // where these two sat. Metalness past ~0.3 is where the men stop being wood
    // and start being cast metal, "a legitimate set and a completely different
    // picture", and it is the picture this scene now opens on: polished at the
    // bottom of the roughness stop and better than half metal, so the men are
    // turned metal that takes the key light as a hard highlight and the room as
    // everything else. `reflection` and `exposure` are up to match, because a
    // metal with nothing to reflect is a flat silhouette.
    roughness: { type: 'range', label: 'Roughness', group: 'Material', min: 0.05, max: 1, step: 0.01, default: 0.05, live: true },
    metalness: { type: 'range', label: 'Metalness', group: 'Material', min: 0, max: 1, step: 0.01, default: 0.58, live: true },
    // The other two sets. Each is a material MODEL rather than another setting of
    // the two sliders above, and neither is reachable from them: what makes glass
    // glass is that the board is visible through it, and what makes marble marble
    // is veining under a polish. Live, because all three sets of materials exist
    // from setup and this only picks one — see `buildPieces`. The two sliders
    // under it do nothing outside glass and the two above it do nothing in glass;
    // the panel has no way to say so, so they are labelled instead.
    //
    // Wood and marble both carry a painted grain — see the surfaces section — so
    // "lacquered wood" is a real blank of it rather than a brown colour with a
    // sheen, and it is the default because it is what a tournament set is.
    finish: {
      type: 'select', label: 'Piece finish', group: 'Material', default: 'wood', live: true,
      options: [
        { value: 'wood', label: 'lacquered wood' },
        { value: 'marble', label: 'polished marble' },
        { value: 'glass', label: 'frosted glass' },
      ],
    },
    // Polished at 0 — cast crystal, and it mirrors the room as a hard line down
    // each piece. It is also, at 0, nearly invisible: a clear man over a board of
    // light and dark squares is the squares, very slightly bent. Roughness blurs
    // what is transmitted as well as what is reflected, and that blur is what
    // gives the piece a body of its own — so the default is well up the slider
    // rather than at the bottom of it, and what the set looks like without anyone
    // touching this is frosted.
    //
    // The stop is still 0.5, and the reason is unchanged: past about 0.3 what
    // comes through a man is the average of the whole board rather than the
    // squares behind him, and the two sides start to look like each other. The
    // default is the top of that stop rather than the middle of it, and the tint
    // below is what pays for it: a glass set here is a BODY of frosted glass and
    // not a lens, so what a man loses in showing the squares through himself he
    // gets back in having a colour deep enough to tell the two sides apart.
    frost: { type: 'range', label: 'Frosting (glass)', group: 'Material', min: 0, max: 0.5, step: 0.01, default: 0.5, live: true },
    // How deeply coloured the glass is — clear crystal at 0, smoked at 1. It is
    // a distance rather than an opacity, and it is felt on the dark men far more
    // than on the light ones; see the attenuation note in `buildPieces` and the
    // mapping in `drawPieces`.
    tint: { type: 'range', label: 'Glass tint (glass)', group: 'Material', min: 0, max: 1, step: 0.02, default: 1, live: true },
    // The board is French-polished where the men are satin, so it gets its own
    // number rather than sharing theirs. The slab's sides follow it, a little
    // rougher — one piece of wood, two finishes. At the bottom of the stop it is
    // a mirror, which is what the metal men above want under them: the board is
    // the only surface in the picture big enough to carry their reflection.
    boardSheen: { type: 'range', label: 'Board sheen', group: 'Material', min: 0.05, max: 1, step: 0.01, default: 0.05, live: true },
    // How much of the surrounding room the set picks up. There is no visible
    // background here, so this is felt rather than seen: it is what puts a
    // highlight down one side of a bishop and keeps the black men from going to
    // flat silhouettes. Over 1 by default, because the default set is metal and a
    // metal is lit almost entirely by what is around it.
    reflection: { type: 'range', label: 'Reflection', group: 'Material', min: 0, max: 3, step: 0.05, default: 1.3, live: true },
    // And a bit under half a stop over the top of that, which is what the polished
    // set costs: most of a metal man's surface is turned away from the one key light,
    // so the same board comes back darker than it did in wood.
    exposure: { type: 'range', label: 'Exposure', group: 'Material', min: 0.3, max: 2.5, step: 0.05, default: 1.35, live: true },

    // ---- the sound ------------------------------------------------------
    /**
     * How loud the board is: a knock as each piece lands, a strike as one is
     * taken, a bell on check and an ending on mate. `sound.js` is the whole of
     * how, and is worth reading for why the six pieces are one knock at six
     * pitches rather than six instruments.
     *
     * A LEVEL RATHER THAN A TOGGLE, on `Strike size`'s argument: off is a level
     * of zero and nobody wants two controls to say it. At 0 nothing is
     * synthesised and no audio context is so much as created, which matters more
     * here than it does for a slider that only scales a number — a page that
     * quietly holds an AudioContext open is a page some browsers put a speaker
     * icon on.
     *
     * IT IS NOT WHAT THE VIDEO HEARS, and that is the one thing about this
     * control worth knowing. A capture renders 5,400 PNGs as fast as the machine
     * will go and there is no clock for a sound to be played against, so the
     * upload's audio is a WAV rendered offline from the same table by
     * `scripts/make-chess-audio.mjs` and muxed by `encode.js --audio`. This
     * slider is how the studio and the website hear it; that script is how the
     * video does. Both read `soundEvents`, so tuning one tunes the other.
     *
     * Live, because it is a gain on a node that already exists.
     */
    sound: { type: 'range', label: 'Sound', group: 'Sound', min: 0, max: 1, step: 0.05, default: 1, live: true },

    /**
     * The game said out loud over the board: what was played, the headline
     * somebody wrote for it, and the paragraph under that headline where there is
     * room. `voiceover.js` is the whole of which of those fit and when, and
     * `narrate.js` beside it is where the sentences come from.
     *
     * IT SAYS THE LIBRARY'S OWN WORDS AND NOTHING ELSE. There is no generated
     * commentary here and there must not be — a synthetic voice sounds exactly as
     * confident when it is making something up, and the move is derived from the
     * notation while everything after it is `games.js`'s own prose. Same rule the
     * website has followed since it learned to talk.
     *
     * ON BY DEFAULT, AS `Sound` IS, because the video this scene exists to make is
     * the narrated one. It was off for a while and the argument was that a voice
     * is a decision about what kind of video this is rather than part of the
     * picture — which is true, and the decision has been made: the game said out
     * loud is why an upload beats a diagram, and a default of 0 meant every
     * capture command had to remember to ask for it. Dial it to 0 for the wood
     * alone. It is still the one control here whose result depends on the machine:
     * the browser speaks with whatever voice the visitor has, and they vary from
     * good to robotic — which is why `chess-app/watch/player.js` turns it back off
     * for the web player, where a speaker button reads the same sentences at the
     * visitor's own pace.
     *
     * IT IS NOT WHAT THE VIDEO HEARS. Same as `Sound`: a capture has no clock for
     * a voice to be spoken against, so the upload's narration is rendered offline
     * by `scripts/make-chess-audio.mjs --narrate` and muxed by `encode.js
     * --audio`. Both sides read `sayEvents`, so the sentences are the same ones.
     */
    narration: { type: 'range', label: 'Narration', group: 'Sound', min: 0, max: 1, step: 0.05, default: 1, live: true },
    // Every note said in full, with the length following from it: each half-move
    // is held for as long as its move, headline and note take to say, and
    // `Length` stops being a control. Off, the narration fits the length instead
    // and drops what does not fit. Not live — it moves every move in the game.
    analysis: { type: 'toggle', label: 'Narrate the whole analysis (sets the length)', group: 'Sound', default: false },

    // ---- the outline ----------------------------------------------------
    /**
     * A line round each man, off by default. `shared/outline.js` is the whole of
     * how it is done, and it is worth reading for why it is an inverted hull
     * rather than three.js's outline example: the example masks the objects it is
     * given as ONE region, so a rank of pieces standing shoulder to shoulder comes
     * back with a line round the row.
     *
     * WHY A CHESS SCENE WANTS ONE, given it already has shadows and a key light.
     * Every other control in the Material group changes what the men are MADE of;
     * this one changes how easy they are to TELL APART from what is behind them,
     * and those are different problems. A light man on a light square in a
     * high-key set has a silhouette worth perhaps two shades of contrast, and the
     * finish that looks best in a still — polished marble, frosted glass — is the
     * one that costs the most of it. The line puts the contrast back without
     * touching the set, which is the only way to have both.
     *
     * THE MEN AND NOT THE BOARD, NOT THE MOVE, NOT THE KING IN CHECK. The
     * temptation with a selective outline is to make it mean something — the piece
     * that just moved, the man who is attacked — and every one of those is already
     * said, better, by something built for it: `squares` marks the move, `arrow`
     * draws it, `glow` lights the checked king and `net` explains the mate. An
     * outline that ALSO tried to say one of those would be a second voice saying
     * the same sentence, and a viewer would have to learn which. So it says the
     * one thing nothing else says: this is a piece, and it ends here.
     *
     * ON BY DEFAULT, which it was not while the men were matt wood on a satin
     * board. It is the default set that changed the answer: polished metal men
     * over a mirrored board are drawn almost entirely by what they reflect, and
     * what they reflect is the board — so a man's edge is the same handful of
     * shades as the square under him, in the frame where he stands still and in
     * the frame where he is halfway across the board. That is the case the line
     * was built for, and it is now the case a capture renders by default rather
     * than only the case `chess-app/` had on a phone.
     */
    ...outlineParams({
      group: 'Outline',
      label: 'Outline the men',
      lightLabel: 'Line on the dark men',
      darkLabel: 'Line on the light men',
      on: true,
    }),

    // ---- how much of it to draw -----------------------------------------
    // What the shadow map costs, and nothing else. `high` is what every capture
    // has ever rendered and is the default, so this parameter changes no video
    // unless it is asked for; the two tiers below it exist for the website in
    // `chess-app/`, which has to hold 30fps live on a phone rather than take as
    // long as it likes per frame. See `qualityTier`.
    //
    // Shadows, because they are both the largest single cost here and the one
    // that can be cut without changing the composition: a 1024 map over the same
    // 19 units is 54 pixels a square instead of 160, which is a softer edge under
    // a piece rather than a missing one. Everything the shot is actually made of
    // — the framing, the writing, the strikes, the mating net — is untouched at
    // every tier, so a frame rendered at `low` is the same frame.
    quality: {
      type: 'select', label: 'Quality', group: 'Format', default: 'high',
      options: [
        { value: 'high', label: 'high — 3072px soft shadows (captures)' },
        { value: 'medium', label: 'medium — 1536px soft shadows' },
        { value: 'low', label: 'low — 1024px hard shadows' },
      ],
    },

    // ---- camera ---------------------------------------------------------
    // 2D IS A DIFFERENT BOARD, NOT A DIFFERENT ANGLE. The camera goes straight
    // overhead with a long lens and stays there — no shots, no hand on it — and
    // the men become the flat, drawn pieces a diagram uses, because a turned
    // piece seen from directly above is a set of rings and nothing else. Every
    // other part of the scene is the same one: the board, the moves, the strikes,
    // the writing. Not live, because the men are different geometry. See
    // `placeFlatCamera` and `flatGeometry`.
    dimension: {
      type: 'select', label: 'Board', group: 'Camera', default: '3d',
      options: [
        { value: '3d', label: '3D' },
        { value: '2d', label: '2D — flat, from above' },
      ],
    },
    // Which men the 2D board draws: the traced Staunton men, or the diagram set
    // the chess-app's 2D board uses (see `DIAGRAM_MEN`). Not live, for the same
    // reason `dimension` is not: the men are different geometry.
    flatSet: {
      type: 'select', label: '2D pieces', group: 'Camera', default: 'traced',
      options: [
        { value: 'traced', label: 'Staunton, drawn' },
        { value: 'diagram', label: 'diagram (the site’s 2D board)' },
      ],
    },
    shots: { type: 'toggle', label: 'Scripted shots', group: 'Camera', default: true },
    tilt: { type: 'range', label: 'Camera height', group: 'Camera', min: 12, max: 80, step: 1, default: 42 },
    // How much the baked camera track is smoothed, in seconds. Not live: the
    // smoothing runs once over the whole timeline, which is what makes it cheap
    // and what keeps the shot a function of the frame rather than of the frame
    // before it.
    steady: {
      type: 'range', label: 'Steadiness', group: 'Camera',
      unit: 's', min: 0, max: 1.2, step: 0.05, default: 0.35,
    },
    // HOW MUCH OF A CHANGE IS WORTH A CAMERA MOVE, in degrees of azimuth, with
    // the slack on the other three channels scaled off it — see `HOLD`.
    //
    // The dial a viewer's complaint turned into. There was a camera key per
    // half-move and each one asked for something, so the frame was never once
    // still: a board that a viewer is trying to READ, drifting under them
    // forty-five times in three minutes. This is the threshold under which an
    // ask is simply dropped, and dropping is the point — a gentler version of a
    // move that should not happen is still a move that should not happen.
    //
    // 0 is the old behaviour exactly: every ask honoured, a key per half-move.
    // 22 degrees is a quarter turn of the board, which is the smallest change of
    // angle that shows you something you could not see before; under it, all a
    // turn does is move the squares about. At the top of the range only the swing
    // to the mating side and the closing pull-up survive, which is a legitimate
    // way to shoot a game and is what a long score wants.
    //
    // Not live: the whole track is baked from it. See `buildCameraTrack`.
    hold: {
      type: 'range', label: 'Hold the frame', group: 'Camera',
      unit: '°', min: 0, max: 45, step: 1, default: 22,
    },
    follow: { type: 'range', label: 'Follow the move', group: 'Camera', min: 0, max: 1, step: 0.05, default: 0.35, live: true },
    // Enough to keep the frame from looking like a still photograph of a board,
    // and no more. It was 0.3, which is ±3.8 degrees of azimuth on a 20-second
    // cycle: small, and the one movement on the list that never stops. Holding
    // the frame everywhere else made it the most visible thing left — a shot
    // that has stopped moving between moves should be still between them.
    drift: { type: 'range', label: 'Drift', group: 'Camera', min: 0, max: 1, step: 0.05, default: 0.12, live: true },
    zoom: { type: 'range', label: 'Framing', group: 'Camera', min: 0.7, max: 1.6, step: 0.01, default: 1, live: true },
    // A CAMERA ANGLE HELD FOR THE WHOLE VIDEO, in place of the shot list:
    // `az,el,zoom,panR,panU`, empty for the composed shots.
    //
    // It is a string rather than five sliders because nobody arrives at an angle
    // by typing one. In the studio it is written by dragging the board — see
    // `studio-camera.js` — and this is where the drag LANDS, so that the URL, the
    // saved state and the copied capture command all carry the angle that is on
    // screen. A Short is a fifteen-second phase seen from one place, and that
    // place is a matter of taste about a particular position; the shot list
    // cannot know it and this is how it gets told.
    //
    // The spelling is `chess-app/board-camera.js`'s to the character, so a view
    // dragged on the website and shared as `?view=-35,52,1.4,0,0` pastes straight
    // into a capture as `--set held=-35,52,1.4,0,0`.
    //
    // Live: `update` reconciles the baked track with it, which is a refill of the
    // channels rather than a rebuild of the scene. See `holdView`.
    held: {
      type: 'text', label: 'Held view', group: 'Camera',
      default: '', maxLength: 48, live: true,
    },

    ...tutorialParams(),

    // ---- the overlay clip ------------------------------------------------
    //
    // THE GAME AS A LAYER FOR ANOTHER VIDEO, and the one group in this panel
    // that is a JOB rather than a part of the picture: slow the moves down, give
    // them a lift and a landing, mark who moved, leave a trail behind, and take
    // the room away from behind the board. Every one is off by default, so a
    // video or the website renders exactly as it did before any of it existed.
    //
    // Last in the spec because the panel's groups follow the order they are
    // declared in, and this is the group to scroll past unless it is what you
    // came for. `Piece travel` stays under Timing: it is an old control that
    // every render uses, and these only lean on it.
    //
    // The two buttons in the bar paste the whole job — see `commands` below.
    // A move normally travels for at most 42% of its slot and the board holds
    // still for the rest. On, it may take 85%: for a render cut into another
    // video, where the piece moving is the picture. If the video is too short to
    // give every move its full `Piece travel`, the console says what length
    // would. Not live, for `travel`'s reason. See `buildTimeline`.
    slow: { type: 'toggle', label: 'Slow moves', group: 'Overlay clip', default: false },
    // The piece is lifted, leans into the glide and settles as it lands, rather
    // than sliding low across the board. Not live: the strike on a capture is
    // timed to when the piece arrives, and this changes when that is. See
    // `liftedMove`.
    lift: { type: 'toggle', label: 'Lift and settle', group: 'Overlay clip', default: false },
    // A glowing ring on the board under the piece that is moving, gold for White
    // and blue for Black, that pulses out once as it lands. Off by default: it is
    // for renders cut into another video, where it says at a glance who moved.
    // See `drawMoveRing`.
    ring: { type: 'toggle', label: 'Glow under the moving piece', group: 'Overlay clip', default: false, live: true },
    // A see-through copy of the moving piece left standing on the square it came
    // from, fading out as it goes. Off by default, for `ring`'s reason. See
    // `drawGhosts`.
    ghost: { type: 'toggle', label: 'Ghost on the square it left', group: 'Overlay clip', default: false, live: true },
    // How many ghosts each square of the trip leaves. 1 is the one on the square
    // it came from; above that they are dropped along the way as well, evenly
    // spaced along the path, so a two-square move at 3 leaves six. See
    // `drawGhosts`.
    ghosts: {
      type: 'range', label: 'Ghosts per square', group: 'Overlay clip',
      min: 1, max: 6, step: 1, default: 1, live: true,
    },
    // Nothing behind the board: no backdrop, no vignette, and the table only
    // where the board's shadow falls on it, as see-through dark. For laying the
    // game over other footage — capture with `--alpha` and encode with `--alpha`
    // for a ProRes 4444 .mov. The writing beside the board, the cards and the
    // strip are still drawn, and each has its own switch under Helpers.
    transparent: { type: 'toggle', label: 'Transparent background', group: 'Overlay clip', default: false },
  },

  // ONE BUTTON IN THE BAR: `Copy capture command`, which pastes the whole
  // render — the frames, the sound (`audio` above, with `--reel` when the reel
  // is on), and the encode that muxes them. The overlay, WebM and audio-only
  // buttons that sat beside it are gone; the `Transparent background` switch
  // still works with `capture.js --alpha` by hand.

  setup(ctx) {
    const { THREE, scene, camera, renderer } = ctx;
    const p = ctx.params;
    verticalOnFlat(p);

    // Tone mapping is a property of the scene; the exposure through it is a
    // parameter, and update() writes it every frame.
    renderer.toneMapping = THREE.NeutralToneMapping;
    // Pieces on a board is the one subject in this project where a cast shadow
    // is not decoration: it is what tells you which square a piece is on when
    // the camera is low and the piece is between two of them.
    renderer.shadowMap.enabled = true;
    const tier = qualityTier(p.quality);
    renderer.shadowMap.type = tier.soft ? THREE.PCFSoftShadowMap : THREE.BasicShadowMap;

    // ---- the game -------------------------------------------------------
    /**
     * Everything that comes from WHICH GAME is being played, in one place.
     *
     * A capture calls this once, from here, with the six lines it always was.
     * It is a function because there is now a second caller: `chess-app/play/`
     * plays a game that does not exist yet — a half-move is appended to the
     * score under the renderer whenever somebody moves — and these are exactly
     * the tables that have to be rebuilt against it. The board, the men, the
     * room, the materials and the lights are NOT among them, which is the whole
     * point: rebuilding those as well, which is what `setParams({ game })` does,
     * measured 400-1000ms a move against the 2ms it takes to draw a frame.
     *
     * `timing` is how that caller keeps its own clock. `buildTimeline` divides a
     * FIXED length among however many moves there are, so appending a move to a
     * live game would silently re-time every move already played.
     */
    /**
     * The thing that makes the noise, or nothing at all in a capture.
     *
     * Made here rather than in `bakeGame` because a bake is a re-bake most times
     * it runs — a different game, the other set of men — and the events are the
     * only part of this that changes when it does. The `AudioContext` under it
     * belongs to the page and outlives every one of them; see `sharedContext`.
     */
    ctx.sound = ctx.mode === 'capture' ? null : createSound();
    ctx.voiceOver = ctx.mode === 'capture' ? null : createVoiceOver();

    ctx.bakeGame = (game, timing = timelineFor) => {
      // Null the first time through, which is how this tells the two cases apart.
      const carry = ctx.actors;
      ctx.g = game;
      ctx.game = replay(game.moves);
      ctx.actors = buildActors(ctx.game);
      // The two men the heat can ever be on. Found once a bake rather than
      // searched for per frame, and safe to hold onto: a king is never taken and
      // never promotes, so neither actor changes shape or leaves the board.
      ctx.kings = {
        w: ctx.actors.find((a) => a.color === 'w' && formAt(a, 0) === 'k'),
        b: ctx.actors.find((a) => a.color === 'b' && formAt(a, 0) === 'k'),
      };
      ctx.timeline = timing(ctx, ctx.game);
      // Before the camera track, which reads the closing square off it: the
      // closing shot and the last phase's angle are both "the end of the board
      // this finished at", and that is a fact about the final position rather
      // than something a game should have to write down. See `framing.js`.
      //
      // TWO KINDS OF NET, ONE SHAPE. A game that ends in mate gets `mateNet` —
      // the king, and every square he cannot use. A game that ends in a
      // resignation or a draw has no such argument to make, so it gets
      // `finishNet` instead: the move the game stopped on, and what that move
      // does. Both return `{ king, checkers, squares, guards }` and `paintNet`
      // draws either without knowing which it has; for a finish, `king` is the
      // square the last move LANDED on rather than a king. See THE TWO WAYS A
      // GAME ENDS in `games.js` and `endingOf` in `framing.js`.
      const finish = ctx.game.plies[ctx.game.plies.length - 1];
      ctx.net = endingOf(ctx.g) === 'mate' || !finish
        ? mateNet(ctx.game.final)
        : finishNet(ctx.game.final, finish);
      ctx.camTrack = buildCameraTrack(ctx, ctx.game);
      // On the first bake the men do not exist yet: setup() builds them, and the
      // strikes off this timeline, a few lines below. On every bake after it both
      // have to be caught up here. The actors are NEW objects, so they are handed
      // the meshes the old ones were holding — plus one for a promotion, which is
      // a shape the actor did not have when the men were built — and the strikes
      // are dropped and rebuilt because they are keyed by ply and timed off the
      // timeline that has just changed underneath them.
      if (carry) {
        fitMeshes(ctx, carry);
        clearStrikes(ctx);
        buildStrikes(ctx);
      }
      // Last, because it reads the timeline this bake has just replaced. A live
      // game re-bakes on every half-move, so this is also how a move played in
      // `chess-app/play/` gets a sound at all.
      // A reel's sound is already in output seconds — see `reelEvents` — and
      // the same events `make-chess-audio.mjs` renders, so the studio plays
      // what the upload will carry.
      if (ctx.timeline.reel) {
        const { sounds, said } = reelEvents(ctx.g, ctx.game, ctx.timeline);
        ctx.reelWords = reelWords(ctx.g, ctx.game.plies, ctx.timeline.reel);
        ctx.sound?.load(sounds);
        ctx.voiceOver?.load(said);
      } else {
        ctx.reelWords = null;
        ctx.sound?.load(soundEvents(ctx.game, ctx.timeline));
        ctx.voiceOver?.load(sayEvents(ctx.g, ctx.game, ctx.timeline));
      }
    };
    ctx.bakeGame(gameById(p.game));

    // ---- surroundings ---------------------------------------------------
    const lights = placeLights([
      { elev: 62, theta: 0.13, power: 14, focus: 30, tint: [1, 0.97, 0.92] },
      { elev: 24, theta: 0.66, power: 5, focus: 20, tint: [0.80, 0.87, 1] },
      { elev: -6, theta: 0.40, power: 2.5, focus: 16, tint: [1, 0.94, 0.88] },
    ]);
    const base = srgbToLinear('#14181f');
    const world = buildWorld(THREE, renderer, {
      key: 'chess-room-v1',
      shade: (dx, dy, dz, out) => {
        const up = Math.max(0, dy);
        const down = Math.max(0, -dy);
        out[0] = base[0] * (0.55 + 2.0 * up + 0.8 * down);
        out[1] = base[1] * (0.55 + 1.95 * up + 0.8 * down);
        out[2] = base[2] * (0.55 + 2.1 * up + 0.75 * down);
        addLights(lights, 1, dx, dy, dz, out);
      },
    });
    scene.environment = world.env;

    const key = new THREE.DirectionalLight(0xfff3e0, 2.3);
    key.position.set(5.5, 9.5, 6.0);
    key.castShadow = true;
    key.shadow.mapSize.set(tier.shadow, tier.shadow);
    // The board's half-diagonal is 6.4, so the ortho box has to hold 7 to keep
    // a corner piece's shadow inside the map. 2048 over 14 units is 146 px per
    // square, which is where the soft edge comes from rather than from the
    // filter.
    // The box is in the LIGHT's frame, not the world's, so it is not the board's
    // half-width that has to fit in it: the slab's half-diagonal is 6.4, the
    // trays reach 6.0, and a corner piece is another metre up. At 7 a straight
    // diagonal line of "shadow" cut right across the board, which is the edge of
    // the map being sampled outside itself. 9.5 covers everything that casts,
    // and the map is 3072 at `high` to keep 160 pixels per square while it does.
    Object.assign(key.shadow.camera, {
      left: -9.5, right: 9.5, top: 9.5, bottom: -9.5, near: 4, far: 26,
    });
    key.shadow.bias = -0.0012;
    key.shadow.normalBias = 0.045;
    scene.add(key);

    const fill = new THREE.DirectionalLight(0xcadcff, 0.55);
    fill.position.set(-6, 3.5, -4.5);
    scene.add(fill);
    const ambient = new THREE.AmbientLight(0xffffff, 0.30);
    scene.add(ambient);

    // Kept, with what they were built at, because the closing shot takes the room
    // down and has to be able to put it back: every frame is drawn from scratch
    // and may be drawn in any order, so "dimmer than last frame" is not a thing
    // that can be said here. `dimRoom` writes all three every frame from these.
    ctx.lights = {
      key, fill, ambient, keyPower: key.intensity, fillPower: fill.intensity,
      ambientPower: ambient.intensity,
    };

    // ---- the board ------------------------------------------------------
    ctx.set = resolveSet(ctx);
    buildBoard(ctx);
    // After the board, and it has to be: a plate lies ON the table, and the table
    // is the board's — `buildNames` reads its height off `ctx.table` rather than
    // keeping a second copy of the number.
    buildNames(ctx);
    // Before the men, because `fitMeshes` hangs a hull off each mesh as it makes
    // it — including the meshes a live game grows later, which is why this is on
    // `ctx` rather than a local.
    ctx.outline = outlineHulls(ctx);
    buildPieces(ctx);
    buildStrikes(ctx);
    buildKingHeat(ctx);

    // ---- writing --------------------------------------------------------
    // The overlays hang off the camera, and a camera outside the scene graph is
    // never traversed — its children would simply not be drawn.
    scene.add(camera);

    // ---- where the board goes, and therefore what the camera is fitted to ---
    //
    // THE PROJECTION IS THE WHOLE OF THE PANEL LAYOUT. Rendering the board into
    // the left part of a wide frame is not a matter of moving the camera: move it
    // and `fitDistance` argues back, because the fit's job is to put the board in
    // the middle of whatever it is told the frame is. So the camera is told.
    // `setViewOffset(bw, H, 0, 0, W, H)` makes the frustum the board region's
    // EXTENDED to the right — three keeps the vertical extent, takes the aspect
    // as bw/H, and scales the horizontal by W/bw, which lands the camera's own
    // axis at pixel bw/2 and the region's far edge at pixel bw. Everything
    // downstream then works without knowing: `fitDistance` asks the layout for
    // the region's aspect, and every project/unproject in `chess-app/camera.js`
    // goes through this matrix and so is already right.
    // THE FLAT BOARD'S LENS, set here because the writing planes below are hung at
    // a distance worked out from `near`, and they have to be hung for the camera
    // they will be seen through. Undone by `build()` in shared/engine.js on every
    // rebuild, which resets fov, near, far and up, so going back to 3D needs
    // nothing from here. See `placeFlatCamera`.
    if (p.dimension === '2d') {
      camera.fov = FLAT_FOV;
      camera.near = FLAT_NEAR;
      camera.far = FLAT_FAR;
      // Straight down, with the screen's up along the board's files — without it
      // `lookAt` has no way to choose which way round a board seen from above is.
      camera.up.set(0, 0, -1);
    }

    const L = frameLayout(ctx);
    ctx.layout = L;
    if (L.panel) camera.setViewOffset(L.board.w, L.board.h, 0, 0, ctx.width, ctx.height);
    else {
      // `build()` in shared/engine.js sets the aspect and nothing clears a view
      // offset, so a rebuild into portrait after a panel frame would inherit the
      // board region's. Both halves are needed: `setViewOffset` overwrote
      // `aspect`.
      camera.aspect = ctx.width / ctx.height;
      camera.clearViewOffset();
    }
    camera.updateProjectionMatrix();

    const a = ctx.width / ctx.height;
    ctx.hudReel = null;
    if (L.panel) {
      // ONE PLANE FOR ALL THE WRITING, because in a column it is one block: the
      // mark, the card and the score read top to bottom in a single measure, and
      // three planes would be three sets of margins to keep in step. It repaints
      // on the same key the card and the strip used to repaint on separately, so
      // the cost is one canvas a half-move rather than two. See `drawPanel`.
      ctx.hudPanel = makeOverlay(ctx, { x0: L.panelX, y0: 0, x1: 1, y1: 1 });
      // THE OPENING TITLE IS OVER THE BOARD, NOT OVER THE FRAME. It holds the
      // whole frame in the other two layouts, and the first pass kept that here
      // — the rule it comes from is that two pieces of writing dissolving past
      // each other in different parts of the frame reads as a fault, which is
      // also why the card is suppressed under it. Rendered, it was wrong twice:
      // the title sat centred on the frame and so half over a column it had
      // nothing to do with, and its wash is not opaque, so the panel's own edge
      // and rule showed through it as a rectangle. Confined to the board's own
      // region it is centred on it — and confining it is also what lets the column
      // be full while it is up, which is the whole of why the intro card and the
      // score are no longer held back until the first move. See `drawHud`.
      ctx.hudOpen = makeOverlay(ctx, { x0: 0, y0: 0, x1: L.panelX, y1: 1 });
      // The cold open's hook and puzzle, over the board for the opener's reason.
      ctx.hudReel = coldOpenOn(p) ? makeOverlay(ctx, { x0: 0, y0: 0, x1: L.panelX, y1: 1 }) : null;
    } else {
      // Full frame width, though the writing only uses the left of it: the scrim
      // behind the text has to fade out somewhere, and a plane narrower than the
      // frame puts a straight edge wherever it ends. See drawCard.
      //
      // PORTRAIT NEEDS A DEEPER TOP BAND, and it is not a taste. A 9:16 frame is a
      // Short or a Reel, and both of them draw their own furniture over the top of
      // it: on 1080x1920 the top 220 pixels carry the app's controls. The aspect
      // formula gave portrait a 378px band, so the eyebrow, the move and the lead
      // all landed inside those 220 and were simply not visible on a phone. A
      // deeper band is what buys room to start the text BELOW them — see
      // `SAFE_TOP` and the portrait branch in `drawCard`.
      const topH = a < 1 ? 0.30 : Math.min(0.31, 0.35 * a);
      const botH = Math.min(0.24, 0.28 * a);
      ctx.hudTop = makeOverlay(ctx, { x0: 0, y0: 0, x1: 1, y1: topH });
      // 0.020 of the frame's height clear of the bottom edge, which is what the
      // old anchor-and-inset spelling worked out to.
      ctx.hudBottom = makeOverlay(ctx, { x0: 0, y0: 1 - 0.020 - botH, x1: 1, y1: 1 - 0.020 });
      // The opening title is the whole frame rather than a corner of it: it is
      // centred, and the wash behind it runs to both edges. Added last, so it is
      // drawn over the other two at the same render order.
      ctx.hudOpen = makeOverlay(ctx, { x0: 0, y0: 0, x1: 1, y1: 1 });
      // The website mark, top right, on its own plane rather than in a corner of
      // `hudTop`: that band is painted only while the move card is up and is
      // suppressed for the whole opening title, and this has to be on the frame
      // whenever the frame is. Added LAST, so it sits over the opener's wash
      // instead of under it. Portrait gets a deeper and wider band for `hudTop`'s
      // reasons exactly — the type starts below the app's controls, and a 9:16
      // frame is 56% as wide, so the same string needs more of it. See `drawSite`.
      const siteW = a < 1 ? 0.62 : 0.42;
      const siteH = a < 1 ? 0.24 : 0.14;
      ctx.hudSite = makeOverlay(ctx, { x0: 1 - siteW, y0: 0, x1: 1, y1: siteH });
      // The reel's hook, puzzle and close, the whole frame like the opener. Only
      // made when the reel is on: an overlay is a full-frame canvas, and nothing
      // else would ever paint it.
      ctx.hudReel = p.reel || coldOpenOn(p) ? makeOverlay(ctx, { x0: 0, y0: 0, x1: 1, y1: 1 }) : null;
    }

    /**
     * Put the camera where a frame would put it, without drawing the frame.
     *
     * For the websites, and for one job: zooming and panning ABOUT THE POINTER
     * rather than about the middle of the frame. Both of those are the same
     * question — "what view puts this bit of the board back under the cursor" —
     * and the only honest way to answer it is to try the view and look, because
     * where a square lands on screen comes out of `fitDistance` projecting the
     * board's corners, the tone of the aim point and the perspective divide.
     * Reimplementing that arithmetic in a page would be a second copy of the
     * hardest part of this file, kept in step by hand.
     *
     * So a page writes the view it is considering into the track, calls this,
     * measures `ctx.camera` with three's own project/unproject, and puts the
     * track back. A frame is never drawn and nothing is left changed — the next
     * `renderFrame` places the camera again from whatever the track says then.
     */
    ctx.aimCamera = (frame) => placeCamera(ctx, frame / ctx.fps);

    /**
     * How many seconds the closing shot needs after the mating move lands.
     *
     * Published because two pages have to leave room for it and neither can work
     * it out: `chess-app/play/live-game.js` rests a finished game this far past
     * the mate rather than a tenth of a second past it, and
     * `chess-app/watch/player.js` gives the step button this much tail on the
     * last move. Written down once here, so a beat added to the shot does not
     * quietly leave both of them freezing part way through it.
     */
    ctx.closingShot = MATE_SHOT;

    report(ctx);
  },

  update(ctx) {
    // Again every frame: `tray` is live, and a live change never reaches setup.
    verticalOnFlat(ctx.params);
    if (ctx.params.transparent) seeThrough(ctx);
    else {
      applyTutorialLook(ctx, { plain: 0x080a0e });
      paperSurface(ctx, ctx.table, { worldSize: 70, cell: 0.5 });
    }

    // A reel shows its source timeline out of order — the payoff first, then a
    // rewind — so everything below draws at the SOURCE instant, and only the
    // reel's own writing and the sound, which are timed in output seconds, read
    // `out`. Everywhere else the two are the same number.
    const out = ctx.frame / ctx.fps;
    const reel = ctx.timeline.reel;
    const t = reel ? sourceTime(reel, out) : out;
    ctx.reelNow = !(reel && ctx.reelWords) ? null
      : ctx.params.cover ? { kind: 'cover', ...ctx.reelWords.cover, alpha: 1 }
        : reelMoment(reel, ctx.reelWords, out);
    const view = viewAt(ctx, t);

    // WHO HAS THE CAMERA. `ctx.heldView` is a hand on the board — the studio's
    // drag (`studio-camera.js`), and on the website `place` in
    // `chess-app/board-camera.js` does the same job a frame at a time. Nothing
    // sets it in a capture. Failing that, the `Held view` parameter is the same
    // view written down, and the track is re-baked when the two disagree.
    //
    // Here rather than in setup() because both are live: refilling six channels
    // is a few microseconds, and rebuilding the scene to turn the board would
    // make a drag unusable.
    if (ctx.heldView) holdView(ctx.camTrack, ctx.heldView);
    else if (ctx.camTrack.held !== ctx.params.held) ctx.camTrack = buildCameraTrack(ctx, ctx.game);

    // The Material group, all of it live and none of it needing a rebuild. Two
    // of these are properties of the scene and the renderer rather than of any
    // material, which is why they are written here rather than in setup: a live
    // parameter changes nothing unless the frame that follows it reads the value.
    ctx.scene.environmentIntensity = ctx.params.reflection;
    ctx.renderer.toneMappingExposure = ctx.params.exposure;
    ctx.boardMat.roughness = ctx.params.boardSheen;
    ctx.slabMat.roughness = clamp(ctx.params.boardSheen + 0.13, 0.05, 1);
    // The set, resolved once and handed to everything that draws in it.
    ctx.set = resolveSet(ctx);
    ctx.slabMat.color.set(ctx.set.frame);

    drawPieces(ctx, view, t);
    // After the men, for `drawKingHeat`'s reason: it follows the mover's mesh.
    drawMoveRing(ctx, t);
    // And this borrows the mover's geometry and material as they are this frame.
    drawGhosts(ctx, t);
    drawStrikes(ctx, t);
    // After the men, never before: the glow hangs off the king's MESH, and his
    // mesh is not where it belongs this frame until `drawPieces` has put it there
    // — which at the end of a game means laid on his side.
    drawKingHeat(ctx, view, t);
    // The outline settles after the room does, and takes the room's level with
    // it: a hull is a `MeshBasicMaterial` and so is not lit, so without this the
    // closing shot would dim everything on the board EXCEPT the lines round the
    // men, and leave them the brightest thing in a picture that is going quiet.
    ctx.outline.settle(dimRoom(ctx, mateShot(ctx, t)));
    placeCamera(ctx, t);
    paintBoard(ctx, view, t);
    drawNames(ctx);
    drawHud(ctx, view, t);
    if (ctx.hudReel) {
      const m = ctx.reelNow;
      if (m?.kind === 'cover') {
        m.at = coverTarget(ctx, m, view);
        // `coverTarget` answers in fractions of the FRAME; in landscape the
        // overlay is the board's region only, the left `panelX` of it.
        const px = ctx.layout?.panelX ?? 1;
        if (m.at && px < 1) m.at = { ...m.at, x: m.at.x / px, r: m.at.r / px };
        // And the words go in the column (see `drawHud`); over the board, the ring.
        if (px < 1) m.ringOnly = true;
      }
      paintOverlay(ctx.hudReel, !!m, (g, W, H) => drawReel(g, W, H, m),
        m && [m.kind, m.eyebrow, m.title, m.sub, m.count, m.at && `${m.at.x.toFixed(3)},${m.at.y.toFixed(3)}`,
          m.progress === undefined ? '' : m.progress.toFixed(2)].join('|'));
      ctx.hudReel.mat.opacity = m ? m.alpha : 0;
    }
    // After the picture, because that is what it is a sound OF — and it works out
    // for itself whether this frame is playback, a scrub or the loop coming
    // round, since `update` is called the same way for all three. See `at`.
    ctx.sound?.at(reel ? out : t, ctx.params.sound);
    ctx.voiceOver?.at(reel ? out : t, ctx.params.narration);
  },
});

// ------------------------------------------------------------------- geometry

/**
 * The five colours in play: the chosen set, the game's own set, or the pickers.
 *
 * Read every frame rather than baked into the materials at build time, which is
 * what makes all of it `live` — nothing here changes any geometry, and a set is
 * exactly the kind of thing worth trying six of in a row.
 */
function resolveSet(ctx) {
  const p = ctx.params;
  if (p.board === 'custom') {
    return {
      light: p.lightSquare, dark: p.darkSquare, frame: p.frame,
      white: p.whiteMen, black: p.blackMen,
    };
  }
  return SETS[p.board] || SETS[ctx.g.board] || SETS[Object.keys(SETS)[0]];
}

/**
 * What `Quality` buys, in the two numbers it actually sets.
 *
 * A table rather than an expression so the tiers are readable as tiers, and
 * unknown values land on `high` — the capture default — because a website that
 * mistypes its own parameter should render the video's picture, not a degraded
 * one nobody asked for.
 */
function qualityTier(name) {
  const tiers = {
    high: { shadow: 3072, soft: true },
    medium: { shadow: 1536, soft: true },
    low: { shadow: 1024, soft: false },
  };
  return tiers[name] || tiers.high;
}

/**
 * Is this half of the set light enough to want a dark line round it?
 *
 * See the call in `drawPieces` for the argument. 0.2 is linear relative
 * luminance, which is about the middle of the sRGB ramp.
 */
function paleMan(hex) {
  const [r, g, b] = srgbToLinear(hex);
  return r * 0.2126 + g * 0.7152 + b * 0.0722 > 0.2;
}

/** Where the centre of a square sits in the world. */
function squarePos(sq) {
  return { x: fileOf(sq) - 3.5, z: 3.5 - rankOf(sq) };
}

/**
 * Where a captured piece is set down.
 *
 * White's losses go beside White's end, Black's beside Black's, so the two rows
 * read as one running scoreline whichever side the camera is on — and in this
 * game that scoreline (a queen, two rooks and a bishop against three pawns) is
 * the whole joke.
 *
 * See TRAY_X above for why it is two columns and not one row.
 */
function trayPos(actor) {
  const col = actor.tray % 2;
  const row = Math.floor(actor.tray / 2) % 5;
  // A third bank, for a game that takes more than ten pieces off one side. This
  // one takes six, so it is reachability rather than a case that fires.
  const bank = Math.floor(actor.tray / 10);
  const out = TRAY_X + col * TRAY_COL + bank * TRAY_COL * 2.2;
  return { x: actor.color === 'w' ? -out : out, z: 3.5 - row * TRAY_ROW };
}

/**
 * The pieces, as things with an identity rather than letters in a board array.
 *
 * A board array says what is on e4; an animation needs to know that the thing on
 * e4 is the same bishop that was on c4, so it can be moved rather than made to
 * disappear and reappear. Each actor carries the square it stands on after every
 * half-move (`at[k]` is the square after k of them, -1 once it has been taken),
 * which makes the state at any instant a lookup and the mover of half-move k the
 * actor whose `at` changed.
 */
function buildActors(game) {
  const start = game.positions[0];
  const actors = [];
  const owner = new Array(64).fill(-1);
  for (let sq = 0; sq < 64; sq++) {
    const piece = start.at(sq);
    if (!piece) continue;
    owner[sq] = actors.length;
    actors.push({
      id: actors.length,
      color: piece === piece.toUpperCase() ? 'w' : 'b',
      forms: [{ from: 0, type: typeOf(piece) }],
      at: [sq],
      square: sq,
      captured: -1,
      tray: -1,
    });
  }

  const trayCount = { w: 0, b: 0 };
  game.plies.forEach((ply, k) => {
    const mv = ply.move;
    if (mv.capturedSq >= 0) {
      const victim = actors[owner[mv.capturedSq]];
      owner[mv.capturedSq] = -1;
      victim.captured = k;
      victim.square = -1;
      victim.tray = trayCount[victim.color]++;
    }
    const mover = actors[owner[mv.from]];
    owner[mv.from] = -1;
    owner[mv.to] = mover.id;
    mover.square = mv.to;
    // Not played in this game, and implemented rather than asserted against:
    // a promotion is a piece that changes shape mid-timeline, which the form
    // list handles and a single mesh per actor would not.
    if (mv.promo) mover.forms.push({ from: k + 1, type: mv.promo });
    if (mv.castle) {
      const home = mover.color === 'w' ? 0 : 56;
      const [rookFrom, rookTo] = mv.castle === 'K' ? [home + 7, home + 5] : [home, home + 3];
      const rook = actors[owner[rookFrom]];
      owner[rookFrom] = -1;
      owner[rookTo] = rook.id;
      rook.square = rookTo;
      ply.rook = { id: rook.id, from: rookFrom, to: rookTo };
    }
    for (const actor of actors) actor.at.push(actor.square);
    ply.mover = mover.id;
    ply.victim = mv.capturedSq >= 0 ? actors.find((x) => x.captured === k).id : -1;
  });

  return actors;
}

/** Which shape an actor has after `ply` half-moves. */
function formAt(actor, ply) {
  let type = actor.forms[0].type;
  for (const form of actor.forms) if (form.from <= ply) type = form.type;
  return type;
}

/**
 * A turned solid from a shape-only profile, scaled to a base radius and a height.
 *
 * The profile is normalised by its own extent first, which is what lets the
 * tables above be written and edited by eye.
 */
function latheGeometry(THREE, points, radius, height, segments = 48) {
  const maxR = Math.max(...points.map(([r]) => r));
  const maxY = Math.max(...points.map(([, y]) => y));
  const spline = new THREE.SplineCurve(points.map(([r, y]) => new THREE.Vector2(
    (r / maxR) * radius,
    (y / maxY) * height,
  )));
  const sampled = spline.getPoints(points.length * 4);
  // A radius of exactly zero at the ends closes the shape; a spline that
  // undershoots leaves a negative radius, which turns the tip inside out.
  for (const v of sampled) v.x = Math.max(0, v.x);
  const geo = new THREE.LatheGeometry(sampled, segments);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Lay the piece's own grain over a part that was not turned.
 *
 * The turned body needs nothing: `LatheGeometry` wraps u once around the piece
 * and runs v up the profile, with a duplicated column at the seam, which is
 * exactly the mapping a blank of wood on a lathe has. The beads, the cross, the
 * knight's head and the rook's crown are spheres, boxes and two scanned
 * carvings, and each of those brings its OWN 0..1 across its own few
 * centimetres — so the whole grain would be squeezed onto a bead the size of a
 * pea while the body beside it shows four lines of it. They are re-projected
 * here instead, off the position, at the body's scale: u across the piece's
 * circumference, v up its height.
 *
 * A PLANE, AND WHAT THAT COSTS ON THE TWO CARVINGS. Most of what comes through
 * here is small and off the axis, where a plane is simply the cheap answer. The
 * head and the crown are neither, and on them a plane compresses the grain
 * wherever the surface turns away from it — around the cheeks, and around the
 * outside of each merlon. That reads as grain running round the shape, which is
 * what carving across a blank does to it; a cylinder centred on the piece's axis
 * would hold its spacing better and would put a seam down a carving that is not
 * symmetric about that axis to hide it in.
 */
function projectUv(THREE, geo, circ, height) {
  const pos = geo.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = 0.5 + pos.getX(i) / circ;
    uv[i * 2 + 1] = pos.getY(i) / height;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geo;
}

/**
 * How big a piece of this type is, in the set that is on the board.
 *
 * The two tables carry the same heights and the same foot radii on purpose, so
 * everything downstream of a piece's size — the camera fit, the tray, how far a
 * struck piece's axis ends up off the table when it lands — is written once and
 * holds for both sets.
 */
function formOf(p, type) {
  return (MEN_FORM[p.men] || FORM)[type];
}

/**
 * One piece, in whichever set is on the board.
 *
 * THE FIGURES GET THEIR UVs HERE AND NOWHERE ELSE
 * `figures.js` sets none: it was written when the men were one flat colour, and
 * a carved figure has no lathe in it to bring the mapping with it. What its parts
 * do carry is whatever three gives a sphere or a cone, which is each primitive's
 * OWN 0..1 across a few centimetres — a full turn of grain on a nose — and the
 * one part with nothing at all is `sweep`, the horse, which would sample the
 * grain at (0,0) and come out flat paint next to bumps that are striped. Both
 * halves of that are wrong in the same picture, which is the tell.
 *
 * So the merged figure is re-projected exactly as the Staunton set's own
 * unturned parts are, at the piece's scale — see `projectUv`. After the merge
 * rather than before it, since the parts arrive with their transforms already
 * baked into the positions this reads.
 */
function pieceGeometry(THREE, type, men) {
  if (men === 'staunton') return stauntonGeometry(THREE, type);
  if (men === 'club') return clubGeometry(THREE, type);
  const f = FIGURE_FORM[type];
  const geo = mergeGeometries(THREE, figureParts(THREE, type));
  return projectUv(THREE, geo, 2 * Math.PI * f.base, f.total);
}

/** One turned piece: the body, plus whatever it is known by on top. */
function stauntonGeometry(THREE, type) {
  const f = FORM[type];
  const body = latheGeometry(THREE, PROFILE[type], f.base, f.lathe);
  const parts = [body];
  const cap = f.total - f.lathe;

  if (type === 'r') {
    parts.push(rookCrown(THREE, f));
  } else if (type === 'b') {
    const finial = new THREE.SphereGeometry(f.base * 0.19, 20, 14);
    finial.translate(0, f.lathe + cap * 0.35, 0);
    parts.push(finial);
  } else if (type === 'q') {
    // A coronet: nine beads round the head, which is what a Staunton queen has
    // where a king has a cross.
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      const bead = new THREE.SphereGeometry(f.base * 0.15, 16, 12);
      bead.translate(Math.cos(a) * f.base * 0.60, f.lathe * 0.955, Math.sin(a) * f.base * 0.60);
      parts.push(bead);
    }
    const top = new THREE.SphereGeometry(f.base * 0.19, 20, 14);
    top.translate(0, f.lathe + cap * 0.45, 0);
    parts.push(top);
  } else if (type === 'k') {
    const stem = new THREE.BoxGeometry(f.base * 0.17, cap, f.base * 0.17);
    stem.translate(0, f.lathe + cap * 0.5, 0);
    const arm = new THREE.BoxGeometry(f.base * 0.44, f.base * 0.15, f.base * 0.16);
    arm.translate(0, f.lathe + cap * 0.62, 0);
    parts.push(stem, arm);
  } else if (type === 'n') {
    parts.push(knightHead(THREE, f));
  }

  // The body keeps the lathe's own wrap; everything bolted to it is given the
  // same grain at the same scale — see `projectUv`.
  for (let i = 1; i < parts.length; i++) {
    projectUv(THREE, parts[i], 2 * Math.PI * f.base, f.total);
  }

  return mergeGeometries(THREE, parts);
}

/**
 * One club piece: the turning, and what is carved on top of it.
 *
 * Every size here is in the photograph's pixels through `CLUB_PX`, like the
 * profiles, so a cap can be checked against the picture by reading one number.
 */
function clubGeometry(THREE, type) {
  const f = CLUB_FORM[type];
  const px = (n) => n * CLUB_PX;
  const body = new THREE.LatheGeometry(CLUB_PROFILE[type].map(([r, h]) => new THREE.Vector2(px(r), px(h))), 64);
  body.computeVertexNormals();
  const parts = [body];

  /*
   * A ring of teeth cut out of a rim: `count` curved blocks between `inner` and
   * `outer`, `height` tall, standing on `from`, filling `share` of their turn.
   *
   * `point` draws each one in toward its own middle as it rises — 0 is a square
   * battlement, 1 a spike — which is the difference between the rook's and the
   * queen's. A coronet's teeth are what is left when V notches are sawn into a
   * rim, so they come to points; a rook's battlements are what is left when
   * square slots are, so they do not.
   */
  const teeth = (count, inner, outer, from, height, share, point = 0, centreAt = 0) => {
    const span = (Math.PI * 2 / count) * share;
    for (let i = 0; i < count; i++) {
      const mid = centreAt + (i / count) * Math.PI * 2;
      const a0 = mid - span / 2;
      const shape = new THREE.Shape();
      shape.absarc(0, 0, outer, a0, a0 + span, false);
      shape.absarc(0, 0, inner, a0 + span, a0, true);
      shape.closePath();
      const tooth = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false, curveSegments: 8 });
      // Drawn flat and extruded along +z; stood up, +z becomes +y and the
      // shape's y becomes -z.
      tooth.rotateX(-Math.PI / 2);
      if (point > 0) {
        const pos = tooth.attributes.position;
        for (let v = 0; v < pos.count; v++) {
          const x = pos.getX(v);
          const y = pos.getY(v);
          const z = pos.getZ(v);
          const r = Math.hypot(x, z);
          let d = Math.atan2(-z, x) - mid;
          d = Math.atan2(Math.sin(d), Math.cos(d));
          const a = mid + d * (1 - point * (y / height));
          pos.setXYZ(v, r * Math.cos(a), y, -r * Math.sin(a));
        }
        tooth.computeVertexNormals();
      }
      tooth.translate(0, from, 0);
      parts.push(tooth);
    }
  };

  if (type === 'n') {
    /*
     * SEATED, NOT FLOATING. `knightHead` sizes the carving off `total` and hangs
     * it from 38% of the height, which is right for the simplified set's taller
     * turning and left a gap of thirteen of the photograph's pixels over this
     * one's short base: a horse's head hovering over a stump. Placed here
     * instead, off the model's own extent, so the carving starts eight pixels
     * INSIDE the top of the bell and ends at the top of the piece — which is the
     * size the tracing shows it.
     */
    const bottom = px(96);
    const top = f.total;
    const lowest = 0.025215; // KNIGHT_MODEL stands on this, per its own header.
    const height = (top - bottom) / (1 - lowest);
    // The tracing's head is a ninth longer nose to mane than the scan, and sits
    // further back over the base than the scan's own axis puts it.
    const head = placeModel(THREE, KNIGHT_MODEL, height, top - height, THICKNESS);
    head.scale(1, 1, 1.11);
    head.translate(0, 0, px(8));
    parts.push(head);
  } else if (type === 'r') {
    // SIX BATTLEMENTS WITH NARROW SLOTS, cut forty units into the top of the
    // tower. Turned so the traced front view comes out: one slot left of the
    // middle, one well left, and a third just inside the right-hand edge.
    teeth(6, px(44), px(56.5), f.lathe, f.total - f.lathe, 0.85, 0, -1.74);
  } else if (type === 'q') {
    // The coronet: eight points shoulder to shoulder on the lowered rim, rising to
    // the traced one — a scalloped edge rather than a ring of separate spikes.
    teeth(8, px(44), px(54), px(357), px(10), 1, 0.6);
  } else if (type === 'k') {
    // The traced cross, both halves, cut out flat and as thick as its stem.
    const outline = [...CLUB_CROSS, ...CLUB_CROSS.slice().reverse().map(([x, h]) => [-x, h])];
    const shape = new THREE.Shape(outline.map(([x, h]) => new THREE.Vector2(px(x), px(h))));
    const cross = new THREE.ExtrudeGeometry(shape, { depth: px(20), bevelEnabled: false });
    cross.translate(0, 0, -px(10));
    parts.push(cross);
  }

  // The body keeps the lathe's own wrap; everything added to it takes the same
  // grain at the same scale — see `projectUv`, and `stauntonGeometry`.
  for (let i = 1; i < parts.length; i++) {
    projectUv(THREE, parts[i], 2 * Math.PI * f.base, f.total);
  }

  return mergeGeometries(THREE, parts);
}

/**
 * A diagram man: no token at all, just the drawing on a square the size of a
 * square — `scale(1/45)` in `flat-board.js`, which lays the set's 45-unit
 * drawing edge to edge on a square. The body is empty geometry so everything
 * that handles a man by his mesh — the ghosts, the trays, the fall — still has
 * one to handle, and the drawing rides on it as the traced men's ink does.
 *
 * One texture per side, because the two sides are two drawings here rather than
 * one drawing in two colours, and `toneMapped: false` so the white men are the
 * page's white and the black ones its black, as they are on the site.
 */
function diagramGeometry(ctx, type) {
  const { THREE } = ctx;
  const body = new THREE.BufferGeometry();
  body.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
  const ink = new THREE.PlaneGeometry(1, 1);
  ink.rotateX(-Math.PI / 2);
  ink.translate(0, 0.004, 0);
  const mat = (side) => {
    const texture = new THREE.CanvasTexture(DIAGRAM_MEN[`${side}${type}`]);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    return new THREE.MeshBasicMaterial({
      map: texture, transparent: true, depthWrite: false, toneMapped: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4,
    });
  };
  // `fixed`: drawn in its own colours, so the per-frame ink colouring leaves it be.
  ctx.flatInk[type] = { geometry: ink, mats: { w: mat('w'), b: mat('b') }, fixed: true };
  return body;
}

/**
 * One flat man for the 2D board: the drawing's silhouette cut out as a thin
 * token, and the drawing itself laid over its face.
 *
 * TWO PARTS, BECAUSE THE DRAWING IS LINES. The traced path is a line drawing —
 * filled with the even-odd rule it is the ink and nothing else — so extruding it
 * whole would stand up a wire sculpture of the outline. The piece is the FIRST
 * contour alone, which is the silhouette; the ink goes on top as a texture on a
 * plane just above the token's face.
 *
 * A TEXTURE AND NOT GEOMETRY for the ink, because of how small it is drawn. A
 * king is under a square tall, a square is a few dozen pixels on a phone, and a
 * line in the drawing is a hundredth of the king — a fraction of a pixel. Lines
 * that thin as triangles flicker in and out as the rasteriser hits or misses
 * them; in a mipmapped texture they fade into the face evenly, which is what a
 * printed diagram does when it is shrunk. They are also drawn half again as
 * heavy as the tracing's, for the same reason: at diagram size the tracing's own
 * weight is a hairline.
 *
 * THE TALL THREE ARE SHOWN DOWN TO THE COLLAR. A whole king at a size that
 * fills a square leaves a crown too small to read, and the crown, the coronet and
 * the mitre are what tell the three apart — the stem and the foot are the same on
 * all of them. So each is cut across at `FLAT_CUT`, under the lowest ring of its
 * collar, and the ring's own rounded underside becomes the base of the token. The
 * cut edge gets the drawing's outline, so it reads as the edge of the piece
 * rather than a slice through it. Then every piece is sized to `FLAT_HEIGHT`.
 *
 * Returns `{ body, ink, inkTexture }` in world units, lying flat, centred on the
 * origin and standing on y = 0, with the piece's head towards -z — up the screen
 * from White's end.
 */
function flatGeometry(THREE, type) {

  // M, C, L and Z, absolute: all the tracing uses.
  const tokens = FLAT_PATHS[type].match(/[MCLZ]|-?\d*\.?\d+(?:e[-+]?\d+)?/g);
  const contours = [];
  let current = null;
  let cmd = '';
  for (let i = 0; i < tokens.length;) {
    if (/[MCLZ]/.test(tokens[i])) cmd = tokens[i++];
    const n = () => Number(tokens[i++]);
    if (cmd === 'M') {
      current = [{ op: 'M', pts: [n(), n()] }];
      contours.push(current);
      cmd = 'L';
    } else if (cmd === 'L') current.push({ op: 'L', pts: [n(), n()] });
    else if (cmd === 'C') current.push({ op: 'C', pts: [n(), n(), n(), n(), n(), n()] });
    else if (cmd === 'Z') cmd = '';
  }

  // The silhouette is the contour with the most area in it.
  const pathOf = (contour, map) => {
    const path = new THREE.Shape();
    for (const { op, pts } of contour) {
      const q = [];
      for (let k = 0; k < pts.length; k += 2) q.push(...map(pts[k], pts[k + 1]));
      if (op === 'M') path.moveTo(q[0], q[1]);
      else if (op === 'L') path.lineTo(q[0], q[1]);
      else path.bezierCurveTo(...q);
    }
    path.closePath();
    return path;
  };
  let outer = null;
  let most = -1;
  for (const contour of contours) {
    const area = Math.abs(THREE.ShapeUtils.area(pathOf(contour, (x, y) => [x, y]).getPoints(2)));
    if (area > most) { most = area; outer = contour; }
  }
  const whole = pathOf(outer, (x, y) => [x, y]).getPoints(6);
  const box = new THREE.Box2().setFromPoints(whole);

  // The cut, for a piece taller than the rest are shown: everything of the
  // silhouette below `cutY`, clipped off the sampled outline one edge at a time
  // (Sutherland–Hodgman, which a non-convex outline survives as long as a line
  // across it crosses it twice — true of a turned piece at any height).
  const cutY = FLAT_CUT[type] ?? null;
  let clipped = null;
  const across = [];
  if (cutY !== null) {
    clipped = [];
    for (let k = 0; k < whole.length; k++) {
      const a = whole[k];
      const b = whole[(k + 1) % whole.length];
      const aIn = a.y <= cutY;
      const bIn = b.y <= cutY;
      if (aIn) clipped.push(a);
      if (aIn !== bIn) {
        const x = a.x + (b.x - a.x) * (cutY - a.y) / (b.y - a.y);
        clipped.push(new THREE.Vector2(x, cutY));
        across.push(x);
      }
    }
    box.setFromPoints(clipped);
  }
  const cx = (box.min.x + box.max.x) / 2;
  const cy = (box.min.y + box.max.y) / 2;
  const scale = FLAT_HEIGHT[type] / (box.max.y - box.min.y);

  // SVG's y is down and the shape's is up, so the head comes out at +y — which
  // the turn below lays towards -z. Uncut pieces keep their curves for the
  // extrusion to sample; a cut one is already the points the clip left.
  const toWorld = (x, y) => [(x - cx) * scale, (cy - y) * scale];
  const silhouette = clipped
    ? new THREE.Shape(clipped.map((v) => new THREE.Vector2(...toWorld(v.x, v.y))))
    : pathOf(outer, toWorld);
  const body = new THREE.ExtrudeGeometry(silhouette, {
    depth: FLAT_DEPTH,
    bevelEnabled: true,
    bevelThickness: FLAT_BEVEL,
    bevelSize: FLAT_BEVEL * 0.5,
    // Inset by what the bevel adds, so the token's edge is the drawing's edge and
    // the ink's outer line lands on it rather than a sliver inside it.
    bevelOffset: -FLAT_BEVEL * 0.5,
    bevelSegments: 2,
    curveSegments: 3,
  });
  body.rotateX(-Math.PI / 2);
  body.translate(0, FLAT_BEVEL, 0);
  body.computeVertexNormals();

  // The ink: the whole path, filled even-odd and stroked a little, white on clear
  // so the material's colour is the ink's colour.
  const pad = 8;
  const px = FLAT_INK_DENSITY * scale;
  const w = box.max.x - box.min.x + pad * 2;
  const h = box.max.y - box.min.y + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w * px);
  canvas.height = Math.ceil(h * px);
  const g = canvas.getContext('2d');
  g.setTransform(px, 0, 0, px, (pad - box.min.x) * px, (pad - box.min.y) * px);
  const drawing = new Path2D(FLAT_PATHS[type]);
  g.fillStyle = '#fff';
  g.strokeStyle = '#fff';
  g.lineJoin = 'round';
  g.lineWidth = FLAT_INK_WEIGHT;
  if (cutY !== null) {
    g.save();
    g.beginPath();
    g.rect(box.min.x - pad, box.min.y - pad, w, cutY - box.min.y + pad);
    g.clip();
  }
  g.fill(drawing, 'evenodd');
  g.stroke(drawing);
  if (cutY !== null) {
    g.restore();
    // The outline along the cut, as heavy as the drawn one round the rest.
    const band = FLAT_OUTLINE_PX + FLAT_INK_WEIGHT / 2;
    const x0 = Math.min(...across);
    const x1 = Math.max(...across);
    g.fillRect(x0, cutY - band, x1 - x0, band);
  }
  const inkTexture = new THREE.CanvasTexture(canvas);
  inkTexture.colorSpace = THREE.SRGBColorSpace;
  inkTexture.anisotropy = 4;

  const ink = new THREE.PlaneGeometry(w * scale, h * scale);
  ink.rotateX(-Math.PI / 2);
  ink.translate(
    (box.min.x - pad + w / 2 - cx) * scale,
    FLAT_DEPTH + FLAT_BEVEL * 2 + 0.002,
    -(cy - (box.min.y - pad + h / 2)) * scale,
  );
  return { body, ink, inkTexture };
}

/**
 * The knight's head: a scanned carving, decimated to 1,658 triangles.
 *
 * WHY THIS ONE PART IS NOT MADE HERE
 * Every other man in the set is a solid of revolution and a lathe describes one
 * exactly. A knight's head is a carving, and the twenty numbers it takes to fake
 * one are the twenty a viewer looks hardest at — it is the piece everyone
 * identifies by silhouette, so it is the piece that gives a set away. This was an
 * extruded outline, which reads correctly in profile and reads as a cardboard
 * cut-out from anywhere else, and the camera here spends its whole time
 * anywhere else.
 *
 * THE BASE IS STILL OURS
 * The model it came from was a whole piece, base and all, and that base was left
 * behind on purpose: `PROFILE.n` turns this knight's foot from the same family of
 * curves as the other five men, and a foot borrowed from another set is exactly
 * the kind of mismatch nobody can name and everybody notices. The carving is cut
 * off at the neck instead — see `--above` in `scripts/obj-to-module.mjs` — and
 * the open ring that leaves sits down inside the turned collar where no camera
 * angle reaches it.
 *
 * Its height comes from `FORM`, and only X is scaled against it — see
 * `THICKNESS`. Never Z: nose to poll is the aspect ratio a knight is judged on,
 * and stretching it is the classic tell of a bad set.
 *
 * Arrives Y-up, standing on its own cut, one unit tall, and facing down Z rather
 * than the +x the extruded outline used — so `actor.faceY` still turns it by the
 * same angle, but a knight now looks along the board instead of across it.
 */
function knightHead(THREE, f) {
  // The upper 62% of the piece — a little more than the 55% the outline had,
  // because the outline was a wide slab and this is round, and at the same
  // fraction it read as a small head on a big body.
  const height = f.total * 0.62;
  return placeModel(THREE, KNIGHT_MODEL, height, f.total - height, THICKNESS);
}

/**
 * The rook's crown: a scanned crenellation, decimated to 1,855 triangles.
 *
 * WHAT IT REPLACES
 * Eight boxes standing on the flat top of the lathe. They read as eight boxes:
 * square in plan, square in section, with nothing joining them and a visible
 * disc of turned wood between them and the rim. A real rook is a cup with slots
 * cut through its wall — the merlons are what is LEFT of the wall, not things
 * added to a lid — and the difference shows at every angle that can see into it.
 *
 * WHY THE TURNED BODY HAD TO GET SHORTER
 * The old crown was `cap` tall, 0.14 of a 1.06 piece. The real one is a quarter
 * of the piece, and squashing it into 0.14 would have meant scaling one axis
 * against the other two on a shape whose slots are square. So the crown keeps its
 * proportions and `FORM.r.lathe` came down from 0.92 to 0.76 to make room, with
 * `PROFILE.r` truncated to match — see both.
 *
 * The model's own flare comes with it, which is why the truncation goes as far
 * down as it does: the flare and the crenellation are one carving, and keeping
 * the lathe's flare as well would have put one inside the other.
 *
 * A uniform scale here, and no `THICKNESS`: a rook is a solid of revolution,
 * there is no thin axis to fatten and no orientation to get wrong.
 */
function rookCrown(THREE, f) {
  const height = f.total * 0.445;
  return placeModel(THREE, ROOK_MODEL, height, f.total - height);
}

/**
 * A baked model, scaled to a height, stood at a lift, and optionally fattened
 * across X — which only the knight wants, to close the seam at its neck.
 *
 * The copy is not waste. `pieceGeometry` runs again on every rebuild, and scaling
 * the module's own arrays in place would compound — the piece would grow by a
 * factor of `height` per rebuild until it filled the room.
 *
 * No UVs of its own. Every caller projects them afterwards at the scale of
 * whatever the model is standing on — see `projectUv`.
 */
function placeModel(THREE, source, height, lift, across = 1) {
  const positions = new Float32Array(source.positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    positions[i] = source.positions[i] * height * across;
    positions[i + 1] = source.positions[i + 1] * height + lift;
    positions[i + 2] = source.positions[i + 2] * height;
  }

  // Normals do not survive a non-uniform scale. Widening X by k tilts every
  // surface, and the normal that goes with it is the INVERSE scale — x over k —
  // renormalised. Carrying the originals across unchanged would light the knight
  // as though it were still the narrow one, which on the fattened cheeks reads as
  // a dent running down each side of the face. At `across` of 1 this is the
  // identity and the originals pass through untouched.
  //
  // They are the ORIGINAL surfaces' normals either way, carried down from the
  // full-resolution scans rather than recomputed from the triangles left behind.
  // That is most of why the reduced pieces still read as carved rather than as
  // faceted, so the correction is applied to them rather than reaching for
  // `computeVertexNormals`.
  const normals = new Float32Array(source.normals.length);
  for (let i = 0; i < normals.length; i += 3) {
    const x = source.normals[i] / across;
    const y = source.normals[i + 1];
    const z = source.normals[i + 2];
    const len = Math.hypot(x, y, z) || 1;
    normals[i] = x / len;
    normals[i + 1] = y / len;
    normals[i + 2] = z / len;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geo.setIndex(new THREE.BufferAttribute(new Uint16Array(source.indices), 1));
  return geo;
}

/**
 * Merge a list of geometries into one.
 *
 * Hand-rolled rather than pulled from three's addons: the import map exposes one
 * module, `three` itself, and one piece being one mesh is worth more than the
 * seven lines this takes — 32 meshes casting shadows instead of 130.
 *
 * UVs come across with the positions, which they did not before the men had a
 * grain in them. A merged geometry with no `uv` attribute does not fail: every
 * fragment samples the texture at (0,0) instead, so the whole set comes out the
 * colour of one texel and reads as flat paint with a slight colour cast. That is
 * a hard failure to see and an easy one to write, so the copy is here rather
 * than in the caller.
 */
function mergeGeometries(THREE, geometries) {
  if (geometries.length === 1) return geometries[0];
  let count = 0;
  for (const g of geometries) count += g.attributes.position.count;
  const position = new Float32Array(count * 3);
  const normal = new Float32Array(count * 3);
  const uv = new Float32Array(count * 2);
  const index = [];
  let base = 0;
  for (const g of geometries) {
    const pos = g.attributes.position;
    const nrm = g.attributes.normal;
    position.set(pos.array.subarray(0, pos.count * 3), base * 3);
    normal.set(nrm.array.subarray(0, nrm.count * 3), base * 3);
    if (g.attributes.uv) uv.set(g.attributes.uv.array.subarray(0, pos.count * 2), base * 2);
    const idx = g.index ? [...g.index.array] : [...Array(pos.count).keys()];
    for (const i of idx) index.push(i + base);
    base += pos.count;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(position, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  out.setIndex(index);
  return out;
}


// ------------------------------------------------------------------- surfaces

/**
 * WHAT THE MEN ARE MADE OF, AND WHY IT IS PAINTED RATHER THAN LOADED
 *
 * Three finishes, and two of them are a pattern in the material rather than a
 * number on it: wood has a grain running up the blank and marble has veins run
 * through it, and neither survives being reduced to "a brown colour, fairly
 * rough". Both are painted here, into a canvas, at load — no image file to
 * fetch, nothing to go missing behind a slow connection, and the same pixels on
 * every machine because the noise is seeded (`makeRng`), which the frame-exact
 * comparison in `scripts/check-player.mjs` depends on.
 *
 * They are painted to MULTIPLY rather than to replace. The set's two colours are
 * still what says boxwood-and-ebony or ivory-and-slate; the texture only says
 * how the light is not even across a piece. That is what keeps the ten board
 * sets and the two custom colour pickers working exactly as they did — a texture
 * with its own colours in it would have overruled all twelve.
 *
 * Each finish gets two images out of one pass: the tone, and a relief for
 * `bumpMap`. The relief is what actually sells them at this size. A chess piece
 * is sixty pixels tall in the frame and the grain is finer than that, so what
 * the eye reads is not the lines themselves but the way the key light catches
 * them along the turned edges.
 *
 * Both tile in both directions, because the turned body wraps u right round the
 * piece and a seam would be a bright line down every man in the set.
 */

/** Value noise on a wrapping `nx` by `ny` lattice — periodic by construction. */
function lattice(nx, ny, rng) {
  const g = new Float32Array(nx * ny);
  for (let i = 0; i < g.length; i++) g[i] = rng();
  return (u, v) => {
    const x = u * nx, y = v * ny;
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const fx = smoothstep(x - x0), fy = smoothstep(y - y0);
    const i0 = ((x0 % nx) + nx) % nx, i1 = (i0 + 1) % nx;
    const j0 = ((y0 % ny) + ny) % ny, j1 = (j0 + 1) % ny;
    const top = g[j0 * nx + i0] + (g[j0 * nx + i1] - g[j0 * nx + i0]) * fx;
    const bot = g[j1 * nx + i0] + (g[j1 * nx + i1] - g[j1 * nx + i0]) * fx;
    return top + (bot - top) * fy;
  };
}

/**
 * A stack of those, each a little more than half the weight of the one before.
 *
 * The two resolutions are given per octave rather than as one number because
 * grain is not isotropic: wood wants noise that is fine ACROSS the piece and
 * long ALONG it, which is `[44, 5]` and not `[44, 44]`.
 */
function fbm(rng, octaves) {
  const layers = octaves.map(([nx, ny]) => lattice(nx, ny, rng));
  return (u, v) => {
    let sum = 0, amp = 1, norm = 0;
    for (const layer of layers) { sum += amp * layer(u, v); norm += amp; amp *= 0.55; }
    return sum / norm;
  };
}

/** Linear light to the sRGB byte that stores it. */
function srgbByte(x) {
  const c = clamp01(x);
  const e = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(e * 255);
}

/**
 * Run `shade` over a square and hand back the two textures it paints.
 *
 * `shade(u, v)` returns `[tone, warmth, relief]`: how much light comes back at
 * that point, how much warmer the red end of it is than the blue, and how high
 * the surface stands there. Tone is LINEAR and encoded on the way out — written
 * straight into the byte it would come back a fifth darker than it says, since
 * the colour texture is read as sRGB and the multiply against the set's colour
 * happens after that.
 */
function materialTexture(THREE, shade, size = 512) {
  const tone = document.createElement('canvas');
  const relief = document.createElement('canvas');
  tone.width = tone.height = relief.width = relief.height = size;
  const tg = tone.getContext('2d'), rg = relief.getContext('2d');
  const ti = tg.createImageData(size, size), ri = rg.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const [lit, warmth, height] = shade((x + 0.5) / size, v);
      const k = (y * size + x) * 4;
      ti.data[k] = srgbByte(lit);
      ti.data[k + 1] = srgbByte(lit * (1 - warmth * 0.45));
      ti.data[k + 2] = srgbByte(lit * (1 - warmth));
      ti.data[k + 3] = 255;
      // The relief is data, not a picture: no encoding, and three reads its red.
      ri.data[k] = ri.data[k + 1] = ri.data[k + 2] = Math.round(clamp01(height) * 255);
      ri.data[k + 3] = 255;
    }
  }
  tg.putImageData(ti, 0, 0);
  rg.putImageData(ri, 0, 0);
  const map = new THREE.CanvasTexture(tone);
  map.colorSpace = THREE.SRGBColorSpace;
  const bump = new THREE.CanvasTexture(relief);
  for (const t of [map, bump]) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
  }
  return { map, bump };
}

/**
 * Lacquered wood.
 *
 * A chess piece is turned from a blank whose grain runs up its axis, so the
 * growth rings meet the turned surface as lines running UP the piece — which is
 * u held and v travelling, and therefore stripes down the texture.
 *
 * HOW MANY LINES, AND HOW DARK
 * Both were badly wrong on the first pass, and the failure is worth recording
 * because it is not the one you would predict. Nine rings round the whole
 * circumference is four or five across the face of a pawn, and a piece with five
 * dark stripes down it is not a piece with grain: it is a FLUTED piece, a
 * column, and the eye reads it as carved geometry rather than as figure in the
 * wood. Twenty-two puts ten or eleven on the visible face — three or four pixels
 * apiece at the size a man occupies in this frame, which is fine enough to
 * average into a surface instead of standing up as a shape.
 *
 * The same goes for how dark they are. Boxwood and ebony are close-grained
 * woods; the line is a shade, not a stripe, and the tone across a ring runs
 * about six per cent here against the thirty it started at. What is left as the
 * loudest thing in the picture is the FIGURE, at nine — the slow blotching of
 * the blank, which is what you actually see of a wooden piece from across a
 * room.
 *
 * Straight stripes read as paint, so the ring count is bent by two scales of
 * noise: a slow one for the figure of the blank, and a finer one that gives the
 * lines the waver a real ring has where the profile cuts through it at an angle.
 * `late` is the hard dark band at the close of each year's growth — thin, and
 * the single feature that says wood rather than marbled paper. The pores are
 * drawn long and thin, which is the shape a pore is when it is sliced lengthways.
 */
function woodShade(rng) {
  const bend = fbm(rng, [[4, 2], [8, 4], [16, 8]]);
  const pore = fbm(rng, [[64, 6], [128, 12]]);
  const figure = fbm(rng, [[2, 2], [4, 4]]);
  return (u, v) => {
    // The warp is worth more than the count. Twenty-two evenly spaced rings is
    // corduroy — the eye finds the period immediately and stops seeing wood — and
    // pushing them around by three whole ring-widths is what breaks it: the lines
    // crowd in one place, open out in another, and run at a slight angle where
    // the profile cuts across them.
    const ring = u * 22 + (bend(u, v) - 0.5) * 7.0 + (figure(u, v) - 0.5) * 2.4;
    const band = 0.5 - 0.5 * Math.cos(2 * Math.PI * ring);
    const late = Math.pow(band, 7);
    const pores = 1 - pore(u, v);
    let lit = 1 - 0.022 * band - 0.038 * late;
    lit *= 1 - 0.018 * pores;
    // The figure of the blank: slow, broad, and the part of the picture that is
    // actually visible from across a room. Nine per cent against the rings' six,
    // which is the right way round at this size: a man is sixty pixels tall in
    // the frame and a ring is three or four across.
    lit *= 0.955 + 0.09 * figure(u, v);
    // The dark line is also the low one: latewood sits below the soft spring
    // growth once a piece has been sanded and polished across both. Shallow —
    // this is a lacquered surface, and what the relief is for is the way the key
    // light travels along a turned edge, not a corrugation you could feel.
    const height = 1 - 0.55 * late - 0.18 * band - 0.30 * pores;
    return [lit, 0.05 + 0.13 * late, height];
  };
}

/**
 * Marble, in the two stones a marble chess set is actually cut from.
 *
 * Veins rather than grain, so the field is a wave through the block —
 * sin(direction + turbulence) — with the turbulence doing all the work: the wave
 * alone is stripes, and the wave pushed sideways by four octaves of noise is the
 * wandering, branching thing a vein does. Raised to a high power it comes out
 * narrow, which is what a vein looks like against the body of the stone. A
 * second, much narrower set crosses it at a different angle, because a single
 * family of veins all leaning the same way is the tell of a procedural marble.
 *
 * The direction coefficients are whole numbers on purpose. The texture has to
 * tile, and `u * 2 + v * 3` closes on itself where `u * 2 + v * 1.5` does not.
 *
 * WHY THE TWO HALVES ARE NOT THE SAME PICTURE
 * They were, and the dark half had no veining in it at all. The tone is a
 * MULTIPLY over the set's colour, so it can only take light away — and a vein
 * that shows as a lighter streak, which is precisely what the veining in black
 * marble is, cannot be drawn by taking light away from something that is already
 * nearly black. Carrara is a pale stone with grey veins and Nero Marquina is a
 * dark stone with white ones; they are two different pictures in life, and this
 * is the same fact arriving from the other side.
 *
 * So `dark` paints its body at a seventh and its veins at full, and the
 * material's colour is opened up by `DARK_STONE_GAIN` to put the body back where
 * the set said it should be. The veins then have six sevenths of somewhere
 * brighter to go, which on a near-black piece is the difference between a stone
 * and a bead.
 */
function marbleShade(rng, dark = false) {
  const turb = fbm(rng, [[3, 3], [6, 6], [12, 12], [24, 24]]);
  const fine = fbm(rng, [[8, 8], [16, 16], [32, 32]]);
  const grit = fbm(rng, [[110, 110]]);
  return (u, v) => {
    const wave = u * 2 + v * 3 + (turb(u, v) - 0.5) * 3.6;
    const s = 0.5 - 0.5 * Math.cos(2 * Math.PI * wave);
    const vein = Math.pow(s, 12);
    const cross = u * 3 - v * 2 + (fine(u, v) - 0.5) * 3.0;
    const hair = Math.pow(0.5 - 0.5 * Math.cos(2 * Math.PI * cross), 22);
    const cloud = Math.pow(1 - s, 3);
    // Nero Marquina is a dark body with WHITE veins cut through it, so the dark
    // picture is painted from its body up to full white rather than down from it:
    // `body` is where the stone sits once the colour has been multiplied back up,
    // and the veins climb the whole way from there to 1. Carrara is the ordinary
    // way round — a pale body that the veins are the grey in.
    const body = 1 / DARK_STONE_GAIN;
    const white = clamp01(vein * 1.05 + hair * 0.6);
    let lit = dark
      ? body * (1 - 0.14 * cloud) + (1 - body) * white
      : 0.99 - 0.38 * vein - 0.20 * hair - 0.08 * cloud;
    lit *= 0.985 + 0.03 * grit(u, v);
    // Polished flat, so the relief is slight — enough for the key light to find
    // a vein along a turned edge and no more. A vein stands very slightly proud
    // of the body on both stones: it is the harder mineral, and it is what is
    // left when the softer stone around it has been ground away.
    const height = clamp01(0.5 + 0.40 * vein + 0.25 * hair - 0.18 * cloud);
    // The veins are warmed as they brighten. A dark stone is lit by multiplying
    // the set colour up, which keeps its hue — and the blue-black every board set
    // uses comes out of that as navy where a real white vein is faintly warm. This
    // is the counterweight, and it is only felt where there is a vein to feel it.
    return [lit, dark ? 0.01 + 0.13 * white : 0.02, height];
  };
}

/**
 * Which of the two stones a colour can be cut from, and its colour set to match.
 *
 * The dark picture only works on a colour with seven times its own brightness in
 * hand above it. Every board set's `black` is a near-black and has forty times
 * that, so this is the answer for all ten of them — but nothing stops somebody
 * typing a pale colour into `Black pieces`, and multiplying THAT by the gain
 * would clip it to a flat white with the veining burned out of it.
 *
 * The way out is not to cap the gain, which would leave the picture's body
 * darker than the colour asked for and hand back a grey piece. It is to cut the
 * piece from the other stone: a light colour gets Carrara, veined the ordinary
 * way round, which is what a light colour wanted in the first place. Returns
 * true when the dark stone was taken.
 */
function darkStone(colour, hex) {
  colour.set(hex);
  const peak = Math.max(colour.r, colour.g, colour.b);
  if (peak * DARK_STONE_GAIN > 0.98) return false;
  colour.multiplyScalar(DARK_STONE_GAIN);
  return true;
}

// ---------------------------------------------------------------------- build

function buildBoard(ctx) {
  const { THREE, scene } = ctx;
  const p = ctx.params;

  // The slab, with the frame colour on its sides. The top is a separate plane
  // so the painted texture has UVs that are trivially predictable — see
  // `boardPixel`.
  // Colour comes from the resolved set every frame — see update().
  ctx.slabMat = new THREE.MeshStandardMaterial({ color: 0x2b2018, roughness: 0.55, metalness: 0.05 });
  const slab = new THREE.Mesh(new THREE.BoxGeometry(BOARD, 0.42, BOARD), ctx.slabMat);
  // Its top face sits just UNDER the painted plane. Level with it — which is
  // where the arithmetic naturally puts it — the two are coplanar and z-fight,
  // which does not look like z-fighting on a chessboard: it looks like fine
  // horizontal hatching over half the squares, and reads as a texture problem
  // or shadow acne rather than as two surfaces at the same depth.
  slab.position.y = TOP - 0.012 - 0.21;
  slab.receiveShadow = true;
  scene.add(slab);

  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = TEX;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  // Unlit under the diagram men: the site's 2D board is the set's colours exactly,
  // and the key light and the exposure would lift the dark squares a shade
  // greener than the board the link opens on. `roughness` is still written to it
  // every frame and means nothing to it.
  ctx.boardMat = p.dimension === '2d' && p.flatSet === 'diagram' && DIAGRAM_MEN
    ? new THREE.MeshBasicMaterial({ map: tex, toneMapped: false })
    : new THREE.MeshStandardMaterial({ map: tex, roughness: 0.42, metalness: 0.02 });
  const top = new THREE.Mesh(new THREE.PlaneGeometry(BOARD, BOARD), ctx.boardMat);
  top.rotation.x = -Math.PI / 2;
  top.position.y = TOP;
  top.receiveShadow = true;
  scene.add(top);
  ctx.board = { canvas, g2: canvas.getContext('2d'), tex, last: null };

  // The table. Only ever seen out of the corners of the frame, and its whole
  // job is to stop the board floating in a void at low camera angles.
  const table = new THREE.Mesh(
    new THREE.PlaneGeometry(70, 70),
    new THREE.MeshStandardMaterial({ color: 0x0d1015, roughness: 0.85, metalness: 0.0 }),
  );
  table.rotation.x = -Math.PI / 2;
  table.position.y = -0.42;
  table.receiveShadow = true;
  scene.add(table);
  // Kept on ctx because the paper goes on it every frame — the table fills this
  // scene's frame at every angle it uses, so the sheet has to BE the table.
  ctx.table = table;
}

/**
 * THE TRANSPARENT BACKGROUND: the room taken away from behind the board.
 *
 * No backdrop, so the canvas is clear wherever nothing is drawn, and no vignette,
 * which would darken the corners of a frame that has nothing in them. The table
 * stays, as a SHADOW CATCHER: invisible except where the board and the men throw
 * shadow on it, and there it is see-through dark. That keeps the board sitting
 * on whatever it is laid over, where cutting the table out altogether would
 * leave it hanging in the air.
 *
 * The lighting is untouched. `scene.environment` is what the men reflect and it
 * is not the background, so a transparent render and an opaque one light the set
 * the same.
 */
function seeThrough(ctx) {
  ctx.scene.background = null;
  if (!ctx.table.material.isShadowMaterial) {
    ctx.table.material.dispose();
    ctx.table.material = new ctx.THREE.ShadowMaterial({ opacity: 0.4 });
  }
}

// ------------------------------------------------------------- the nameplates

/**
 * A plaque on the table at each player's edge of the board.
 *
 * WHY THIS IS A THING IN THE ROOM AND NOT A CAPTION. The two names were on the
 * opening card, on the closing card and in the page's header — three places, all
 * of them writing laid over the picture, and none of them able to say the one
 * thing somebody watching a game actually needs: WHICH SET OF MEN is whose. A
 * board cannot say that for itself. A plaque lying at White's end says it without
 * a word of explanation, for as long as the game lasts, and goes on saying it
 * when the camera walks round to the other side — which is exactly where a
 * caption in the corner of the frame stops being any help at all.
 *
 * ONE GROUP PER PLATE, TURNED TO FACE THE VIEWER. The plaque is a box with the
 * name on its top face, and a box's top-face UVs run in the direction the
 * geometry says — top of the image toward -z: the right way up from White's end.
 * Both plates are turned together, every frame, to whichever end the picture is
 * seen from (`drawNames`), so the far plate reads the right way up rather than
 * upside down at the top of the frame. The box is symmetrical, so the rotation
 * is invisible on everything except the one thing it is for.
 *
 * The five faces nobody reads share one material and take the frame colour, so a
 * plate is the same wood the board's own slab is: the pair of them are furniture
 * from the same set rather than two cards left on the table. Both halves are
 * written every frame from `ctx.set`, which is what makes them follow a set
 * change with everything else — see `drawNames`.
 */
function buildNames(ctx) {
  const { THREE, scene } = ctx;
  // The table's own height rather than a second copy of it, and lifted five
  // thousandths clear so the plate's underside and the table are not coplanar.
  const y = ctx.table.position.y + PLATE.h / 2 + 0.005;
  ctx.names = {};

  for (const side of ['w', 'b']) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(PLATE.w * PLATE_PX);
    canvas.height = Math.round(PLATE.d * PLATE_PX);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;

    const body = new THREE.MeshStandardMaterial({ color: 0x2b2018, roughness: 0.62, metalness: 0.04 });
    // A shade smoother than the body: the name is printed on the wood rather than
    // cut into it, and a printed panel catches the key light where bare wood does
    // not. BoxGeometry takes its materials +x, -x, +y, -y, +z, -z, so the top
    // face is index 2 and everything else is the body.
    const face = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.48, metalness: 0.04 });
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(PLATE.w, PLATE.h, PLATE.d),
      [body, body, face, body, body, body],
    );
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const group = new THREE.Group();
    group.add(mesh);
    group.position.set(0, y, side === 'w' ? PLATE_Z : -PLATE_Z);
    scene.add(group);

    ctx.names[side] = {
      group, body, tex, canvas,
      g2: canvas.getContext('2d'),
      who: side === 'w' ? ctx.g.white : ctx.g.black,
      last: null,
    };
  }
}

/**
 * The plates, per frame: whether they are there, and what wood they are.
 *
 * Painted against a key, the way the board top is. Nothing on a plate depends on
 * the move, so in a whole game this repaints twice — once at the first frame,
 * and again if somebody changes the set — and every other frame it is two string
 * comparisons and a colour write.
 */
function drawNames(ctx) {
  const on = ctx.params.names;
  const set = ctx.set;

  for (const side of ['w', 'b']) {
    const plate = ctx.names[side];
    plate.group.visible = on;
    // Both plates read from the end the viewer is at, so the far one is the right
    // way up rather than upside down at the top of the picture. In 2D that is the
    // end the board is drawn from; in 3D it is whichever end the camera is on
    // this frame (`placeCamera` has already run), so a shot walking round to
    // Black's side turns them with it.
    plate.group.rotation.y = viewerAtBlack(ctx) ? Math.PI : 0;
    // Nothing to repaint while they are off, and the key is left alone rather
    // than cleared: turning them back on shows the frame it was already holding.
    if (!on) continue;
    plate.body.color.set(set.frame);
    const key = [plate.who, set.frame, set.light].join('|');
    if (plate.last === key) continue;
    plate.last = key;
    paintName(plate, set);
    plate.tex.needsUpdate = true;
  }
}

/** Whether the picture is seen from Black's end of the board — White stands at +z. */
function viewerAtBlack(ctx) {
  if (ctx.params.dimension === '2d') return flatFlip(ctx);
  return ctx.camera.position.z < 0;
}

/**
 * One plate's top face.
 *
 * Everything here is in SQUARES times `PLATE_PX`, so the layout is readable
 * against the geometry above rather than being a list of pixel counts that only
 * happen to fit it.
 *
 * The top of this canvas is the plate's edge FARTHER from the viewer, which is
 * what puts the name above the line under it on both plates — see the rotation
 * in `drawNames`.
 */
function paintName(plate, set) {
  const g = plate.g2;
  const U = PLATE_PX;
  const W = plate.canvas.width;
  const H = plate.canvas.height;
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';

  g.clearRect(0, 0, W, H);
  g.fillStyle = set.frame;
  g.fillRect(0, 0, W, H);
  // The hairline the board top has inside its own frame, at the same weight and
  // the same colour: it is what stops a filled rectangle reading as a sticker.
  g.strokeStyle = 'rgba(0,0,0,0.45)';
  g.lineWidth = 0.02 * U;
  g.strokeRect(0.10 * U, 0.10 * U, W - 0.20 * U, H - 0.20 * U);

  g.textAlign = 'center';
  g.textBaseline = 'middle';
  // The light squares' colour, which is where the coordinates on the board's own
  // edge take theirs from: it keeps the type legible on a dark set and stops it
  // shouting on a pale one, at no cost to either.
  g.fillStyle = set.light;
  // Half the depth, and the baseline is the middle of the type rather than the
  // foot of it: a name is centred in the plaque it is printed on.
  fitText(g, plate.who, W / 2, H / 2, W - 0.5 * U, 0.36 * U, 600, SANS);
}

function buildPieces(ctx) {
  const { THREE, scene } = ctx;
  const p = ctx.params;

  // Lacquered wood, and the grain that makes it wood rather than a brown solid.
  // The two maps are shared by both halves of the set — one blank, sawn into
  // thirty-two men — and the colour on top of them is written every frame from
  // whichever board set is chosen.
  //
  // `bumpScale` is small and had to be found by looking. The relief is fine and
  // it is packed tight, so the slope three takes off it per screen pixel is
  // steep: at 1 the men came out FLUTED, with the grain standing up as ridges you
  // could count, which is the same failure the ring count had and arrived from a
  // different direction. A tenth is a lacquered surface that catches the key
  // light along the turned edges and is flat everywhere else, which is what a
  // polished piece of wood is.
  const wood = materialTexture(THREE, woodShade(makeRng(7411)));
  const woodMat = (colour) => new THREE.MeshStandardMaterial({
    color: colour, map: wood.map, bumpMap: wood.bump, bumpScale: 0.10,
    roughness: 0.46, metalness: 0.06,
  });
  ctx.mats = { w: woodMat(0xefe4cd), b: woodMat(0x1c2028) };

  // Marble. A third set rather than the wooden one with different numbers on it,
  // because what makes polished stone read as stone is two things the wood
  // materials cannot carry: the veining, which is a different picture, and the
  // POLISH, which is a clear coat over a slightly rough body rather than a
  // uniformly glossy surface. `MeshPhysicalMaterial` for the clearcoat alone —
  // there is no transmission here, so it costs a slightly longer shader and none
  // of the extra passes a glass set costs.
  //
  // `envMapIntensity` above 1 on top of the scene's own `environmentIntensity`,
  // because a dark polished stone has almost nothing else to be seen by: its
  // diffuse is nearly nothing, the one key light gives it one highlight, and what
  // is left to draw the rest of the shape is the room.
  //
  // Both stones are kept rather than one being chosen at setup, because which one
  // the dark half is cut from depends on the colour it is asked for, and that
  // colour is live — see `darkStone`.
  ctx.stone = {
    light: materialTexture(THREE, marbleShade(makeRng(2266), false)),
    dark: materialTexture(THREE, marbleShade(makeRng(5108), true)),
  };
  const marbleMat = (colour, stone) => new THREE.MeshPhysicalMaterial({
    color: colour, map: stone.map, bumpMap: stone.bump, bumpScale: 0.20,
    roughness: 0.24, metalness: 0,
    clearcoat: 0.9, clearcoatRoughness: 0.10,
    envMapIntensity: 1.35,
  });
  ctx.marbleMats = {
    w: marbleMat(0xefe4cd, ctx.stone.light),
    b: marbleMat(0x1c2028, ctx.stone.dark),
  };

  // The same men in glass, as a SECOND PAIR rather than as `transmission` set on
  // the pair above: transmission changes which shader a mesh compiles, so
  // turning it on and off through a live parameter would rebuild two programs
  // every time it moved. Both pairs are built here, and `drawPieces` hands each
  // mesh whichever one `Piece finish` asks for — one assignment a frame.
  //
  // WHERE THE COLOUR OF A GLASS MAN COMES FROM
  // Not from `color`. On a fully transmissive material that tints a thin surface
  // layer and little else, and putting the set's two colours there gives two
  // sheets of coloured cellophane. It comes from ATTENUATION instead, which is
  // the honest model: `attenuationColor` is what is LEFT of a ray after it has
  // crossed `attenuationDistance` of the stuff, and `thickness` is how much of it
  // there is to cross — 0.55, a little under the 0.7 width of a piece whose base
  // radius is 0.35. The light men then come out near-clear crystal, because
  // their colour is nearly white and nearly white absorbs nothing, and the dark
  // men come out smoked rather than black. That is the actual difference between
  // the two halves of a real glass set.
  //
  // `transparent` is deliberately left FALSE. Transmissive meshes are drawn in a
  // pass of their own, ahead of the transparent list, and they write depth — so
  // thirty-two of them occlude each other correctly with no sorting at all, and
  // what shows through them comes from the transmission sampler rather than from
  // blending against the frame.
  //
  // What they do not refract is each other: that pass draws the OPAQUE scene, so
  // a king standing behind a queen shows the board through her rather than the
  // king. At this size it is invisible, and the alternative is another full pass
  // per piece.
  //
  // WHY IT IS FROSTED AND NOT CLEAR
  // Clear glass at this size is nearly invisible. A polished transmissive man
  // over a board that is already light and dark squares shows you the squares,
  // very slightly bent, and the piece itself survives only as a rim — which is
  // fine for one hero shot and useless for reading a position. Frosting is what
  // gives it a body: roughness scatters what comes through, so the man is a pale
  // solid with the board suggested behind him rather than seen through him. See
  // `Frosting` for the default and for where it stops being worth raising.
  //
  // The clear coat is the other half of the same problem and does not come off a
  // parameter, because there is no setting of it that is wrong. It is a polished
  // skin over the frosted body — a second, sharp specular lobe — and it is what
  // draws the silhouette: without it a frosted man has no hard edge anywhere and
  // dissolves into whatever is behind him at the exact angles the camera spends
  // its time at. Real frosted glass is sandblasted and then fire-polished, so
  // this is also what it is.
  const glassMat = () => new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 0,
    // Overwritten every frame from `Frosting`; this is only the first frame.
    roughness: 0.26,
    transmission: 1,
    ior: 1.52,               // soda-lime glass, the same as a window
    // Thicker than the 0.55 it was, which is the other half of making a frosted
    // man read: attenuation is a distance THROUGH the piece, so a thin one is a
    // pale one whatever the tint says.
    thickness: 0.66,
    clearcoat: 1,
    clearcoatRoughness: 0.06,
    // The room is where a glass piece's shape comes from. The two directional
    // lights and the ambient are aimed at a set that has a diffuse surface for
    // them to land on, and a transmissive one very nearly does not — what is left
    // is what it reflects, which is the environment.
    envMapIntensity: 1.5,
    // Both of these are written every frame from the set and from Glass tint;
    // the values here are only what the first frame is built with.
    attenuationDistance: 1.4,
    attenuationColor: 0xffffff,
  });
  ctx.glassMats = { w: glassMat(), b: glassMat() };

  // A fourth pair of pairs: the three sets again, kept aside for whichever king
  // is alight. Cloned here rather than at the moment one is wanted, because a
  // clone is a new material and a new material is a shader compiled — which
  // would be a hitch on the exact frame a mate lands, the one frame in the video
  // that must not stutter. `drawKingHeat` copies the live material into whichever
  // of these it needs and adds the emissive on top, so the defines never change
  // and nothing recompiles after this line.
  ctx.hotMats = {
    wood: { w: ctx.mats.w.clone(), b: ctx.mats.b.clone() },
    marble: { w: ctx.marbleMats.w.clone(), b: ctx.marbleMats.b.clone() },
    glass: { w: ctx.glassMats.w.clone(), b: ctx.glassMats.b.clone() },
  };

  fitMeshes(ctx);
}

/**
 * Give every actor a mesh for every shape it ever takes.
 *
 * Split out of `buildPieces` because a live game can hand an actor a shape it did
 * not have when the men were built — a promotion is a piece that changes form
 * mid-score — and because a re-bake makes new actor objects for the same
 * thirty-two men. `carry` is the actors from the bake before, and what is carried
 * across is the MESHES: they are in the scene, they are what the last frame was
 * drawn with, and nothing about a new half-move makes them wrong. Only a shape
 * nobody has a mesh for yet is built here.
 *
 * The geometry cache is on `ctx` for the same reason — one lathe per piece type
 * for the life of the scene, however many times the game is re-baked.
 */
function fitMeshes(ctx, carry = null) {
  const { THREE, scene } = ctx;
  const p = ctx.params;
  ctx.geos ||= {};
  // Cached by TYPE alone, which is only safe because `men` is not live: changing
  // the set rebuilds the scene from `setup`, and a fresh `ctx` is a fresh cache.
  // A re-bake — the thing this cache exists for — cannot change which set is on
  // the board, so no key is needed for it.
  // The flat board keeps its own men whatever `Piece style` says: a turned piece
  // from straight above is rings. Its ink is cached beside the token, one texture
  // per type, and a material per type per side because the texture is the type's
  // and the colour is the side's. `dimension` is not live either, so the same
  // fresh-ctx argument covers it.
  const flat = p.dimension === '2d';
  ctx.flatInk ||= {};
  const geoFor = (type) => {
    if (ctx.geos[type]) return ctx.geos[type];
    if (!flat) return (ctx.geos[type] = pieceGeometry(THREE, type, p.men));
    if (p.flatSet === 'diagram' && DIAGRAM_MEN) return (ctx.geos[type] = diagramGeometry(ctx, type));
    const { body, ink, inkTexture } = flatGeometry(THREE, type);
    const inkMat = () => new THREE.MeshBasicMaterial({
      map: inkTexture, transparent: true, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4,
    });
    ctx.flatInk[type] = { geometry: ink, texture: inkTexture, mats: { w: inkMat(), b: inkMat() } };
    return (ctx.geos[type] = body);
  };

  // Re-seeded per bake and walked in actor order, so a given man's facing and the
  // way it falls are the same numbers on every bake — the video's determinism
  // rule applied to a table that is now built more than once.
  const spin = makeRng(4177);
  for (const actor of ctx.actors) {
    // A knight is the one man with a facing in BOTH sets, and it gets the same
    // one in both: mirrored by colour, so the two sides look like each other's
    // reflection. Everything else in the Staunton set is a solid of revolution
    // and does not care, but it still needs a base facing for the knock to turn
    // from.
    //
    // The two knights are not modelled the same way round, and the angle is
    // written to suit the carved one. `figures.js` builds its horse facing +x —
    // sideways across the board, because that head is a silhouette and this
    // camera lives at the two ENDS of the board, where a knight facing the enemy
    // would show you the back of its own neck. The scanned Staunton head is
    // deeper than it is wide and reads from any angle, so it has nothing to lose
    // by taking the same number; what it does with it is look along the board
    // rather than across it.
    //
    // The carved men are not solids of revolution and every one of them has a
    // FACE, so in that set the rest of the pieces have a facing too: down the
    // board, at the other side, which is how a figurine set is laid out and the
    // only arrangement in which the two armies are looking at each other. They
    // are modelled facing +z and White stands at +z, so White is the half that
    // turns. It does mean the camera behind White is behind White's own men for
    // most of the game — which is what being behind your own men looks like, and
    // the crowns, the mitres and the mane read from the back as well as the
    // front.
    // Flat men all face the same way, head up the screen — they are a diagram,
    // not a pair of armies. `drawPieces` turns them for a viewer at Black's end.
    actor.faceY = flat ? 0 : formAt(actor, 0) === 'n'
      ? (actor.color === 'w' ? 0 : Math.PI)
      // THE FIGURES, AND ONLY THE FIGURES. This read `!== 'staunton'` while there
      // were two sets and meant "the carved one"; with a third it would have
      // turned the club set too, which is a solid of revolution except for the
      // knight's head — and the knight is already handled on the line above, so
      // the turn would have been invisible on five pieces and wrong on the
      // sixth. Named rather than negated, so a fourth set opts in.
      : (p.men === 'figures' && actor.color === 'w' ? Math.PI : 0);
    // How far it turns when it is hit. Drawn once, so a given piece always falls
    // the same way however many times the video is rendered.
    actor.spin = (spin() - 0.5) * 2.4;

    actor.meshes = carry ? carry[actor.id].meshes : {};
    for (const form of actor.forms) {
      if (actor.meshes[form.type]) continue;
      const mesh = new THREE.Mesh(geoFor(form.type), ctx.mats[actor.color]);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.rotation.y = actor.faceY;
      mesh.visible = false;
      scene.add(mesh);
      // Its outline, as a child with no transform of its own, so everything that
      // moves this piece moves its line and nothing has to remember to. Not on a
      // flat man: its drawing already has its own outline band, and a hull round
      // the token as well drew every edge twice. With no hull, `wrap` skips it.
      if (!flat) ctx.outline.wear(mesh);
      // The flat man's drawing, the same way and for the same reason.
      if (flat) {
        const ink = ctx.flatInk[form.type];
        const drawing = new THREE.Mesh(ink.geometry, ink.mats[actor.color]);
        drawing.name = 'ink';
        drawing.renderOrder = 1;
        mesh.add(drawing);
      }
      actor.meshes[form.type] = mesh;
    }
  }
}

/**
 * Taking a piece, built once as nine little ballistic tables.
 *
 * WHAT THE EFFECT IS, AND WHY IT IS NOT AN EXPLOSION
 * The obvious reading of "make taking a piece feel like something" is debris and
 * fire, and it is the wrong one twice over. It is wrong for the subject — this is
 * an 1851 drawing-room game, not a battle — and it is wrong for the frame, since
 * anything with real extent sits on top of the board and hides the position,
 * which is the one thing the viewer must be able to read at all times. Fracturing
 * the piece was the other candidate and `shared/fracture.js` is right there, but
 * a piece that shatters cannot then be laid beside the board, and the two rows of
 * taken pieces are this game's running scoreline.
 *
 * So it is a STRIKE: what it would look like if two hard lacquered things met at
 * speed. Four parts, each with a different job:
 *
 *   sparks       a spray of embers off the point of contact, thrown up and out,
 *                falling under gravity and skittering when they land
 *   a flash      a real point light for a sixth of a second, so the strike
 *                lights the men around it instead of being pasted over them
 *   a ring       a shockwave flat on the board, which is the part that still
 *                reads from the overhead shots
 *   the knock    the taken piece is hit rather than lifted — it tips hard, spins,
 *                and only then arcs into the tray
 *
 * IT SCALES WITH WHAT WAS TAKEN, and that is the whole reason it is worth having
 * in this particular game. Nine captures, and their whole point is what is being
 * given away: a pawn gets a tick, and the queen going on f6 gets four times the
 * sparks, twice the ring and a light you can see across the board. No move is
 * special-cased — 22.Qf6+ is the biggest strike in the video because a queen is
 * worth nine, which is also why it is the most famous move in it.
 *
 * DETERMINISM
 * Every particle's direction, speed and lifetime is drawn from a seeded PRNG at
 * setup and never touched again, so a particle's position is a closed-form
 * function of how long ago the strike happened: p = p0 + v*e - g*e*e/2. There is
 * nothing to step and nothing to carry between frames, which is what the harness
 * requires and also why frames can be rendered out of order across eight
 * browsers.
 */
function buildStrikes(ctx) {
  const { THREE, scene } = ctx;
  const tl = ctx.timeline;
  // Cached rather than drawn per call: a live game rebuilds these tables on every
  // half-move, and one canvas texture for the life of the scene is both cheaper
  // and one fewer thing for `clearStrikes` to have to remember to dispose.
  const sprite = (ctx.sparkSprite ||= sparkTexture(THREE));

  ctx.strikes = [];
  ctx.strikeByPly = new Map();

  ctx.game.plies.forEach((ply, k) => {
    if (ply.move.capturedSq < 0) return;
    const actor = ctx.actors[ply.victim];
    const value = Math.max(1, VALUE[formAt(actor, k)]);
    const at = squarePos(ply.move.capturedSq);
    // The strike lands when the capturing piece arrives, not when it sets off.
    // Lifted, that is the end of its glide, which is later in the move and later
    // still for the pause at the top of the lift — see `liftWindow`.
    const t0 = tl.start[k] + tl.moveSec[k]
      * (ctx.params.lift ? liftWindow(tl.moveSec[k])[1] : 0.45);
    // A queen takes longer to go over than a pawn does, and the sparks off her
    // last longer. Both are the same one line of scaling.
    const life = 0.55 + 0.045 * value;
    const fall = clamp(0.75 + 0.055 * value, 0.7, 1.5);

    // Capacity is set for the largest the `Strike size` slider can ask for, so
    // the slider stays live — it changes how many of these are drawn, not how
    // many exist.
    const capacity = Math.round((12 + 8 * value) * 2);
    const rng = makeRng(1009 + k * 131 + value * 7);
    const dir = new Float32Array(capacity * 3);
    const speed = new Float32Array(capacity);
    const span = new Float32Array(capacity);
    const seed = new Float32Array(capacity);
    const off = new Float32Array(capacity * 3);
    for (let i = 0; i < capacity; i++) {
      // An upward-biased cone rather than a sphere: sparks off a struck object
      // go up and out, and half a sphere aimed into the board is half the
      // particles wasted under it.
      const theta = rng() * Math.PI * 2;
      // Biased hard upward. A wider cone looked right in the abstract and put
      // most of the burst flat along the board, where warm additive sparks over
      // cream squares in full key light are very nearly invisible. Thrown up,
      // they clear the pieces and are seen against the dark of the room.
      const up = Math.pow(rng(), 0.78);
      const r = Math.sqrt(Math.max(0, 1 - up * up));
      dir[i * 3] = Math.cos(theta) * r;
      dir[i * 3 + 1] = up;
      dir[i * 3 + 2] = Math.sin(theta) * r;
      speed[i] = (1.9 + 0.24 * value) * (0.5 + 0.8 * rng());
      span[i] = life * (0.55 + 0.55 * rng());
      seed[i] = rng() * Math.PI * 2;
      // Emitted from a small disc at the foot of the piece, not from one point:
      // two pieces meet along a base, and a single origin reads as a firework.
      const oa = rng() * Math.PI * 2;
      const orad = 0.15 * Math.sqrt(rng());
      off[i * 3] = Math.cos(oa) * orad;
      off[i * 3 + 1] = 0.10 + 0.26 * rng();
      off[i * 3 + 2] = Math.sin(oa) * orad;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(capacity * 3), 3));
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(capacity * 3), 3));
    const mat = new THREE.PointsMaterial({
      size: 0.09, map: sprite, vertexColors: true, transparent: true,
      // Additive, so a spark fades by going dark rather than by going
      // transparent — which is what lets one uniform point size carry a whole
      // burst without a custom shader to shrink each one.
      blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
      toneMapped: false,
    });
    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.visible = false;
    points.renderOrder = 20;
    scene.add(points);

    // The ring lies on the board rather than standing in the air: it is the part
    // of the strike that has to survive the overhead shots, where anything
    // vertical is edge on.
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xffc070, transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
      toneMapped: false,
    });
    // Thin. At 0.62 the annulus is a disc with a hole in it, and what it read as
    // on the board was a warm stain three squares across rather than a wave.
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.86, 1, 72), ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(at.x, TOP + 0.006, at.z);
    ring.visible = false;
    ring.renderOrder = 18;
    scene.add(ring);

    // The core: a hard white pop at the point of contact, gone in a sixth of a
    // second. The sparks and the light both take a moment to read, and a strike
    // on a light square needs something at frame one that is brighter than the
    // square is. A billboard rather than a sphere, because it is a flash of light
    // and not an object.
    const coreMat = new THREE.SpriteMaterial({
      map: sprite, color: 0xffffff, transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
    });
    const core = new THREE.Sprite(coreMat);
    core.position.set(at.x, TOP + 0.14, at.z);
    core.visible = false;
    core.renderOrder = 22;
    scene.add(core);

    const strike = {
      ply: k, actor, value, t0, life, fall, capacity,
      x: at.x, z: at.z, dir, speed, span, seed, off,
      points, geo, mat, ring, ringMat, core, coreMat,
    };
    ctx.strikes.push(strike);
    ctx.strikeByPly.set(k, strike);
  });

  // One light for all nine of them — they never overlap, and a light per capture
  // would be nine shadowless lights in the scene for the sake of nine sixths of
  // a second. No shadow: a shadow that appears for six frames reads as a glitch,
  // and the cost is a second shadow map on every frame of the video.
  // Three squares of reach, not six. At six a pawn being taken washed the whole
  // board peach for two frames, which is a lighting effect rather than a strike
  // and swamped the white core it was supposed to be supporting. Three keeps it
  // a pool around the square, and the pieces next to it are what read as lit.
  // Built once and left in the scene across re-bakes — it belongs to no
  // particular capture, so there is nothing about a new move that changes it.
  if (!ctx.strikeLight) {
    ctx.strikeLight = new THREE.PointLight(0xffb257, 0, 3.0, 2);
    ctx.strikeLight.castShadow = false;
    ctx.strikeLight.visible = false;
    scene.add(ctx.strikeLight);
  }
}

/**
 * Take the strikes back out of the scene, with what they own.
 *
 * Only a re-bake calls this, and a re-bake is only a live game. Each strike holds
 * a points cloud, a ring and a core sprite that are its own — the spark texture is
 * the one thing they share, and it is kept. Without the disposals a game would
 * leave a set of buffers on the GPU behind every capture, every move.
 */
function clearStrikes(ctx) {
  for (const s of ctx.strikes) {
    ctx.scene.remove(s.points, s.ring, s.core);
    s.geo.dispose();
    s.mat.dispose();
    s.ring.geometry.dispose();
    s.ringMat.dispose();
    s.coreMat.dispose();
  }
  ctx.strikes = [];
  ctx.strikeByPly.clear();
}

/** A round, soft dot for the sparks to be drawn with. */
/**
 * THE GHOSTS: see-through copies of the piece that is moving, left behind along
 * its path and fading out as it goes, so the eye has where it came from and
 * where it is at once. Castling leaves its own for the king and the rook.
 *
 * HOW MANY is `Ghosts per square`. At 1 there is only the one on the square it
 * came from; above that the rest are dropped along the way, spaced evenly along
 * the PATH — a knight's round its L — so a move of two squares at 3 leaves six,
 * wherever the easing had the piece at the time. Each is a snapshot of the piece
 * where it passed: at that height, leaning as it leant.
 *
 * NOTHING FADES UNTIL THE PIECE HAS SETTLED. The trail holds at full strength
 * for the whole move, so the move can be read whole with the piece standing at
 * the end of it. Then it clears IN THE ORDER IT WAS LAID DOWN — the square it
 * came from first, the last one dropped last — so the trail rubs out from the
 * back and the move still reads the way it went while it goes. That is why this
 * runs off the CLOCK rather than the move's progress, as the strikes and the
 * ring do: it outlives the move that made it.
 *
 * IT IS THE PIECE, NOT A PICTURE OF ONE: the same geometry and the same
 * material, copied every frame, so it follows the finish and the colour
 * sliders live, and a glass set leaves a glass ghost. Then it is tinted and lit
 * in its side's glow, gold or blue as `RING` has it. It throws no shadow and
 * wears no outline, which is most of what makes it read as a ghost rather than
 * as a second piece. On the flat board it keeps its drawing, faded with it.
 *
 * Two of them for the whole board, made the first time they are wanted.
 */
function drawGhosts(ctx, t) {
  const pool = ctx.ghosts;
  if (pool) for (const g of pool) g.group.visible = false;
  const p = ctx.params;
  if (!p.ghost) return;

  // The last move to have started, and how far through it — or how long since it
  // finished, which is what the trail is now fading on.
  const tl = ctx.timeline;
  const first = tl.first ?? 0;
  let k = tl.last ?? ctx.game.plies.length - 1;
  while (k >= first && !(tl.start[k] <= t)) k--;
  if (k < first) return;
  const move = tl.moveSec[k];
  const frac = clamp01((t - tl.start[k]) / Math.max(1e-6, move));
  const since = t - (tl.start[k] + move);
  if (since > GHOST.stagger + GHOST.fade) return;
  // How solid the `j`th of `n` ghosts is: full while the piece is still on its
  // way, then out in turn once it has settled.
  const strengthOf = (j, n) => {
    if (since <= 0) return GHOST.opacity;
    const waits = n > 1 ? (j / (n - 1)) * GHOST.stagger : 0;
    return GHOST.opacity * (1 - smoothstep((since - waits) / GHOST.fade));
  };

  const ply = ctx.game.plies[k];
  if (!ply) return;

  const { THREE } = ctx;
  ctx.ghosts ||= [];
  ctx.ghostTint ||= new THREE.Color();
  // Made as they are wanted and kept: a move across the board at six a square
  // leaves a few dozen, a short one leaves two, and neither pays for the other.
  const ghostAt = (i) => {
    let ghost = ctx.ghosts[i];
    if (!ghost) {
      const group = new THREE.Group();
      const body = new THREE.Mesh();
      const ink = new THREE.Mesh();
      ink.renderOrder = 2;
      group.add(body, ink);
      ctx.scene.add(group);
      // Their own materials, so fading them fades nothing on the board.
      ghost = ctx.ghosts[i] = { group, body, ink, mats: new Map() };
    }
    return ghost;
  };

  // Copied from the piece's own material each frame, then made see-through.
  // One copy per source material, kept, so switching finish is a lookup.
  const faded = (ghost, source, opacity, tint) => {
    let mat = ghost.mats.get(source);
    if (!mat) ghost.mats.set(source, (mat = source.clone()));
    mat.copy(source);
    mat.transparent = true;
    mat.depthWrite = false;
    mat.opacity = (source.opacity ?? 1) * opacity;
    if (tint) {
      mat.color.lerp(tint, GHOST.tint);
      if (mat.emissive) mat.emissive.copy(tint).multiplyScalar(GHOST.glow);
    }
    return mat;
  };

  const flat = p.dimension === '2d';
  const perSquare = Math.max(1, Math.round(p.ghosts ?? 1));
  const ids = [ply.mover, ply.rook?.id].filter((id) => id !== undefined);
  let used = 0;

  for (const id of ids) {
    const actor = ctx.actors[id];
    const from = actor.at[k];
    const to = actor.at[k + 1] ?? from;
    if (from === undefined || from < 0 || from === to) continue;
    const type = formAt(actor, k);
    const mesh = actor.meshes[type];
    if (!mesh) continue;
    const a = squarePos(from);
    const b = squarePos(to);
    const knight = Math.abs(b.x - a.x) * Math.abs(b.z - a.z) === 2;
    const lifting = p.lift && !flat;
    const window = liftWindow(move);
    const height = liftHeight(a, b, knight);
    // One on the square it came from, then one more for every square of the
    // trip at this setting, spaced along the PATH rather than the clock.
    const squares = knight ? 3 : Math.hypot(b.x - a.x, b.z - a.z);
    const count = Math.max(1, Math.round(perSquare * squares));
    ctx.ghostTint.set(actor.color === 'w' ? RING.white : RING.black);
    const drawing = mesh.getObjectByName('ink');

    for (let j = 0; j < count; j++) {
      const along = j / count;
      // When the piece was there: the glide walks the path inside its own window
      // of the move, everything else walks it across the whole of it.
      const u = unsmooth(along);
      const dropped = lifting ? window[0] + u * (window[1] - window[0]) : u;
      if (frac < dropped) break;

      const strength = strengthOf(j, count);
      if (strength <= 0.001) continue;

      const ghost = ghostAt(used++);
      const at = movePath(a, b, knight, along);
      // The piece as it was when it passed: at that height, leaning as it leant.
      const lifted = lifting ? liftedMove(dropped, height, move) : null;
      ghost.group.visible = true;
      ghost.group.position.set(at.x, TOP + (lifted ? lifted.rise : 0), at.z);
      ghost.group.rotation.set(0, actor.faceY, 0);
      if (lifted) leanInto(ctx, ghost.group, at.hx, at.hz, lifted.lean);
      ghost.body.geometry = mesh.geometry;
      ghost.body.material = faded(ghost, mesh.material, strength, ctx.ghostTint);
      // The flat man's drawing, if he has one.
      ghost.ink.visible = !!drawing;
      if (drawing) {
        ghost.ink.geometry = drawing.geometry;
        ghost.ink.material = faded(ghost, drawing.material, strength);
        ghost.ink.position.copy(drawing.position);
        ghost.ink.rotation.copy(drawing.rotation);
      }
    }
  }
}

/**
 * A soft glowing annulus, white, for a material to colour: clear in the middle,
 * brightest just inside the edge and falling away to nothing at it, so it reads
 * as light on the board rather than as a painted line.
 */
function ringTexture(THREE) {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d');
  const c = size / 2;
  const grad = g.createRadialGradient(c, c, 0, c, c, c);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.52, 'rgba(255,255,255,0)');
  grad.addColorStop(0.66, 'rgba(255,255,255,0.3)');
  grad.addColorStop(0.8, 'rgba(255,255,255,0.95)');
  grad.addColorStop(0.9, 'rgba(255,255,255,0.35)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * THE GLOW UNDER THE MOVING PIECE, for this instant.
 *
 * A ring of light on the board under whichever man moved last, in his side's
 * colour. It fades in as he sets off and follows him across, spreading and
 * dimming the higher he is lifted, the way a light under him would; when he
 * lands a second ring pulses out from under him and both are gone within the
 * second. Knights, castling kings and pawns that take all get it; a castling
 * rook does not, since the move is the king's.
 *
 * Off the clock rather than the move's progress, like the strikes, because the
 * pulse outlives the move that made it. Two meshes for the whole board, made
 * the first time they are wanted: one move glows at a time.
 */
function drawMoveRing(ctx, t) {
  const p = ctx.params;
  const tl = ctx.timeline;
  const rings = ctx.moveRing;
  if (rings) rings.glow.visible = rings.pulse.visible = false;
  if (!p.ring) return;

  // The last move to have started, and whether it is still glowing.
  const first = tl.first ?? 0;
  let k = tl.last ?? ctx.game.plies.length - 1;
  while (k >= first && !(tl.start[k] <= t)) k--;
  if (k < first) return;
  const move = tl.moveSec[k];
  const landed = tl.start[k] + move * (p.lift ? LIFT.down[1] : 1);
  const since = t - tl.start[k];
  const after = t - landed;
  if (after > Math.max(RING.fade, RING.pulse)) return;

  const ply = ctx.game.plies[k];
  const actor = ctx.actors[ply.mover];
  const mesh = Object.values(actor.meshes).find((m) => m.visible);
  if (!mesh) return;

  const { THREE } = ctx;
  if (!ctx.moveRing) {
    const tex = ringTexture(THREE);
    const make = () => {
      // Laid OVER the board, not added to it. Added, as the strike's ring is, a
      // blue ring on a cream square clipped to white and so did a gold one, and
      // both sides glowed the same on half the board. Over it, the colour is
      // the colour on any square, and it carries its own alpha for a render with
      // no background under it.
      const mat = new THREE.MeshBasicMaterial({
        map: tex, transparent: true, opacity: 0, depthWrite: false, toneMapped: false,
      });
      const ring = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 17;
      ring.visible = false;
      ctx.scene.add(ring);
      return ring;
    };
    ctx.moveRing = { glow: make(), pulse: make() };
  }
  const { glow, pulse } = ctx.moveRing;
  const colour = actor.color === 'w' ? RING.white : RING.black;
  const x = mesh.position.x;
  const z = mesh.position.z;
  const high = Math.max(0, mesh.position.y - TOP);

  // The glow: in over the first fifth of the move, held, out after landing.
  const on = smoothstep(since / Math.max(1e-3, move * RING.in));
  const off = after > 0 ? 1 - smoothstep(after / RING.fade) : 1;
  const spread = 1 + high * RING.lifted;
  glow.visible = true;
  glow.material.color.set(colour);
  glow.material.opacity = on * off / (spread * spread);
  glow.position.set(x, TOP + 0.004, z);
  glow.scale.setScalar(RING.radius * spread);

  // The pulse: from under him outward, fading as it goes.
  if (after > 0 && after < RING.pulse) {
    const k2 = after / RING.pulse;
    pulse.visible = true;
    pulse.material.color.set(colour);
    pulse.material.opacity = (1 - k2) ** 2;
    pulse.position.set(x, TOP + 0.005, z);
    pulse.scale.setScalar(RING.radius * (1 + (RING.spread - 1) * (1 - (1 - k2) ** 2)));
  }
}

function sparkTexture(THREE) {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Every strike, evaluated for this instant.
 *
 * Nine of them, checked every frame, which is cheaper than working out which one
 * could be live and has no edge case where two overlap — at a short `Length`
 * with `Emphasis` at 0 the tail of one burst can still be in the air when the
 * next capture lands, and the honest answer to that is to draw both.
 */
function drawStrikes(ctx, t) {
  const p = ctx.params;
  const scale = p.strike;
  const tint = new ctx.THREE.Color(p.spark);
  let flash = 0;
  let flashAt = null;

  for (const s of ctx.strikes) {
    const e = t - s.t0;
    const alive = scale > 0 && e >= -1e-6 && e <= s.life * 1.35;
    s.points.visible = alive;
    s.ring.visible = alive && p.shockwave;
    s.core.visible = alive;
    if (!alive) continue;

    // ---- sparks --------------------------------------------------------
    const shown = Math.min(s.capacity, Math.max(1, Math.round((12 + 8 * s.value) * scale)));
    const pos = s.geo.attributes.position.array;
    const col = s.geo.attributes.color.array;
    for (let i = 0; i < s.capacity; i++) {
      const i3 = i * 3;
      if (i >= shown) { col[i3] = col[i3 + 1] = col[i3 + 2] = 0; continue; }
      const u = e / s.span[i];
      if (u >= 1) { col[i3] = col[i3 + 1] = col[i3 + 2] = 0; continue; }
      const v = s.speed[i] * (0.7 + 0.5 * scale);
      let y = TOP + s.off[i3 + 1] + s.dir[i3 + 1] * v * e - 0.5 * SPARK_G * e * e;
      let bright = (1 - u) * (1 - u);
      // A spark that reaches the board skitters along it instead of falling
      // through — and dims fast, because it has just given up its heat to a
      // piece of wood.
      if (y < TOP + 0.012) {
        y = TOP + 0.012;
        bright *= 0.45;
      }
      pos[i3] = s.x + s.off[i3] + s.dir[i3] * v * e;
      pos[i3 + 1] = y;
      pos[i3 + 2] = s.z + s.off[i3 + 2] + s.dir[i3 + 2] * v * e;
      // Embers flicker. Off the particle's own seed and the clock, so it is a
      // function of the frame like everything else.
      const flicker = 0.72 + 0.28 * Math.sin(s.seed[i] + e * 34);
      const b = bright * flicker * (1.7 + 0.9 * scale);
      col[i3] = tint.r * b;
      col[i3 + 1] = tint.g * b;
      col[i3 + 2] = tint.b * b;
    }
    s.geo.attributes.position.needsUpdate = true;
    s.geo.attributes.color.needsUpdate = true;
    s.mat.size = (0.075 + 0.008 * s.value) * (0.6 + 0.7 * scale);

    // ---- the ring ------------------------------------------------------
    if (s.ring.visible) {
      const u = clamp01(e / (s.life * 0.7));
      // Fast out then easing off, which is how a wave in anything behaves, and
      // fading on a curve so it thins out rather than switching off.
      const grow = 1 - Math.pow(1 - u, 2.6);
      // A queen's wave reaches about a square and a third. The first pass had it
      // at two and a half, which stops being a shockwave and becomes weather.
      const radius = (0.30 + 0.28 * Math.sqrt(s.value)) * (0.7 + 0.5 * scale) * grow;
      s.ring.scale.setScalar(Math.max(0.02, radius));
      s.ringMat.color.copy(tint);
      s.ringMat.opacity = 0.9 * Math.pow(1 - u, 2) * clamp01(scale);
    }

    // ---- the flash, as a light and as a pop -----------------------------
    const fu = clamp01(e / 0.20);
    const strength = (1 - fu) * (1 - fu) * (0.8 + 1.1 * s.value * 0.11) * scale;
    const cu = clamp01(e / 0.16);
    s.core.visible = cu < 1;
    if (s.core.visible) {
      const size = (0.34 + 0.075 * s.value) * (0.65 + 0.5 * scale) * (0.55 + 0.75 * cu);
      s.core.scale.set(size, size, 1);
      s.coreMat.opacity = Math.pow(1 - cu, 1.6) * clamp01(scale);
      // White at the instant of contact, cooling into the spark colour as it
      // dies — which is the whole of what makes it read as heat.
      s.coreMat.color.setRGB(
        1 - (1 - tint.r) * cu,
        1 - (1 - tint.g) * cu,
        1 - (1 - tint.b) * cu,
      );
    }
    if (strength > flash) {
      flash = strength;
      flashAt = s;
    }
  }

  ctx.strikeLight.visible = flash > 0.001;
  if (ctx.strikeLight.visible) {
    ctx.strikeLight.color.copy(tint);
    ctx.strikeLight.intensity = flash * 7;
    ctx.strikeLight.position.set(flashAt.x, TOP + 0.28, flashAt.z);
  }
}

// ------------------------------------------------- check, and the closing shot

/**
 * A king in check glows. A king who is mated has the glow go out of him, goes
 * over, and is left lit on a board the room has fallen away from.
 *
 * WHY THE MATE IS NOT AN ESCALATION OF THE CHECK
 * It was, for a while: the glow turned into a fire and the king burned. It looked
 * well and it was wrong twice, in exactly the two ways `buildStrikes` sets out
 * for why a capture is a strike and not an explosion. Wrong for the SUBJECT,
 * because this is an 1851 drawing-room game and not a battle. And wrong for the
 * FRAME, because anything with real extent sits on top of the board and hides the
 * position — which at the mate is the worst thing an effect can do, since the
 * whole closing shot is `mateNet` laying out the argument for why it IS mate, and
 * a fire stands on top of that argument waving.
 *
 * There is a third thing, and it is the one that decides the shape of this.
 * CHECKMATE IS NOT AN ESCALATION. Chess does not kill the king; it runs out his
 * squares until there is nowhere left to go. Mate is enclosure, and it is silent.
 * A bonfire says "the same thing, louder", when what actually happened is that it
 * stopped. So the glow goes OUT here rather than up, and it is the last thing to
 * happen to him rather than the first.
 *
 * THREE BEATS, BECAUSE THE HOLD IS NINETEEN SECONDS
 * `timeline.js` gives the mate whatever is left of the video, and at three
 * minutes that is the better part of twenty seconds. No single gesture fills
 * that, and the shot has three separate things to say, so it says them in order:
 *
 *   the net closes    WHY. The squares he cannot go to put themselves out one at
 *                     a time, each with the line back to the piece that covers
 *                     it, and the ring lands on him as the last one goes. All of
 *                     it on the board's own texture, so nothing is ever covered.
 *   the king goes over  WHAT. The oldest image in the game. He falls INTO one of
 *                     the squares that was closed to him, which is both the
 *                     reason he is mated and a guarantee the square is empty.
 *   the room falls away  The photograph. The fill, the ambient and the room the
 *                     set is reflecting all drop back, the key stays, and what is
 *                     left lit is the board, the net and a warm patch where he
 *                     fell.
 *
 * Every part of it is a closed-form function of the clock, for the reason
 * everything else in this file is: frames are rendered out of order across eight
 * browsers, and nothing may be stepped or carried.
 */

/** The fractional part — where a breath is read from. */
function frac(x) { return x - Math.floor(x); }

/**
 * What is happening to which king at this instant, or null.
 *
 * Read off the position the board is SHOWING rather than off the game's final
 * one, which is what makes it work while the video scrubs and while a live game
 * is still being played. `view.done` is the number of half-moves that have
 * landed, so `positions[done]` is what is on the board and `positions[done].turn`
 * is the side who has to answer — the side, if anybody is, standing in check.
 */
function kingHeatAt(ctx, view, t) {
  const plies = ctx.game.plies;
  const done = view.phase === 'intro' ? 0 : view.done;
  if (done <= 0 || done > plies.length) return null;
  const ply = plies[done - 1];
  if (!ply.check) return null;
  const pos = ctx.game.positions[done];
  const king = ctx.kings?.[pos.turn];
  if (!king) return null;

  const tl = ctx.timeline;
  // It comes up as the checking move LANDS, not as it sets off.
  const since = t - (tl.start[done - 1] + tl.moveSec[done - 1]);

  if (ply.mate) {
    // A LAMP GOING OUT, not a fire catching. It flares as the move lands — the
    // same flare any check gets, because for that quarter of a second this IS
    // just a check — and then dies away under the closing net. By the time he
    // goes over there is nothing left of it, which is the point: what falls is
    // dark.
    const flare = smoothstep(clamp01(since / 0.3));
    const die = smoothstep(clamp01((since - 0.25) / 1.15));
    return { mate: true, king, amount: flare * (1 - die) };
  }

  const rise = smoothstep(clamp01(since / 0.35));
  // It goes out across whatever answers it. There is no look-ahead here and none
  // needed: a legal move made FROM a checked position must end the check, so a
  // move in flight while this is lit is always the escape from it.
  const fall = view.moving ? 1 - smoothstep(view.frac) : 1;
  return { mate: false, king, amount: rise * fall };
}

/**
 * How far into the closing shot this frame is, or null if it is not in one.
 *
 * GUARDED SO A LIVE GAME NEVER ENTERS IT. Every game in `chess-app/play/` is a
 * score that is still growing, and it says nothing about how it ends, so
 * `endingOf` answers 'mate' for it and the guard below is the one it always was:
 * the last ply has to actually be mate. A game in `games.js` that says it ends in
 * a resignation or a draw is let through instead — the shot is what holds on the
 * move the game stopped at. Scrubbing back before the final move still leaves it.
 *
 * `topple` is the other half of the difference. A mated king goes over, and so
 * does a resigned one — tipping your king over IS resigning, and has been for
 * four hundred years. A draw topples nobody: there is no loser, and a king on his
 * side would be the one thing on screen claiming there was.
 */
function mateShot(ctx, t) {
  if (!ctx.params.closing) return null;
  const plies = ctx.game.plies;
  const last = plies.length - 1;
  const ending = endingOf(ctx.g);
  if (last < 0) return null;
  if (ending === 'mate' && !plies[last].mate) return null;
  // A clip that stops before the mate never enters it. The sentinel start times
  // would already keep `since` negative, but saying it here is the difference
  // between a guard and a coincidence.
  if ((ctx.timeline.last ?? last) !== last) return null;
  const king = ctx.kings?.[ctx.game.final.turn];
  if (!king) return null;
  const tl = ctx.timeline;
  const since = t - (tl.start[last] + tl.moveSec[last]);
  if (since <= 0) return null;
  const beat = ([a, b]) => clamp01((since - a) / (b - a));
  return {
    since,
    king,
    ending,
    topple: ending !== 'drawn',
    net: beat(MATE_NET),
    fall: beat(MATE_FALL),
    dim: beat(MATE_DIM),
  };
}

/**
 * Which way a beaten king falls, and whether he has room to lie flat.
 *
 * HE IS 1.7 SQUARES LONG LYING DOWN, which is the fact the first version of this
 * missed. Standing, a piece is on one square and that is the end of it; on his
 * side his crown comes to rest a square and a half from his own centre, which is
 * in the SECOND square along, not the first. Checking only the square he falls
 * into left him lying through a pawn two squares away in Legal's Mate.
 *
 * So every neighbour is scored on what is actually in the way:
 *
 *   4  the square he falls into is empty — without this he goes through
 *      whatever is standing there at the hips, which nothing rescues
 *   2  the square his crown reaches is empty, or is off the board altogether:
 *      the border is 0.55 wide and a crown overhanging it reads as a king who
 *      fell against the edge of the board, which is exactly what it is
 *   1  and, all else equal, a square the ENEMY covers rather than one of his
 *      own men is standing on. That is the picture worth having — he goes over
 *      into one of the squares he could not escape to.
 *
 * A smothered mate scores badly everywhere by construction: he is boxed in by
 * his own men and there is no clear way down. That is what `clear` is for — see
 * `toppleKing`, which leans him against them instead of laying him through them.
 *
 * IT IS SCORED ROUND THE KING, NOT ROUND THE NET, and that distinction is the
 * whole of this function's history. It used to walk `ctx.net.squares` and
 * measure everything from `net.king` — which is the mated king and his eight
 * neighbours for a game that ends in mate, and for a game that ends in a
 * RESIGNATION is `finishNet`'s answer to a different question: the square the
 * last move landed on, and the squares that move covers. So a resigning king
 * had his fall computed round a square he was not standing on, out of a list
 * that was not his neighbourhood. What that produced was a direction of seven
 * files (Kasparov–Topalov), a "square he falls into" that was three ranks away
 * and therefore never empty, and so `clear` false and a 62° lean for twenty-two
 * of the thirty-one resignations in the library — every one of which had an
 * empty square beside the king to go over into. Tipping your king IS resigning;
 * a king who only leans is the one gesture the shot cannot afford to fumble.
 *
 * So the king's square comes off the POSITION, the neighbours come off
 * `KING_STEPS`, and the net is asked for the one thing only it knows: which of
 * those squares the enemy covers, for the tie-break. A finish net has no such
 * claim to make and is not consulted — the third term simply drops out, and the
 * fall is decided on what is in the way, which is the part that was always true
 * of both endings.
 */
function fallWay(ctx) {
  const { THREE } = ctx;
  const pos = ctx.game.final;
  const king = pos.kingSquare(pos.turn);
  const kf = fileOf(king);
  const kr = rankOf(king);

  // Only a mating net is a statement about the squares round THIS king, and
  // `net.king` is how it says so. Empty for a resignation, and empty is right:
  // there is no net closing on him, so nothing prefers one open square to
  // another and the two terms above decide it.
  const covered = new Set(
    ctx.net && ctx.net.king === king
      ? ctx.net.squares.filter((cell) => cell.reason === 'attacked').map((cell) => cell.square)
      : [],
  );

  let best = null;
  for (const [df, dr] of KING_STEPS) {
    const f = kf + df;
    const r = kr + dr;
    if (f < 0 || f > 7 || r < 0 || r > 7) continue;
    const square = r * 8 + f;
    const cf = kf + df * 2;
    const cr = kr + dr * 2;
    const beyond = cf < 0 || cf > 7 || cr < 0 || cr > 7 ? null : cr * 8 + cf;
    const lands = !pos.at(square);
    const score = (lands ? 4 : 0)
      + (beyond === null || !pos.at(beyond) ? 2 : 0)
      + (covered.has(square) ? 1 : 0);
    if (!best || score > best.score) best = { score, df, dr, lands };
  }
  if (!best) return { way: new THREE.Vector3(0, 0, 1), clear: true };

  // `squarePos` counts x up the files and z DOWN the ranks, so the rank step
  // changes sign on the way into the world.
  const way = new THREE.Vector3(best.df, 0, -best.dr);
  return {
    way: way.lengthSq() > 1e-6 ? way.normalize() : new THREE.Vector3(0, 0, 1),
    clear: best.lands,
  };
}

/**
 * Lay the mated king down where he stands.
 *
 * Not `layDown`, though it is the same idea and was the first thing tried. That
 * one is a piece being KNOCKED — fast out, over inside the first third, spinning
 * as it goes and arriving in the tray — because it is what a capture looks like.
 * A mate is not a blow, it is a concession, and it wants the opposite easing: a
 * hesitation, then gravity, then one rock as he settles.
 *
 * He is tipped about a horizontal axis chosen by `fallWay`, which is why this
 * uses the quaternion rather than the Euler angles the rest of `drawPieces`
 * sets. `rotation.x` alone can only drop a piece down the board's z, and a king
 * who always fell the same way whatever mated him would be an animation rather
 * than an ending.
 *
 * AND HE DOES NOT ALWAYS REACH THE TABLE. With his own men packed round him
 * there is nowhere to lie flat, so he goes over as far as they let him and
 * stops against them — which is both what would happen and the only reading of a
 * smothered mate that does not put a king through a knight. Sixty-two degrees is
 * far enough past the vertical to be unmistakably over, and near enough to the
 * pieces round him to look propped rather than floating.
 */
function toppleKing(ctx, shot, u) {
  const { THREE } = ctx;
  const mesh = shot.king.meshes.k;
  if (!mesh || !mesh.visible) return;
  const at = squarePos(shot.king.at[ctx.game.plies.length]);
  const { way, clear } = fallWay(ctx);
  const flat = clear ? Math.PI / 2 : 1.08;

  // THE FALL AND THE LANDING ARE TWO SEPARATE STRETCHES OF THE BEAT, which is
  // the change that made this readable. It was one curve — `pow(u, 1.6)` all the
  // way to the end, with a small wobble laid over its last fifth — so the wobble
  // arrived while he was still two thirds of the way over and there was no
  // moment at which he could be said to LAND. He fell, and then he was down.
  //
  // Now he is flat at `DOWN` and the rest of the beat is the arrival. With
  // MATE_FALL at 1.9 seconds that is 1.5 to go over and 0.4 to come to rest,
  // and the arrival is the part the eye actually catches: a thing that stops
  // moving is a thing that has finished, and a thing that stops and then moves
  // twice more, less each time, is a thing that fell.
  const DOWN = clear ? 0.78 : 0.86;
  // Slow to leave and then gravity — he hesitates, and then he goes. Steeper
  // than the 1.6 it was, because over 1.9 seconds the old curve spent the
  // middle of the fall drifting; a topple is the opposite of a drift.
  const tip = Math.pow(clamp01(u / DOWN), 1.9);
  const land = clamp01((u - DOWN) / (1 - DOWN));
  // NEVER PAST FLAT, which is why this is an absolute sine and always negative:
  // what stopped him is the board, and a rock that overshoots the other way is
  // a king whose crown goes through the table. Two swings — six and a half
  // degrees and then under two — damped out by the end of the beat.
  //
  // A king who only got as far as his own men has not landed on anything and
  // does not bounce — he leans into them, and gets one small settle against
  // them instead. See AND HE DOES NOT ALWAYS REACH THE TABLE above.
  const rock = clear
    ? -Math.abs(Math.sin(land * Math.PI * 2)) * 0.115 * (1 - land)
    : -Math.sin(land * Math.PI) * 0.045;
  const angle = flat * tip + rock;

  // A solid of revolution lying down rests on the widest part of it, so the axis
  // ends up one base radius off the table — the same arithmetic `layDown` does,
  // and taken off the SINE of the angle so a king who only gets halfway over is
  // only halfway up. The small lift on top of it is the pivot: he is going over
  // an edge of his own base, not sinking through it.
  const rest = FORM.k.base;
  // HOW FAR HIS CENTRE TRAVELS. A piece going over an edge of its own base
  // covers about a base radius, which is 0.38 for a king, and 0.34 was under
  // even that: he ended up lying half on the square he started on, which from a
  // camera that has not moved reads as a king standing at an angle rather than a
  // king who has gone over. At 0.46 he finishes plainly IN the square he fell
  // into — and his crown, a square and a half further on, is still inside the
  // square `fallWay` cleared for it, so the extra travel cannot put him through
  // anything. A propped king keeps the short slide: he is resting against a man,
  // and sliding into him is the one thing that would give the lean away.
  const slide = clear ? 0.46 : 0.30;
  mesh.position.set(
    at.x + way.x * slide * tip,
    TOP + (rest - TOP) * Math.sin(angle) + 0.09 * Math.sin(Math.PI * tip),
    at.z + way.z * slide * tip,
  );
  const axis = new THREE.Vector3(0, 1, 0).cross(way).normalize();
  mesh.quaternion.setFromAxisAngle(axis, angle);
}
/**
 * The room falls away, and the board is what is left.
 *
 * The last beat, and the only one that touches anything outside the mated king's
 * own square. The fill, the ambient and the environment the set is reflecting all
 * drop back; the KEY DOES NOT, because the key is what models the men and puts a
 * shadow under each of them, and a closing shot that cannot be read is not a
 * closing shot. What that leaves is the same board lit by one lamp in a darker
 * room, which is where a game like this one was actually played.
 *
 * Nothing here touches the writing: the overlays are unlit basic materials hung
 * off the camera, so the card, the score strip and the note are exactly as bright
 * on the last frame as on the first.
 */
function dimRoom(ctx, shot) {
  const p = ctx.params;
  const lights = ctx.lights;
  if (!lights) return;
  const u = shot ? smoothstep(shot.dim) : 0;
  // Not to nothing. At zero the black men go to silhouettes and the board loses
  // its far edge, which is a different picture rather than a quieter one.
  const room = 1 - 0.55 * u;
  lights.fill.intensity = lights.fillPower * room;
  lights.ambient.intensity = lights.ambientPower * room;
  ctx.scene.environmentIntensity = p.reflection * room;
  // The key comes down a little, but only a little: it is doing the work.
  lights.key.intensity = lights.keyPower * (1 - 0.18 * u);
  return room;
}

/**
 * Build what the glow is drawn with, once.
 *
 * One set for the whole scene rather than one per king: only one king can be in
 * check at a time — a rule of the game, not an assumption — so there is never a
 * second of these to want. It belongs to no particular game either, which is why
 * it is built at setup and left alone across a re-bake, unlike the strikes.
 */
function buildKingHeat(ctx) {
  const { THREE, scene } = ctx;
  const dot = (ctx.sparkSprite ||= sparkTexture(THREE));

  const add = (obj, order) => {
    obj.visible = false;
    obj.renderOrder = order;
    scene.add(obj);
    return obj;
  };

  // The halo: what the king is seen THROUGH rather than what he is lit by, and
  // on the white king it is very nearly the whole effect. Emissive lifts a dark
  // piece off the board and does little to a cream one — there is not much above
  // cream to go — so on White's king the surface can only tint, and a tinted king
  // is a king somebody has painted pink. What says LIGHT is the air around him.
  const haloMat = new THREE.SpriteMaterial({
    map: dot, color: 0xffffff, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  const halo = add(new THREE.Sprite(haloMat), 19);

  // A pool of light on the square he is standing on, for the overhead shots —
  // the same argument the strike's shockwave ring is there for. It outlives the
  // glow at a mate: when everything else has gone out, a little warmth is left
  // on the square he fell in.
  const poolMat = new THREE.MeshBasicMaterial({
    color: 0xffffff, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    toneMapped: false,
  });
  const pool = add(new THREE.Mesh(new THREE.CircleGeometry(0.62, 40), poolMat), 17);
  pool.rotation.x = -Math.PI / 2;

  // One light, and no shadow: a second shadow map in every frame of the closing
  // shot for the sake of an edge nobody is looking at, and the shadow the key
  // already throws is the one that says which square he is on.
  //
  // IT IS TURNED DOWN RATHER THAN TURNED OFF. How many point lights are visible
  // is a #define in every lit material in the scene, so a light that comes and
  // goes rebuilds the shader for every man on the board twice per check — a
  // stutter on the exact frame a mate lands, which is the one frame in the video
  // that must not stutter. Held visible at intensity 0 it costs a multiply by
  // nothing per lit pixel and the programs are built once, at setup, like
  // everything else here.
  const light = new THREE.PointLight(0xff8a30, 0, 3.2, 2);
  light.castShadow = false;
  scene.add(light);

  // THREE OF THESE ARE THE PAGES', not the scene's.
  //
  // `loop` is how long a turn of the breath is, published rather than written
  // down twice, because what depends on it depends on the two numbers agreeing.
  //
  // `phase` is seconds ADDED to that clock for the next frame drawn, and it is
  // the answer to a problem neither the video nor a live game has but both
  // websites do: a page that is not playing draws one frame and holds it, and a
  // glow that does not move does not read as a glow — it reads as a piece
  // somebody has painted a different colour, which is what stepping onto a check
  // looked like. So a page parked on one turns this instead of turning the
  // playhead: position, camera and writing all stay exactly where they were.
  //
  // IT IS WRITTEN FOR ONE RENDER AND PUT BACK, the way `placeView` in
  // `watch/player.js` writes one entry of the camera track and puts it back. The
  // restore is not tidiness: a frame asked for BY INDEX — by the scrub bar, by
  // `capture.js`, by `scripts/check-player.mjs` comparing the website against the
  // video — has to be the frame the video has at that index, and it is, because
  // outside that one render this is 0.
  //
  // `lit` is the answer coming back: whether the frame just drawn had a glow on
  // it that is still MOVING. A page has no other way to know, and it is what
  // tells a paused one whether it has any reason to keep drawing. The closing
  // shot deliberately does not set it — that runs on the frame index like the
  // rest of the game, and the two pages give it room off `ctx.closingShot`.
  ctx.heat = { loop: FIRE_LOOP, phase: 0, lit: false, halo, haloMat, pool, poolMat, light };
}

/**
 * Draw what is happening to the king this frame.
 *
 * Runs AFTER `drawPieces`, and depends on having done so: the glow is hung off
 * the king's MESH rather than off his square, so it goes with him while he walks
 * out of check, it is in the right place during the frames he is in the air, and
 * it follows him down when he is mated.
 */
function drawKingHeat(ctx, view, t) {
  const { THREE } = ctx;
  const p = ctx.params;
  const heat = ctx.heat;
  if (!heat) return;

  const state = p.glow > 0 ? kingHeatAt(ctx, view, t) : null;
  const amount = state ? clamp01(state.amount) * p.glow : 0;
  const mesh = state ? state.king.meshes.k : null;
  const lit = Boolean(amount > 0.002 && mesh && mesh.visible);
  // A check breathes and wants a page parked on it to keep drawing. The closing
  // shot moves too, but it moves with the FRAME, so it is the timeline's job.
  heat.lit = lit && !state.mate;

  // The ember left on the square a mated king fell in, once everything else has
  // gone out. It is the one thing that outlives the glow, and it is why the eye
  // still knows where to land on a board the room has dropped away from.
  const shot = mateShot(ctx, t);
  // Gated on the king actually going over, for the same reason the fall is: it is
  // the mark left where he landed, and in a drawn game nobody lands anywhere.
  //
  // OFF `MATE_FALL` RATHER THAN OFF NUMBERS OF ITS OWN. It comes up under him as
  // he goes over — a little before the fall starts, full as he lands — and that
  // is the whole specification, so it is written as that. Held as 0.9 and 1.2 it
  // was full at 2.1s, which was the moment he landed when the fall took 1.1
  // seconds; lengthening the fall left the ember finished and glowing under a
  // king still on his way down.
  const EMBER_LEAD = 0.65;
  const ember = shot && shot.topple
    ? 0.09 * smoothstep(clamp01(
      (shot.since - MATE_FALL[0] + EMBER_LEAD) / (MATE_FALL[1] - MATE_FALL[0] + EMBER_LEAD),
    ))
    : 0;

  // Dimmed rather than hidden, and hidden only when there is to be no glow at all
  // — see the light in `buildKingHeat`.
  heat.light.visible = p.glow > 0;
  heat.light.intensity = 0;
  if (!lit && ember <= 0) {
    heat.halo.visible = false;
    heat.pool.visible = false;
    return;
  }

  const at = mesh ? mesh.position : squarePos(shot.king.at[ctx.game.plies.length]);
  const tint = new THREE.Color(p.glowColour);
  // The clock the breath runs on: the frame's own time plus whatever a parked
  // page has asked for. The catching and the going out are NOT on it — those are
  // tied to the move that landed and the move answering it, and a page turning
  // the breath over is not playing the game on.
  const ph = ((t + heat.phase) / FIRE_LOOP) * Math.PI * 2;
  const beat = 0.84 + 0.16 * Math.sin(ph * 2);

  // ---- the king's own surface ------------------------------------------
  // Copied off whichever material `drawPieces` has just handed him rather than
  // kept as one of its own, so a glowing king is still made of the wood, the
  // marble or the glass the panel asked for — the same man, lit from inside.
  // Nothing has to be put back: `drawPieces` writes `mesh.material` on every
  // frame unconditionally.
  const hot = mesh && ctx.hotMats[p.finish] && ctx.hotMats[p.finish][state.king.color];
  if (hot) {
    hot.copy(mesh.material);
    hot.emissive.setRGB(
      tint.r + (1 - tint.r) * 0.12,
      tint.g + (1 - tint.g) * 0.12,
      tint.b + (1 - tint.b) * 0.12,
    );
    hot.emissiveIntensity = amount * beat * 0.46;
    mesh.material = hot;
  }

  // ---- the halo and the pool -------------------------------------------
  const haloSize = 1.75 * (0.94 + 0.06 * beat);
  heat.halo.visible = amount > 0.002;
  heat.haloMat.opacity = 0;
  if (heat.halo.visible) {
    heat.halo.position.set(at.x, at.y + 0.80, at.z);
    heat.halo.scale.set(haloSize, haloSize, 1);
    heat.haloMat.color.copy(tint);
    heat.haloMat.opacity = clamp01(amount * beat * 0.30);
  }

  const glowPool = amount * beat * 0.17;
  heat.pool.visible = true;
  heat.pool.position.set(at.x, TOP + 0.005, at.z);
  heat.pool.scale.setScalar(0.62);
  heat.poolMat.color.copy(tint);
  heat.poolMat.opacity = clamp01(Math.max(glowPool, ember));

  // ---- the light --------------------------------------------------------
  heat.light.position.set(at.x, at.y + 0.75, at.z);
  heat.light.color.copy(tint);
  heat.light.intensity = Math.max(amount * beat * 2.6, ember * 8);
}

// -------------------------------------------------------------------- timing

/**
 * When everything happens — see `timeline.js`, which owns the arithmetic.
 *
 * It lives in its own module because `scripts/make-publish-sheet.mjs` writes
 * YouTube chapter timestamps out of the same figures, and two copies of this
 * would be two copies that drift.
 */
/** The cold open applies to the whole game only, and never inside a reel. */
function coldOpenOn(p) {
  return !!p.coldOpen && !p.reel && !(Math.round(p.clip || 0) > 0);
}

function timelineFor(ctx, game) {
  const p = ctx.params;
  if (p.reel) {
    return reelTimeline(ctx.g, game.plies, {
      duration: ctx.duration, emphasis: p.emphasis, travel: p.travel,
    });
  }
  if (coldOpenOn(p)) {
    return coldOpenTimeline(ctx.g, game.plies, {
      duration: ctx.duration, emphasis: p.emphasis, travel: p.travel,
      opener: ctx.g.ytTitle ? p.opener : 0,
      needs: p.analysis ? analysisNeeds(ctx.g, game) : null,
      story: !!p.story,
    });
  }
  const span = clipSpan(ctx.g, p.clip);
  const tl = buildTimeline(ctx.g, game.plies.length, {
    duration: ctx.duration, emphasis: p.emphasis, from: span.from, to: span.to,
    // The intro is the title's hold plus the settle after it, so the dial that
    // sets the hold is an input to the timeline and not only to what is drawn.
    // Zero where there is no title to hold: a game with no `ytTitle` opens on the
    // board, and the lead-in it wants is the settle alone. See `openerHold`.
    opener: ctx.g.ytTitle ? p.opener : 0,
    travel: p.travel,
    slow: !!p.slow,
    needs: p.analysis ? analysisNeeds(ctx.g, game) : null,
    story: !!p.story,
  });
  // Said, not fixed: the length is the engine's, and quietly stretching it would
  // be a video of a length nobody set. Only when it is the pace being asked for.
  if (p.slow && tl.lengthNeeded > tl.length + 0.5) {
    console.warn(`Slow moves: at ${p.travel}s a move, some are cut short. `
      + `Set Length to ${Math.ceil(tl.lengthNeeded)}s to give every move its full travel.`);
  }
  return tl;
}

/**
 * The ply range one `clip` setting means, and the phase it came from.
 *
 * A phase in `games.js` records only the ply it runs THROUGH, because the one it
 * starts on is the ply after the previous phase's last — the same derivation
 * `buildTimeline` does for `phaseSpan` and `chess-app` does for its chapter
 * marks. Out of range, including 0, is the whole game: there is no error case
 * here on purpose, because this is read by a slider that does not know how many
 * phases the loaded game has.
 */
function clipSpan(game, clip) {
  const n = Math.round(clip || 0);
  const whole = { from: 0, to: game.moves.length - 1, phase: null, index: 0 };
  if (n < 1 || n > game.phases.length) return whole;
  const phase = game.phases[n - 1];
  return {
    from: n === 1 ? 0 : game.phases[n - 2].through + 1,
    to: phase.through,
    phase,
    index: n,
  };
}

/**
 * The camera, baked to one value per frame per channel.
 *
 * WHY A TABLE AND NOT A KEYFRAME LOOKUP
 * This began as 206's camera: a list of keys, and per frame, interpolate from
 * the previous key's value to the current one over an ease. That has two faults,
 * and on a board both of them are visible.
 *
 * The first is that the interpolation starts from the previous KEY rather than
 * from where the camera actually is. Those are the same thing only if every ease
 * finishes before the next key arrives, and the moment one does not -- a long
 * swing, a short move, a slider nudged -- the shot JUMPS by whatever was left to
 * travel. Walking the frames in order and starting each segment from the value
 * the track really had removes the case outright, and it is why every ease below
 * can be lengthened freely without checking what it collides with.
 *
 * The second was worse, and was the reported one: the aim point was a step
 * function of the move index. `aim = midpoint of the move being played` is
 * recomputed the instant that index changes, so the look-at snapped up to a
 * square and a third sideways at EVERY change of turn -- forty-five times. Here
 * the aim is a channel like any other, eased over its own slower window, because
 * a pan is not a reframe.
 *
 * AND THEN THE FRAME IS HELD, which is the third fault and the one a viewer
 * reports as "the board will not sit still". Smoothing the aim fixed the SNAP;
 * it did not stop the camera pointing somewhere new forty-five times. There was
 * a key per half-move and every key asked for something -- a phase eight degrees
 * round from the last one, a scripted shot four degrees off home, an aim a square
 * to the left -- so the shot was always mid-gesture, and a board being read is
 * exactly the wrong subject for that. Reading a chessboard means comparing what
 * is there now against what was there a move ago, and a moving frame is what
 * breaks the comparison: the eye spends itself re-finding the squares.
 *
 * So a key is now written only when the shot has stopped serving the board, and
 * `Hold the frame` is how much of a change that takes. Everything else is
 * dropped, not softened: no key, no gesture, the camera sits still. What survives
 * is the swing to the mating side, the scripted shots big enough to be shots
 * rather than nudges, and a re-aim when the play has left the frame it was in.
 *
 * Nothing is clipped or slowed to get there, and the distinction matters: a
 * slower version of forty-five moves is still forty-five moves, and it is worse,
 * because now the camera never arrives either. What makes dropping safe is that
 * the asks are ABSOLUTE rather than incremental -- a phase asks for an elevation,
 * not for a change of one -- so four dropped in a row lose nothing. The fifth
 * still asks for where the fourth would have got to, and the camera goes there in
 * one move when it finally goes. The elevation arc every hand-framed game in
 * `games.js` walks down, 47 to 32 over five phases, arrives as one settle onto
 * the mate instead of four separate creeps toward it.
 *
 * THEN A SLEW LIMIT, THEN SMOOTHING -- which is 203 and 205's conclusion reached
 * a third time. The one big move in the shot list is the swing behind Black for
 * the last phase: 160 degrees of azimuth. At the ease the shot list asks for,
 * that is a whip-pan, so any key whose move exceeds `MAX_SLEW` degrees a second
 * has its ease stretched until it does not, and the same is now done to the aim
 * in squares a second -- holding the frame makes each re-aim rarer and therefore
 * bigger, which is a trade worth making and not one to make silently. Then every
 * channel gets `Steadiness` seconds of box smoothing, which rounds the corner at
 * each end of each ease -- smoothstep arrives with zero velocity, so without it
 * the camera visibly stops and starts between moves. `report()` prints the
 * resulting view-direction angular velocity, because that is the quantity that
 * reads as unsteady: 205's lesson, that it is not the same as how much the camera
 * slows through a corner. It prints the count of camera moves beside it now,
 * because that is the quantity this section is about and the two are independent
 * -- a shot can be perfectly smooth and still never once stop moving.
 *
 * The channels are baked WITHOUT the live parameters in them. `Framing`
 * multiplies the zoom and `Follow the move` multiplies the aim at read time, and
 * scaling a smoothed signal by a constant is the same as smoothing a scaled one.
 * `Drift` is added at read time for the same reason -- it is a sum of sines and
 * already as smooth as anything here.
 */

/** Which phase a half-move is in. Prepared phases always cover the score. */
function phaseAt(phases, k) {
  const at = phases.findIndex((ph) => k <= ph.through);
  return at >= 0 ? at : phases.length - 1;
}

/**
 * Where the play is over the `AIM_WINDOW` half-moves starting at `k`.
 *
 * Toward the moves, not onto them: a board is read as a whole, and a camera that
 * centres every move turns the other sixty-three squares into something that
 * slides about. `Follow the move` scales this at read time.
 */
function aimWindow(plies, k, through) {
  const end = Math.min(through, plies.length - 1, k + AIM_WINDOW - 1);
  let x = 0;
  let z = 0;
  let n = 0;
  for (let i = k; i <= end; i++) {
    const a = squarePos(plies[i].move.from);
    const b = squarePos(plies[i].move.to);
    x += (a.x + b.x) / 2;
    z += (a.z + b.z) / 2;
    n++;
  }
  return n ? { aimX: x / n, aimZ: z / n } : { aimX: 0, aimZ: 0 };
}

/**
 * A HELD VIEW — the five numbers that stand in for the whole shot list.
 *
 * `az,el,zoom,panR,panU`, and the spelling is `chess-app/board-camera.js`'s to
 * the character: the website already writes exactly this into a share link, so an
 * angle found by turning the board on the site is an angle a capture can be given.
 *
 * The clamps are the SCENE's rather than the website's, and they differ in one
 * place worth knowing about: `cameraAngles` holds the elevation to 84 degrees
 * whatever it is handed, so a view that says 87 is a view that draws at 84. It is
 * brought back to 84 here so that what the panel shows, what the URL says and
 * what the camera does are one number rather than three.
 *
 * EXPORTED, with `formatHeld`, for `studio-camera.js` — the hand the studio page
 * puts on the board. The reading and the writing of this value belong to the
 * scene that acts on it, so the studio borrows them rather than keeping a second
 * spelling of the same five numbers.
 */
export function parseHeld(text) {
  const parts = String(text ?? '').split(',').map(Number);
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return null;
  return {
    az: parts[0],
    el: clamp(parts[1], 8, 84),
    // The stops the website's wheel works between. Nothing in the scene minds
    // being zoomed past them; what runs out is the board texture, and 6 is three
    // times magnified. See ZOOM_MIN/ZOOM_MAX in `chess-app/camera.js`.
    zoom: clamp(parts[2] ?? 1, 0.6, 6),
    // Seven squares is enough to bring any corner of the board to the middle of
    // the frame at any zoom, and little enough that the board cannot be lost off
    // the edge of it.
    panR: clamp(parts[3] ?? 0, -7, 7),
    panU: clamp(parts[4] ?? 0, -7, 7),
  };
}

/** The same five numbers, short enough to live in a URL. */
export function formatHeld(view) {
  const tidy = (n, dp) => String(Number(Number(n).toFixed(dp)));
  return [
    tidy(wrapDeg(view.az), 1), tidy(view.el, 1), tidy(view.zoom, 3),
    tidy(view.panR, 2), tidy(view.panU, 2),
  ].join(',');
}

/**
 * Write a held view over every frame of a baked track.
 *
 * THE AIM IS LEFT ALONE, and that is the one decision here. The channels a hand
 * takes are where the camera STANDS; where it looks is still the shot list's, so
 * a held view keeps following the play by however much `Follow the move` says —
 * which is what stops a fixed angle on a long game becoming a photograph of an
 * empty corner. Dial Follow and Drift to zero for a shot that is truly nailed
 * down.
 *
 * `steady` goes to 1 because a hand is not a composed shot: see the note on it in
 * `placeCamera`, and `steadyFit` for the fit it selects. It is what stops the
 * board swelling and shrinking as the angle turns, and it is the reason the
 * studio's drag and the capture that follows it frame the same picture.
 *
 * The stamp is cleared rather than set: these channels no longer say what any
 * parameter says, so `update` must re-bake the moment the hand lets go.
 */
function holdView(track, view) {
  track.az.fill(view.az);
  track.el.fill(view.el);
  track.zoom.fill(view.zoom);
  track.panR.fill(view.panR);
  track.panU.fill(view.panU);
  track.steady.fill(view.steady ?? 1);
  track.moves = 0;
  track.held = null;
}

function buildCameraTrack(ctx, game) {
  const p = ctx.params;
  const tl = ctx.timeline;
  const phases = ctx.g.phases;
  // A flat board projects `sin(elevation)` as tall as it is wide, so the shape
  // it makes on screen is chosen by how high the camera is -- and which shape
  // fills the frame is chosen by the frame. In landscape a low camera is both
  // the better picture and the better fit; in portrait the same shot leaves the
  // board a band across a third of the height, so every key is lifted. One
  // number rather than a second set of shots, because the shots are about WHERE
  // the action is and that does not change with the aspect ratio.
  const tall = ctx.height > ctx.width ? 17 : 0;
  // The camera a phase gets when it did not choose one: off the squares its own
  // moves land on, and off the side the mate happens at. A phase that DID choose
  // still wins. See `framing.js` for the whole of it, and why a game should not
  // have to carry this by hand.
  const framed = phaseFraming(phases, game.plies, game.plies.length ? ctx.net.king : null);

  const keys = [{
    t: 0, az: -9, el: clamp(p.tilt + 2 + tall, 12, 80), zoom: 0.94,
    ease: 1, aimEase: 1, aimX: 0, aimZ: 0,
  }];

  // Where the camera is going, as against what the shot list is asking for.
  // Every ask below is compared against THIS rather than against the previous
  // ask, which is what makes dropping one lossless: the next ask is absolute and
  // is still asking for the same place.
  let live = { az: keys[0].az, el: keys[0].el, zoom: keys[0].zoom, aimX: 0, aimZ: 0 };

  game.plies.forEach((ply, k) => {
    const at = phaseAt(phases, k);
    const shot = p.shots ? ctx.g.notes[k]?.shot : null;
    const want = {
      az: shot?.az ?? framed[at].az,
      el: clamp((shot?.el ?? (framed[at].el + p.tilt - 42)) + tall, 12, 80),
      zoom: shot?.zoom ?? 1,
      ...aimWindow(game.plies, k, phases[at].through),
    };

    // ---- hold the frame --------------------------------------------------
    // Channel by channel, because they are asked for separately: a shot worth a
    // push in but not a turn should get the push and not the turn.
    //
    // The FIRST half-move is exempt from all of it. That key is the opening
    // settle — the one the intro below stretches over the title card — and it is
    // a move that is happening whatever the dial says, so it should arrive at
    // what was asked for rather than at the nearest thing to the opening key.
    // Held to the dial it kept the opening key's deliberately wide 0.94 for the
    // whole video, which is the board six per cent smaller for three minutes to
    // save a push-in nobody would have noticed.
    if (k > 0) {
      if (Math.abs(wrapDeg(want.az - live.az)) < p.hold * HOLD.az) want.az = live.az;
      if (Math.abs(want.el - live.el) < p.hold * HOLD.el) want.el = live.el;
      if (Math.abs(want.zoom - live.zoom) < p.hold * HOLD.zoom) want.zoom = live.zoom;
      if (Math.hypot(want.aimX - live.aimX, want.aimZ - live.aimZ) < p.hold * HOLD.aim) {
        want.aimX = live.aimX;
        want.aimZ = live.aimZ;
      }
      // Nothing asked for that survived, so nothing is written and the camera
      // holds. No key, no gesture.
      if (want.az === live.az && want.el === live.el && want.zoom === live.zoom
        && want.aimX === live.aimX && want.aimZ === live.aimZ) return;
    }

    keys.push({
      t: tl.start[k] - tl.moveSec[k] * 0.5,
      ...want,
      // A scripted shot is a cut in intent and a move in fact: a second of
      // travel where a jump would read as a mistake, several seconds where the
      // camera is only breathing.
      ease: shot ? Math.max(0.9, tl.slot[k] * 0.45) : Math.max(1.6, tl.slot[k] * 0.9),
      // The aim always takes longer than the framing does. It is a pan across
      // the subject rather than a move around it, and the eye follows it.
      aimEase: Math.max(1.4, tl.slot[k] * 0.95),
    });
    live = want;
  });

  // A score with no moves in it, which is where `chess-app/play/` starts every
  // game and a state no video has ever been in: there is no first move to bring
  // the opening key forward to and no mate to pull up off, so the track is the
  // one opening key held flat. Everything below reads the same either way.
  if (game.plies.length) {
    // The intro is one long move rather than a hold: the key for 1.e4 is brought
    // forward to just after the title lands and given the whole intro to ease
    // into, so the shot settles onto the board as the card finishes being read.
    // Left where the loop put it, the camera would sit dead still for ten seconds
    // and then start moving at the moment the first piece did.
    keys[1].t = tl.intro * 0.18;
    keys[1].ease = Math.max(2, tl.intro * 0.72);
    keys[1].aimEase = keys[1].ease;

    // The last shot pulls up and back: the mating net is a picture of five squares
    // round the mated king, and it is only a picture from above. It inherits the
    // aim it is handed, so the pull-up is the only thing moving.
    //
    // Square on to the end the mate happened at, which is `closingAz`'s whole
    // job. This was the constant 180 -- right for the seven games in `games.js`
    // that finish on Black's back rank, and a hundred and eighty degrees away
    // from the net it is meant to be showing for the three that finish on
    // White's.
    const last = game.plies.length - 1;
    const tail = keys[keys.length - 1];
    keys.push({
      t: tl.start[last] + tl.slot[last] + Math.min(4, tl.hold * 0.25),
      az: closingAz(ctx.net.king),
      el: clamp(58 + (p.tilt - 42) * 0.3 + tall * 0.5, 20, 82), zoom: 1.0,
      ease: Math.max(3, tl.hold * 0.35),
      aimEase: Math.max(3, tl.hold * 0.35),
      aimX: tail.aimX, aimZ: tail.aimZ,
    });
  }

  for (let i = 1; i < keys.length; i++) {
    // Azimuth is UNWRAPPED into one continuous signal, each key taking the short
    // way round from the one before. Smoothing a bearing that wraps at 180 would
    // average -179 and 179 to nothing and swing the camera through White's side
    // of the board to get between two shots that are next to each other.
    keys[i].az = keys[i - 1].az + wrapDeg(keys[i].az - keys[i - 1].az);
    // The slew limit, applied to the EASE rather than to the result, so the move
    // stays one eased gesture instead of being clipped part way through.
    const swing = Math.max(
      Math.abs(keys[i].az - keys[i - 1].az),
      Math.abs(keys[i].el - keys[i - 1].el),
    );
    keys[i].ease = Math.max(keys[i].ease, swing / MAX_SLEW);
    // And the same for the aim, in squares a second rather than degrees.
    keys[i].aimEase = Math.max(keys[i].aimEase, Math.hypot(
      keys[i].aimX - keys[i - 1].aimX,
      keys[i].aimZ - keys[i - 1].aimZ,
    ) / MAX_AIM_SLEW);
  }

  // ---- bake, in order, each segment starting where the last one got to -----
  const n = ctx.totalFrames + 1;
  const az = new Float32Array(n);
  const el = new Float32Array(n);
  const zoom = new Float32Array(n);
  const aimX = new Float32Array(n);
  const aimZ = new Float32Array(n);

  let ki = 0;
  let from = { az: keys[0].az, el: keys[0].el, zoom: keys[0].zoom, aimX: 0, aimZ: 0 };
  for (let f = 0; f < n; f++) {
    const t = f / ctx.fps;
    while (ki + 1 < keys.length && keys[ki + 1].t <= t) {
      ki++;
      if (f > 0) {
        from = {
          az: az[f - 1], el: el[f - 1], zoom: zoom[f - 1], aimX: aimX[f - 1], aimZ: aimZ[f - 1],
        };
      }
    }
    const key = keys[ki];
    const u = smoothstep((t - key.t) / Math.max(1e-6, key.ease));
    const v = smoothstep((t - key.t) / Math.max(1e-6, key.aimEase));
    az[f] = from.az + (key.az - from.az) * u;
    el[f] = from.el + (key.el - from.el) * u;
    zoom[f] = from.zoom + (key.zoom - from.zoom) * u;
    aimX[f] = from.aimX + (key.aimX - from.aimX) * v;
    aimZ[f] = from.aimZ + (key.aimZ - from.aimZ) * v;
  }

  // Three box passes of half-width r approximate a Gaussian of sigma ~ r + 0.5
  // frames, so the radius comes straight off `Steadiness` in seconds.
  const r = Math.max(0, Math.round(p.steady * ctx.fps - 0.5));
  const track = {
    n,
    // How many times the camera does anything, which is what `Hold the frame` is
    // for and the number `report()` prints. Not read while drawing: the count of
    // keys is not recoverable from the baked channels, and it is the figure to
    // watch when a game is added or the dial is moved.
    moves: keys.length,
    az: boxSmooth(az, r),
    el: boxSmooth(el, r),
    zoom: boxSmooth(zoom, r),
    aimX: boxSmooth(aimX, r),
    aimZ: boxSmooth(aimZ, r),
    // WHERE A PANNED VIEW LIVES, and it is baked flat because the video never
    // pans: no shot in this scene slides the frame off the board, and there is
    // no key anywhere above that writes these. They are here so a WEBSITE can
    // hold a view the way it already holds an azimuth — by writing the entry
    // for the frame about to be drawn and putting it back after, which is what
    // `placeView` in `watch/player.js` and `draw` in `play/stage.js` do
    // with the three above. Zeroes for every frame anybody captures.
    //
    // In squares, along the camera's own right and up rather than along the
    // world's: a pan is a movement of the FRAME, so it has to mean the same
    // thing whichever way round the board is being looked at.
    panR: new Float32Array(n),
    panU: new Float32Array(n),
    // Whether the camera is in a hand rather than on the shot list, blended 0 to
    // 1 so a takeover settles instead of jumping. Baked flat for the same reason
    // the pan is: no video is ever hand-held — unless `Held view` says otherwise,
    // which is the one case where a video IS shot from where a hand left the
    // camera. See `steadyFit`, and `holdView` just above.
    steady: new Float32Array(n),
  };

  // ---- and then a held view throws all of it away ------------------------
  // Last, over the finished channels, rather than as a key at the top: a held
  // view is not a shot the track eases into, it is the absence of a shot list.
  // Smoothing a constant would only give the constant back.
  const held = parseHeld(p.held);
  if (held) holdView(track, held);
  // What the channels were baked against, so `update` can tell when the dial has
  // moved out from under them. `holdView` clears it; this is after that on
  // purpose.
  track.held = p.held;
  return track;
}

/**
 * Box blur along a signal, `passes` times, edges held rather than wrapped.
 *
 * Held because this is a timeline: frame 0 is the opening position and the last
 * frame is the mate, and there is nothing on the far side of either to average
 * with.
 */
function boxSmooth(src, radius, passes = 3) {
  if (radius < 1) return src;
  const n = src.length;
  const w = radius * 2 + 1;
  const at = (arr, i) => arr[i < 0 ? 0 : i >= n ? n - 1 : i];
  let a = Float32Array.from(src);
  let b = new Float32Array(n);
  for (let pass = 0; pass < passes; pass++) {
    let sum = 0;
    for (let i = -radius; i <= radius; i++) sum += at(a, i);
    for (let i = 0; i < n; i++) {
      b[i] = sum / w;
      sum += at(a, i + radius + 1) - at(a, i - radius);
    }
    const swap = a; a = b; b = swap;
  }
  return a;
}

/**
 * Where the camera is pointing on frame `f`, in degrees.
 *
 * The one place the baked track and the live `Drift` are combined, so that
 * `placeCamera` and the sway measurement in `report()` cannot disagree about
 * what the shot actually does.
 */
function cameraAngles(ctx, f) {
  const p = ctx.params;
  const cam = ctx.camTrack;
  const t = f / ctx.fps;
  // A little life, off the clock rather than off the cut list, so it never lines
  // up with anything and never repeats.
  return {
    az: cam.az[f] + p.drift * 2.6 * Math.sin(t * 0.31) + p.drift * 1.2 * Math.sin(t * 0.74 + 1.3),
    el: clamp(cam.el[f] + p.drift * 1.4 * Math.sin(t * 0.26 + 2.1), 8, 84),
  };
}

/**
 * How fast the view direction turns, in degrees a second: median, 99th
 * percentile and worst frame over the whole timeline.
 *
 * 205's lesson, and the reason this is measured rather than eyeballed: the
 * quantity that reads as unsteady is the angular velocity of the DIRECTION the
 * camera looks, not how far it travels or how much it slows through a corner.
 * The view direction here is exactly the azimuth and elevation, since the camera
 * always looks at its aim point.
 *
 * `shunt` is the second measurement and it is the one that catches the bug this
 * function was written for. An aim that jumps does not turn the camera at all —
 * it slides the whole board sideways in frame, so it is invisible to any angular
 * measure however carefully that is taken. It needs its own number: how far the
 * aim point travels in a frame, in squares a second.
 */
function cameraSway(ctx) {
  const cam = ctx.camTrack;
  const p = ctx.params;
  const rates = [];
  let px = 0, py = 0, pz = 0;
  let shunt = 0;
  for (let f = 0; f < cam.n; f++) {
    const { az, el } = cameraAngles(ctx, f);
    const ar = az * Math.PI / 180;
    const er = el * Math.PI / 180;
    const x = Math.cos(er) * Math.sin(ar);
    const y = Math.sin(er);
    const z = Math.cos(er) * Math.cos(ar);
    if (f > 0) {
      rates.push(Math.acos(clamp(x * px + y * py + z * pz, -1, 1)) * 180 / Math.PI * ctx.fps);
      shunt = Math.max(shunt, Math.hypot(
        cam.aimX[f] - cam.aimX[f - 1],
        cam.aimZ[f] - cam.aimZ[f - 1],
      ) * p.follow * 0.55 * ctx.fps);
    }
    px = x; py = y; pz = z;
  }
  rates.sort((a, b) => a - b);
  const at = (q) => rates[Math.min(rates.length - 1, Math.floor(rates.length * q))] ?? 0;
  return { median: at(0.5), p99: at(0.99), peak: rates[rates.length - 1] ?? 0, shunt };
}

/**
 * What is on screen at time `t`.
 *
 * The two ends are the clip's, not the game's. On a whole-game render they are
 * the same thing — `first` is 0 and `last` is the mate — and this reads exactly
 * as it did before. On a clip, "before the first move" is the position the range
 * starts FROM, already set up with no animation, which is the whole trick: the
 * men are where they are and nothing has to replay to get them there.
 */
function viewAt(ctx, t) {
  const tl = ctx.timeline;
  const plies = ctx.game.plies;
  const first = tl.first ?? 0;
  const last = tl.last ?? plies.length - 1;

  if (t < tl.intro) {
    return { phase: 'intro', ply: first - 1, k: first - 1, frac: 1, moving: false, done: first };
  }
  if (t >= tl.gameEnd) {
    return { phase: 'done', ply: last, k: last, frac: 1, moving: false, done: last + 1 };
  }
  let k = last;
  while (k > first && tl.start[k] > t) k--;
  const frac = clamp01((t - tl.start[k]) / tl.moveSec[k]);
  return { phase: 'play', ply: k, k, frac, moving: frac < 1, done: k + (frac >= 1 ? 1 : 0) };
}

// -------------------------------------------------------------------- render

/**
 * Put every piece where it is at this instant.
 *
 * Three cases: standing on a square, travelling between two of them, or lying
 * beside the board. The travelling one is the only one with any shape to it —
 * a knight hops, because a knight's move is the one move that cannot be made by
 * sliding a piece across a board without lifting it over something.
 */
/**
 * Where a move's path is at `prog` of the way along it, and which way it is
 * heading there: `{ x, z, hx, hz }`.
 *
 * A KNIGHT GOES ROUND THE L, the same L its arrow draws (see `paintArrow`): the
 * long leg, the turn, the short leg, at one steady share of the progress per
 * square, so the corner is passed without a pause and the whole trip takes the
 * time a straight one did. One by two is only ever a knight.
 *
 * Its own function because the ghosts walk the same path the piece does — see
 * `drawGhosts` — and a trail that cut the corner a knight goes round would be a
 * second answer to where the move went.
 */
function movePath(a, b, knight, prog) {
  if (!knight) {
    return { x: a.x + (b.x - a.x) * prog, z: a.z + (b.z - a.z) * prog, hx: b.x - a.x, hz: b.z - a.z };
  }
  const corner = Math.abs(b.x - a.x) === 2 ? { x: b.x, z: a.z } : { x: a.x, z: b.z };
  const along = prog * 3;
  if (along <= 2) {
    return {
      x: a.x + (corner.x - a.x) * along / 2,
      z: a.z + (corner.z - a.z) * along / 2,
      hx: corner.x - a.x,
      hz: corner.z - a.z,
    };
  }
  return {
    x: corner.x + (b.x - corner.x) * (along - 2),
    z: corner.z + (b.z - corner.z) * (along - 2),
    hx: b.x - corner.x,
    hz: b.z - corner.z,
  };
}

/**
 * When a move was `y` of the way along its path: `smoothstep` turned round.
 *
 * The ghosts are spaced along the PATH rather than along the clock — that is
 * what "per square" means — so each one has to know when the piece passed the
 * point it stands on. Smoothstep is 3y² − 2y³, whose inverse over 0..1 is this
 * one line.
 */
function unsmooth(y) {
  return 0.5 - Math.sin(Math.asin(1 - 2 * clamp01(y)) / 3);
}

/**
 * When a lifted move sets off across the board and when it arrives, as shares of
 * a move of `moveSec` seconds: `[g0, g1]`.
 *
 * The piece rises for `LIFT.up` of the move, then hangs there for `LIFT.pause`
 * seconds before it goes anywhere. That pause is a real second rather than a
 * share of the move, so it reads the same at every speed — which means the
 * window has to be worked out per move rather than written down, and everything
 * that follows the piece has to ask the same question: the ghosts it drops on
 * the way (`drawGhosts`) and the strike it lands on a capture (`drawStrikes`).
 */
function liftWindow(moveSec) {
  const start = Math.min(LIFT.startMax, LIFT.up + LIFT.pause / Math.max(1e-6, moveSec));
  return [start, LIFT.glideEnd];
}

/**
 * A piece LIFTED rather than slid: where it is `u` of the way through its move.
 *
 * Four beats. It rises off its square; it HANGS there for a second, held up
 * where it can be seen, which is the beat a hand takes between picking a piece
 * up and deciding; it glides across, leaning into the direction of travel by how
 * fast it is going, the way a piece carried in a hand tips forward; and it comes
 * down onto the new square, with a small rebound and a rock back upright as it
 * settles. The glide is a smoothstep across its window, so it leaves and arrives
 * gently whatever the move's length.
 *
 * Returns `{ along, rise, lean }`: how far across (0 to 1, for the path to
 * place), how high off the board in squares, and the lean in radians, positive
 * being the top of the piece tipped the way it is going.
 */
function liftedMove(u, height, moveSec) {
  const [g0, g1] = liftWindow(moveSec);
  const k = clamp01((u - g0) / (g1 - g0));
  const along = smoothstep(k);
  // How fast it crosses, as a share of the fastest it gets: smoothstep's slope
  // is 6k(1 − k), which peaks at 1.5 halfway.
  const speed = (6 * k * (1 - k)) / 1.5;

  const up = 1 - (1 - clamp01(u / LIFT.up)) ** 2;
  const [d0, d1] = LIFT.down;
  const down = clamp01((u - d0) / (d1 - d0)) ** 2;
  let rise = height * up * (1 - down);
  // Touched down at `d1`; the rebound and the rock fill the rest.
  const after = clamp01((u - d1) / (1 - d1));
  if (after > 0 && after < 1) rise += LIFT.bounce * Math.sin(Math.PI * after);
  const lean = LIFT.lean * speed + LIFT.settle * Math.sin(2 * Math.PI * after) * (1 - after);
  return { along, rise, lean };
}

/** How high a lifted move is carried: a knight over what is in its way. */
function liftHeight(a, b, knight) {
  return knight ? LIFT.knight : LIFT.height + LIFT.perSquare * Math.hypot(b.x - a.x, b.z - a.z);
}

/**
 * Tip `mesh` into a heading of `(hx, hz)` by `lean`: turning about (hz, 0, −hx)
 * by a positive angle carries the top of the piece towards (hx, 0, hz). Its
 * rotation is set first, and this turns it from there.
 */
function leanInto(ctx, mesh, hx, hz, lean) {
  if (!lean) return;
  const len = Math.hypot(hx, hz) || 1;
  ctx.leanAxis ||= new ctx.THREE.Vector3();
  ctx.leanTurn ||= new ctx.THREE.Quaternion();
  ctx.leanAxis.set(hz / len, 0, -hx / len);
  mesh.quaternion.premultiply(ctx.leanTurn.setFromAxisAngle(ctx.leanAxis, lean));
}

function drawPieces(ctx, view, t) {
  const p = ctx.params;
  const plies = ctx.game.plies;
  const tl = ctx.timeline;
  const ply = view.ply >= 0 ? plies[view.ply] : null;
  const settled = view.ply >= 0 ? view.ply + (view.moving ? 0 : 1) : 0;

  const mats = p.finish === 'glass' ? ctx.glassMats
    : p.finish === 'marble' ? ctx.marbleMats
      : ctx.mats;
  if (p.finish === 'glass') {
    // `tint` reads as "how deeply coloured", and what it sets is a DISTANCE,
    // shorter being stronger — geometrically, because absorption is a power law
    // and a linear slider over it spends most of its travel doing nothing. At 0
    // it takes seven squares of glass to reach the set's colour, which is
    // water-clear whatever that colour is; at 1 it takes a quarter of a square,
    // which is a piece the light does not get through. Measured on a black pawn
    // over a lit square, that range runs 213 to almost nothing, and the middle
    // of it is a smoked-glass man that still shows the board.
    //
    // One slider for both halves rather than two that would only ever be moved
    // together: the light men are nearly white, and nearly white absorbs nearly
    // nothing at any distance, so this is felt almost entirely on the dark half
    // of the set — which is the half that has to stay dark to be read.
    const dist = 0.25 * Math.pow(30, 1 - p.tint);
    ctx.glassMats.w.attenuationColor.set(ctx.set.white);
    ctx.glassMats.b.attenuationColor.set(ctx.set.black);
    for (const mat of [ctx.glassMats.w, ctx.glassMats.b]) {
      mat.attenuationDistance = dist;
      mat.roughness = p.frost;
    }
  } else if (p.finish === 'marble') {
    ctx.marbleMats.w.color.set(ctx.set.white);
    // Not `set()`: the dark stone is painted below full so its veins have room to
    // be lighter than its body, and this is the other half of that — see
    // `marbleShade` and `darkStone`. A colour too light to carry it is cut from
    // the pale stone instead, and swapping the map is a pointer either way: both
    // pictures are built at setup and both have the same shape, so nothing here
    // recompiles a shader.
    const cut = darkStone(ctx.marbleMats.b.color, ctx.set.black) ? ctx.stone.dark : ctx.stone.light;
    if (ctx.marbleMats.b.map !== cut.map) {
      ctx.marbleMats.b.map = cut.map;
      ctx.marbleMats.b.bumpMap = cut.bump;
    }
    for (const mat of [ctx.marbleMats.w, ctx.marbleMats.b]) {
      // `Roughness` still does what it says, over the range stone has. Handed
      // straight through it spends most of its travel on surfaces marble does not
      // have — 0.46, the default a wooden set wants, is a matt chalky block, and
      // anything above about 0.5 is unpolished limestone. Remapped, the slider
      // runs from mirror-polished to honed and the default lands on a set that
      // has been waxed, which is what a marble set in a shop looks like.
      mat.roughness = 0.06 + p.roughness * 0.34;
      // The clear coat carries the polish, so the body under it can go as matt as
      // it likes without the piece ever looking dry.
      mat.clearcoatRoughness = 0.04 + p.roughness * 0.16;
      mat.metalness = p.metalness;
    }
  } else {
    ctx.mats.w.color.set(ctx.set.white);
    ctx.mats.b.color.set(ctx.set.black);
    for (const mat of [ctx.mats.w, ctx.mats.b]) {
      mat.roughness = p.roughness;
      mat.metalness = p.metalness;
    }
  }

  const ease = smoothstep(view.frac);

  // WHICH LINE EACH MAN GETS, and it is not "White's is the dark one". The line
  // is there to separate a man from what is behind him, so it has to contrast
  // with the MAN: a light line round an ivory bishop is the same value as the
  // bishop and reads as a glow he is wearing rather than as his outline. So the
  // split is on how light each half of the set actually IS, measured, rather than
  // on which side of the board it plays.
  //
  // That distinction is not academic here, because the set is a control. All ten
  // named sets have a pale White and a dark Black and would come out the same
  // either way — but `board: custom` is five colour pickers, and somebody who
  // dials a near-black White wants the light line on him.
  //
  // Relative luminance, in linear light, so the threshold sits near the middle of
  // the sRGB ramp rather than the middle of the numbers. The two halves of a real
  // set are nowhere near it: the boxwood men measure 0.78 and 0.014.
  const inkWhite = paleMan(ctx.set.white);
  const inkBlack = paleMan(ctx.set.black);

  // THE FLAT MEN'S DRAWING IS IN THE OTHER SIDE'S COLOUR, which is how a printed
  // diagram does it and what ties the drawing to the set: dark lines on the pale
  // men, pale lines on the dark ones. When a set makes both halves the same side
  // of the middle, the line falls back to plain ink so it is never lost on its
  // own man.
  const flat = p.dimension === '2d';
  if (flat) {
    const onWhite = inkWhite === inkBlack ? (inkWhite ? '#1a1916' : '#f3eee3') : ctx.set.black;
    const onBlack = inkWhite === inkBlack ? (inkBlack ? '#1a1916' : '#f3eee3') : ctx.set.white;
    for (const ink of Object.values(ctx.flatInk)) {
      if (ink.fixed) continue;
      ink.mats.w.color.set(onWhite);
      ink.mats.b.color.set(onBlack);
    }
  }
  const flatYaw = flat && flatFlip(ctx) ? Math.PI : 0;

  for (const actor of ctx.actors) {
    if (flat) actor.faceY = flatYaw;
    const type = formAt(actor, settled);
    for (const [key, mesh] of Object.entries(actor.meshes)) mesh.visible = key === type;
    const mesh = actor.meshes[type];
    // Wood or glass. Written every frame rather than when the switch moves,
    // because a live parameter has no hook that fires when it does.
    mesh.material = mats[actor.color];
    // Only the mesh being drawn: the other forms of a man who promotes are
    // invisible, and three does not traverse the children of what it is not
    // drawing. The one that becomes visible is wrapped on the frame it does.
    ctx.outline.wrap(mesh, (actor.color === 'w' ? inkWhite : inkBlack) ? 'dark' : 'light');

    // Taken. The fall runs off the CLOCK rather than off the move's progress,
    // which it did first: a struck piece has to be allowed to still be tumbling
    // after the move that struck it has finished, and tying it to `view.frac`
    // capped the whole thing at the half-second the capturing piece was in the
    // air for. The strike carries the timing now, so both agree by construction.
    if (actor.captured >= 0 && actor.captured <= view.ply) {
      const strike = ctx.strikeByPly.get(actor.captured);
      const u = actor.captured < view.ply || !strike
        ? 1
        : clamp01((t - strike.t0) / strike.fall);
      layDown(ctx, mesh, actor, type, u);
      if (!p.tray && u >= 1) mesh.visible = false;
      continue;
    }

    const base = view.ply < 0 ? 0 : view.ply;
    const from = actor.at[base];
    const to = actor.at[base + 1] ?? from;

    // The castling rook travels with its king, on the same clock.
    const isMover = ply && (actor.id === ply.mover || actor.id === ply.rook?.id);
    if (!view.moving || !isMover || from === to || from < 0) {
      // Before the game starts there is no "after" to settle into: the intro
      // holds the opening position, not the position after 1.e4.
      const sq = view.ply < 0 || view.moving ? from : to;
      if (sq < 0) { mesh.visible = false; continue; }
      const at = squarePos(sq);
      mesh.position.set(at.x, TOP, at.z);
      mesh.rotation.set(0, actor.faceY, 0);
      continue;
    }

    const a = squarePos(from);
    const b = squarePos(to);
    const hop = formAt(actor, settled) === 'n' ? 0.42 : 0.10 + 0.02 * Math.hypot(b.x - a.x, b.z - a.z);
    // A KNIGHT GOES ROUND THE L, the same L its arrow draws (see `paintArrow`):
    // the long leg, the turn, the short leg, at one steady share of the move's
    // ease per square, so the corner is passed without a pause and the whole trip
    // takes the time a straight one did. One by two is only ever a knight.
    const knight = Math.abs(b.x - a.x) * Math.abs(b.z - a.z) === 2;
    // Lifted, the path is walked at the glide's pace and the height is its own;
    // otherwise both run off the move's ease, as they always have.
    const lifted = p.lift && !flat
      ? liftedMove(view.frac, liftHeight(a, b, knight), tl.moveSec[view.ply])
      : null;
    const { x, z, hx, hz } = movePath(a, b, knight, lifted ? lifted.along : ease);
    mesh.rotation.set(0, actor.faceY, 0);
    if (!lifted) {
      mesh.position.set(x, TOP + Math.sin(Math.PI * ease) * hop, z);
      continue;
    }
    mesh.position.set(x, TOP + lifted.rise, z);
    leanInto(ctx, mesh, hx, hz, lifted.lean);
  }

  // LAST, and after every man has been put where he stands, because this puts
  // one of them somewhere else: the mated king goes over on his own square, and
  // the loop above has just stood him up on it. See `toppleKing`.
  const shot = mateShot(ctx, t);
  // Not on the flat board: a token tipped onto its edge is a line seen from above,
  // and the king would simply vanish from the diagram at the moment he loses.
  if (shot && shot.topple && shot.fall > 0 && !flat) toppleKing(ctx, shot, shot.fall);
}

/**
 * A taken piece, `u` of the way from standing on its square to lying in the tray.
 *
 * It is KNOCKED, not lifted, and the difference is all in the easing. The first
 * version eased both the travel and the tip with a smoothstep, which is a hand
 * picking a piece up and putting it down — correct, polite, and completely at
 * odds with a spray of sparks going the other way. Now it leaves fast and
 * arrives slow, goes over inside the first third of the trip with a little
 * overshoot at the end of the tip, and turns as it goes. What settles in the
 * tray is a piece that was hit.
 */
function layDown(ctx, mesh, actor, type, u) {
  const from = actor.at[actor.captured];
  const a = from >= 0 ? squarePos(from) : { x: 0, z: 0 };
  const b = trayPos(actor);
  // Fast out, slow in — but not as fast out as it was: at 2.4 a struck queen was
  // halfway to the tray three tenths of a second after the strike, ahead of her
  // own sparks.
  const e = 1 - Math.pow(1 - clamp01(u), 1.9);
  // Higher and earlier than a lift would be: the peak of the arc wants to be
  // over the board, where the sparks are, not halfway to the tray.
  const lift = Math.sin(Math.PI * Math.pow(e, 0.78)) * (0.5 + 0.055 * VALUE[type]);
  // Lying down, a solid of revolution rests on the widest part of it — so the
  // axis ends up one base radius off the table, not on it.
  const flat = ctx.params.dimension === '2d';
  // The tray is on the TABLE, which is the slab's depth below the board. A turned
  // man's number is where its axis ends up; a flat one lies on the table itself,
  // or it hovers there and its shadow lands a man's width away.
  const rest = flat ? ctx.table.position.y + 0.002 : formOf(ctx.params, type).base;
  mesh.position.set(
    a.x + (b.x - a.x) * e,
    TOP + lift + (rest - TOP) * e,
    a.z + (b.z - a.z) * e,
  );
  // A flat man is already lying down: it is thrown, and lands the right way up
  // and the right way round, so the row of taken men still reads as a row.
  if (flat) {
    mesh.rotation.set(0, actor.faceY, 0);
    return;
  }
  // Over inside the first third, and past ninety degrees before it comes back —
  // a struck piece rocks as it lands rather than arriving flat.
  const tip = smoothstep(clamp01(u / 0.34));
  mesh.rotation.x = -(Math.PI / 2) * tip * (1 + 0.11 * Math.sin(Math.PI * tip));
  // And it turns. Which way is a property of the piece, so the same piece always
  // falls the same way; a knight keeps its facing as the base of that turn.
  mesh.rotation.y = actor.faceY + (actor.spin || 0) * e;
  mesh.rotation.z = 0;
}

/**
 * Where the camera stands, read off the baked track.
 *
 * Azimuth 0 is behind White, which is where the board is legible from for most
 * of the game; the last phase is at 168-180, behind Black, because the last four
 * moves happen on the squares in front of the black king and no angle from
 * White's side shows them.
 */
function placeCamera(ctx, t) {
  const p = ctx.params;
  const cam = ctx.camTrack;
  const f = clamp(Math.round(t * ctx.fps), 0, cam.n - 1);
  if (p.dimension === '2d') return placeFlatCamera(ctx);

  const { az, el } = cameraAngles(ctx, f);
  const zoom = cam.zoom[f] * p.zoom;
  const follow = p.follow * 0.55;

  const ar = az * Math.PI / 180;
  const er = el * Math.PI / 180;
  // The board sits above centre: the writing lives along the bottom of the frame.
  //
  // EXCEPT IN THE PANEL LAYOUT, where it does not — the writing is in a column
  // beside the board, and the board's region has nothing else in it, so the board
  // belongs in the middle of it. Nothing like as far up as -0.8, then, and not
  // zero either: projecting `FIT_POINTS` at the elevations the shot list uses
  // leaves 0.01–0.07 of ndc more room above the board than below it at zero, and
  // the eye reads the difference. -0.25 is where that balances across 32 to 46
  // degrees, and it errs high on purpose — the fit's ceiling is a king's crown at
  // the board's BACK CORNERS, where no piece in these games ever stands, so the
  // tallest thing actually drawn sits some way below the room reserved for it.
  //
  // The same number holds for both shapes the region has had. Re-measured for
  // 1280 x 1080 after the column was set to a third of the width, -0.25 leaves at
  // most 0.05 of ndc out of balance across the whole shot list, against 0.05 for
  // the square it replaced — the region got wider, not taller, so what changed is
  // how much of the height the board fills, from about two thirds to over four
  // fifths.
  const lookY = ctx.layout?.panel ? -0.25 : (ctx.height > ctx.width ? -1.1 : -0.8);
  const aim = { x: cam.aimX[f] * follow, y: lookY, z: cam.aimZ[f] * follow };
  const dir = new ctx.THREE.Vector3(
    Math.cos(er) * Math.sin(ar),
    Math.sin(er),
    Math.cos(er) * Math.cos(ar),
  );
  // A SHOT IS FRAMED FOR ITS ANGLE; A HAND IS NOT. `steady` is 0 on every frame
  // of every video and 1 whenever a visitor is holding the camera, and the two
  // fits are blended rather than switched so that taking the board over is a
  // settle rather than a jump — the websites ease the channel, and see
  // `steadyFit` for what it is and why it exists at all.
  const steady = cam.steady ? cam.steady[f] : 0;
  const shot = fitDistance(ctx, dir, aim);
  const base = steady > 0
    ? shot + (steadyFit(ctx, el, aim) - shot) * clamp01(steady)
    : shot;
  const dist = base / zoom;

  // THE PAN IS APPLIED AFTER THE FIT, AND THAT IS THE WHOLE OF WHY IT WORKS.
  // `fitDistance` answers "how far back to stand for the board to be in frame",
  // which it works out FROM THE AIM POINT — so a pan folded into the aim before
  // it would be a pan the fit immediately argues with: slide the frame off the
  // board and the camera would walk backwards to bring the board back, undoing
  // half the pan and changing the zoom while it did. Standing back is decided
  // by the shot; the pan is then a plain TRANSLATION of the whole camera, eye
  // and target together, which moves the frame and leaves the framing alone.
  //
  // The basis is the one `fitDistance` projects into, so a screen edge means the
  // same thing to both.
  const right = new ctx.THREE.Vector3().crossVectors(new ctx.THREE.Vector3(0, 1, 0), dir).normalize();
  const up = new ctx.THREE.Vector3().crossVectors(dir, right).normalize();
  const shift = right.multiplyScalar(cam.panR[f]).addScaledVector(up, cam.panU[f]);

  // From the aim point, not from the aim point's shadow on the table: dropping
  // aim.y here would tilt the view a degree or two off the axis the fit below
  // assumes, which is exactly the margin the fit is working with.
  ctx.camera.position.copy(dir).multiplyScalar(dist)
    .add(new ctx.THREE.Vector3(aim.x, aim.y, aim.z)).add(shift);
  ctx.camera.lookAt(aim.x + shift.x, aim.y + shift.y, aim.z + shift.z);
  ctx.camera.updateMatrixWorld();
}

/**
 * The 2D board's camera: straight overhead, and nowhere else, ever.
 *
 * "Orthographic, from above" is what was asked for, and this is a PERSPECTIVE
 * camera with a six-degree lens standing ninety-odd units up, for one reason:
 * everything that is hung on `ctx.camera` — the writing planes, and every
 * project/unproject in `chess-app/camera.js` that turns a tap into a square — is
 * written for a perspective camera, and swapping the class would have been a
 * second copy of all of it. At six degrees nothing on the board can tell the
 * difference: the board's corner is under three degrees off the axis, so the
 * side of a man a tenth of a unit tall shows as half a thousandth of a unit.
 *
 * It ignores the track completely — shots, drift, follow, zoom, a visitor's
 * hand — which is what "cannot be rotated, panned or moved" means. The one
 * thing it reads from the track is WHICH END the viewer was at, and only when a
 * visitor chose it: a player who is Black on the play pages has the camera held
 * at Black's end, and a flat board seen from White's would put their own men at
 * the top. The scripted shots on the watch page never turn it, because they swing
 * to Black's end for the last phase of a game and a diagram that spins round
 * with three moves to go is not a diagram. See `flatFlip`.
 *
 * Fitted to the board and its frame rather than to the men, which lie flat and
 * add nothing to it, in the region the board is given — the left of the frame in
 * the panel layout, all of it otherwise.
 */
function placeFlatCamera(ctx) {
  const region = ctx.layout?.board ?? { w: ctx.width, h: ctx.height };
  const aspect = region.w / region.h;
  const margin = ctx.layout?.panel ? 1.08 : 1.04;
  const half = (BOARD / 2) * margin;
  const tanV = Math.tan(ctx.camera.fov * Math.PI / 360);
  const dist = Math.max(half / tanV, half / (tanV * aspect));
  const flip = flatFlip(ctx);
  // The board stands lower in a tall studio frame — see `FLAT_DROP`. Screen-up is
  // -z from White's end and +z from Black's, so the camera moves the other way.
  const drop = STUDIO && aspect < 1 ? FLAT_DROP : 0;
  const z = flip ? drop : -drop;
  ctx.camera.up.set(0, 0, flip ? 1 : -1);
  ctx.camera.position.set(0, TOP + dist, z);
  ctx.camera.lookAt(0, TOP, z);
  ctx.camera.updateMatrixWorld();
}

/**
 * Whether the flat board is seen from Black's end: a visitor is holding the view
 * (`steady`, which no scripted shot ever sets) and holding it from that side.
 */
function flatFlip(ctx) {
  const cam = ctx.camTrack;
  const f = clamp(ctx.frame, 0, cam.n - 1);
  if (!cam.steady || !(cam.steady[f] > 0.5)) return false;
  return Math.cos(cam.az[f] * Math.PI / 180) < 0;
}

/**
 * How far back the camera has to stand for the whole board to be in frame.
 *
 * Fitting a SPHERE round the board — the usual trick, and what 206 does — is
 * wrong here and wrong by a lot: a chessboard is flat, so at 40 degrees of
 * elevation its 11.6-unit diagonal projects to about six units tall, and a
 * camera placed to hold a 11.6-unit ball leaves the board a stamp in the middle
 * of the frame. So the eight corners are projected properly and the distance is
 * whichever of them needs the most room.
 *
 * For a camera at `aim + d*dir` the lateral offset of a point does not depend on
 * d at all, and its depth is `d + q·dir`, so the constraint per point and per
 * axis is just `d >= |lateral| / tan(halfAngle) + q·dir` — one max, no solving.
 *
 * The trays are deliberately NOT in the fit. Framing for six captured pawns
 * lying beside the board would shrink the board itself by a fifth; the heaps are
 * allowed to sit at the edge of the frame and sometimes past it.
 */
function fitDistance(ctx, dir, aim, points = FIT_POINTS) {
  const { THREE } = ctx;
  const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), dir).normalize();
  const up = new THREE.Vector3().crossVectors(dir, right).normalize();
  // THE REGION, NOT THE FRAME. In the panel layout the board owns a square at
  // the left and the writing owns the rest, so what the fit has to hold the
  // board inside is that square — `ctx.layout.board` is it, and it is the whole
  // frame in the other two layouts. `ctx.layout` is set in setup; the `??` is
  // for the frame `run()` draws during its own call, before setup has returned.
  const region = ctx.layout?.board ?? { w: ctx.width, h: ctx.height };
  // A PANEL FRAME NEEDS THE LOOSEST MARGIN OF THE THREE, which is the opposite of
  // what it looked like at first: nothing is written over its board region, so it
  // was set tighter than landscape's 1.06 to make use of that. What the tighter
  // fit ran into is the TRAYS. They are deliberately not in `points` — framing
  // for six captured pawns lying beside the board would shrink the board itself
  // by a fifth — and they sit BESIDE the board, on the axis a two-thirds-width
  // region is short of. At 1.04 the heaps in a game with sixteen men off ran off
  // the left edge and up against the column's seam on the right; 1.12 costs the
  // board about seven per cent and puts both of them back inside the frame. The
  // extreme is still the extreme — 25 captures in Carlsen–Nepomniachtchi is more
  // men than any margin fits — and that is the bargain `FIT_POINTS` describes.
  const margin = ctx.layout?.panel ? 1.12 : (ctx.width >= ctx.height ? 1.06 : 1.02);
  const tanV = Math.tan(ctx.camera.fov * Math.PI / 360) / margin;
  const tanH = tanV * (region.w / region.h);
  const q = new THREE.Vector3();
  let d = 1;
  for (const [px, py, pz] of points) {
    q.set(px - aim.x, py - aim.y, pz - aim.z);
    const along = q.dot(dir);
    d = Math.max(d, Math.abs(q.dot(right)) / tanH + along, Math.abs(q.dot(up)) / tanV + along);
  }
  return d;
}

/**
 * The distance that holds the board at EVERY azimuth, for the elevation given.
 *
 * WHY A HAND NEEDS A DIFFERENT FIT FROM A SHOT.
 * `fitDistance` answers "how far back for this exact view", and re-answering it
 * every frame is right for a camera the scene is driving: each authored angle is
 * framed as tightly as it can be. Under a HAND it is a bug you can feel. A
 * chessboard is square, so turning it from face-on to a diagonal presents 11.3
 * units of width where there were 8 — and the fit walks the camera backwards to
 * keep up. Measured: dragging through 45 degrees dollies from 15.3 units out to
 * 19.8, and the board SHRINKS by 27 per cent on the way. Nobody asked the camera
 * to move; they asked to turn the board. It reads as the view zooming itself.
 *
 * WHY NOT SIMPLY FREEZE THE DISTANCE.
 * Tried, measured, no good: held at the face-on fit, a turn to 45 degrees pushes
 * the corner squares 16 per cent past the edge of the frame. On a chessboard
 * that is a rook you can no longer see. A constant radius is what every other 3D
 * viewer does and it is fine for objects that are roughly as wide as they are
 * deep; a flat square is the case it is worst for.
 *
 * So the fit is made INVARIANT rather than frozen: the worst case over a whole
 * turn, at this elevation, over `HELD_POINTS`. Turning then moves nothing at
 * all, because there is nothing left for the azimuth to change. It costs about a
 * seventh of the board's size face-on against what a shot would frame — and it
 * gives a tenth of it back at the diagonals, where a shot pulls further out than
 * this does.
 *
 * Elevation is still fitted honestly, per frame, because tilting SHOULD change
 * the framing — a board seen from overhead really does present more of itself —
 * and because the distance only moves eight per cent across the whole range of
 * it, which reads as a tilt rather than as a zoom.
 *
 * A quarter turn is the whole sweep: the squares and the nine crown points are
 * symmetric under a quarter turn, so azimuths past 90 degrees can only repeat
 * what the first quarter already found.
 */
function steadyFit(ctx, el, aim) {
  const { THREE } = ctx;
  const er = (el * Math.PI) / 180;
  const dir = new THREE.Vector3();
  let worst = 1;
  for (let i = 0; i < STEADY_STEPS; i++) {
    const ar = ((i / (STEADY_STEPS - 1)) * Math.PI) / 2;
    dir.set(Math.cos(er) * Math.sin(ar), Math.sin(er), Math.cos(er) * Math.cos(ar));
    worst = Math.max(worst, fitDistance(ctx, dir, aim, HELD_POINTS));
  }
  return worst;
}

// --------------------------------------------------------------- the board top

/** Texture pixel for a point on the board, in board units. */
function boardPixel(x, z) {
  return [((x + BOARD / 2) / BOARD) * TEX, ((z + BOARD / 2) / BOARD) * TEX];
}

/** Texture pixel at the centre of a square. */
function squarePixel(sq) {
  const at = squarePos(sq);
  return boardPixel(at.x, at.z);
}

/**
 * Repaint the board top — but only when what is drawn on it has changed, which
 * is once per half-move rather than once per frame.
 */
function paintBoard(ctx, view, t) {
  const p = ctx.params;
  // A ply under `ctx.quietPlies` is not marked. Unset in the videos; the Learn
  // page sets it to the length of the score that SETS UP a lesson, because those
  // moves are scaffolding — an arrow on the last of them points at a black knight
  // in the middle of a lesson about the rook. See `quietSetup` in `table.js`.
  const ply = view.ply >= (ctx.quietPlies || 0) ? ctx.game.plies[view.ply] : null;
  const landed = ply && !view.moving;
  // THE NET COMES UP WITH THE MATE, not with the phase.
  //
  // It used to wait for `view.phase === 'done'`, which is the end of the last
  // move's SLOT rather than the end of its travel — and the last move of a game
  // is the most heavily weighted one there is, so that is seconds after the piece
  // arrives. Fine for a net that appears whole and holds; useless for one that
  // closes, because the closing is timed off the moment the mate lands and would
  // have played out on a board that was not showing it yet. So the shot opens the
  // net as well, and the phase still does it on its own when the shot is off.
  const shot = p.net ? mateShot(ctx, t) : null;
  // Only where the clip runs to the end of the game: the net is drawn off the
  // FINAL position, and a clip that stops earlier — a phase, a reel — would have
  // it painted round a king who is no longer on those squares.
  const toEnd = (ctx.timeline.last ?? ctx.game.plies.length - 1) === ctx.game.plies.length - 1;
  const showNet = p.net && ((view.phase === 'done' && toEnd) || Boolean(shot));
  // How far it has closed, or the whole thing at once with the closing shot off.
  //
  // WHAT THIS COSTS, since it is the one thing in the scene that repaints the
  // board every frame instead of once a half-move. Measured: the 2D drawing is
  // under a millisecond and irrelevant, and the whole of it is the UPLOAD — 2048
  // squared of RGBA, sixteen megabytes, handed to the GPU per frame for the 1.4
  // seconds the net takes to close.
  //
  // Which is affordable because of how the beats are ORDERED. Nothing in three
  // dimensions moves while the net is closing: the king does not start to go over
  // until 1.55s, which is the frame the net finishes on, and the room does not
  // start to drop until later still. So the one stretch of the video that might
  // drop frames is the one stretch with no motion in it to judder — a static
  // camera over a board nobody is touching, with a diagram closing on it. The
  // beat that has to be smooth is the fall, and by then this is stable and the
  // board is not being repainted at all.
  //
  // Quantised into the key rather than compared raw, so it is at most fifty
  // uploads however many frames the beat is drawn at.
  const closing = shot ? shot.net : 1;

  const set = ctx.set;
  // Which end a flat board is read from decides which two edges carry the
  // coordinates, so it is part of what the picture is. Null in 3D.
  const flatEnd = p.dimension === '2d' ? (flatFlip(ctx) ? 'b' : 'w') : null;
  const key = [
    view.ply, view.moving, showNet, Math.round(closing * 50), ctx.quietPlies || 0,
    p.squares, p.arrow, p.coords, set.light, set.dark, set.frame, flatEnd, p.forces,
  ].join('|');
  if (ctx.board.last === key) return;
  ctx.board.last = key;

  const g = ctx.board.g2;
  g.save();
  g.clearRect(0, 0, TEX, TEX);

  // ---- frame and squares ------------------------------------------------
  g.fillStyle = set.frame;
  g.fillRect(0, 0, TEX, TEX);
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const [px, py] = squarePixel(file + rank * 8);
      g.fillStyle = (file + rank) % 2 === 1 ? set.light : set.dark;
      g.fillRect(Math.round(px - PX / 2), Math.round(py - PX / 2), Math.ceil(PX), Math.ceil(PX));
    }
  }
  // A hairline inside the frame, which is what stops the eight-by-eight reading
  // as a pattern printed on a slab.
  g.strokeStyle = 'rgba(0,0,0,0.45)';
  g.lineWidth = PX * 0.02;
  g.strokeRect(BORDER * PX, BORDER * PX, 8 * PX, 8 * PX);

  if (p.coords) drawCoordinates(g, set, flatEnd);

  // ---- who controls what --------------------------------------------------
  // The position on the board: after the move once it has landed, before it
  // while the man is still in the air.
  if (p.forces && p.forces !== 'off') {
    const at = view.ply < 0 ? 0 : landed || (ply === null && view.ply >= 0) ? view.ply + 1 : view.ply;
    const shown = ctx.game.positions[Math.min(at, ctx.game.positions.length - 1)];
    paintForces(g, shown.board, p.forces);
  }

  // ---- the move ---------------------------------------------------------
  if (ply && p.squares) {
    paintSquare(g, ply.move.from, 'rgba(255,238,170,0.30)');
    // A capture's destination is warmer than a quiet move's, so the square the
    // strike happened on is not the same yellow as the square a pawn stepped to.
    const took = ply.move.capturedSq >= 0;
    paintSquare(g, ply.move.to, took
      ? (landed ? 'rgba(255,150,95,0.52)' : 'rgba(255,150,95,0.28)')
      : (landed ? 'rgba(255,226,120,0.46)' : 'rgba(255,226,120,0.24)'));
    if (took && ply.move.capturedSq !== ply.move.to) {
      paintSquare(g, ply.move.capturedSq, 'rgba(255,120,90,0.40)');
    }
    if (ply.check && !ply.mate) {
      const after = ctx.game.positions[ply.index + 1];
      paintCheck(g, after.kingSquare(after.turn), 'rgba(255,90,70,0.55)');
    }
  }
  if (ply && p.arrow) paintArrow(g, ply.move.from, ply.move.to, landed ? 0.5 : 0.28);

  // ---- why it is mate ---------------------------------------------------
  if (showNet) paintNet(g, ctx.net, closing, set);

  g.restore();
  ctx.board.tex.needsUpdate = true;
}

/** File letters and rank numbers on all four edges, each the right way up for
 *  the side it is read from — the camera spends the last quarter behind Black.
 *
 *  EXCEPT ON THE FLAT BOARD, which is read from one end only and never turns:
 *  there it is a diagram's two edges, letters along the bottom and numbers up the
 *  left, all upright — `flatEnd` says whose bottom. Four edges of labels on a
 *  page, two of them upside down, is a board photographed from above rather than
 *  a diagram. */
function drawCoordinates(g, set, flatEnd = null) {
  const size = PX * 0.30;
  g.font = `600 ${Math.round(size)}px system-ui, Arial, sans-serif`;
  // Off the light squares rather than a fixed cream, so the letters stay legible
  // on a near-black frame and stop shouting on a pale one.
  g.fillStyle = `${set.light}a0`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const edge = BORDER * PX * 0.5;
  if (flatEnd) {
    // The texture's top is Black's end; seen from Black it is all turned round.
    const turn = flatEnd === 'b';
    const label = (text, x, y) => {
      g.save();
      g.translate(x, y);
      if (turn) g.rotate(Math.PI);
      g.fillText(text, 0, 0);
      g.restore();
    };
    for (let file = 0; file < 8; file++) {
      label(FILES[file], squarePixel(file)[0], turn ? edge : TEX - edge);
    }
    for (let rank = 0; rank < 8; rank++) {
      label(String(rank + 1), turn ? TEX - edge : edge, squarePixel(rank * 8)[1]);
    }
    g.textAlign = 'left';
    g.textBaseline = 'alphabetic';
    return;
  }
  for (let file = 0; file < 8; file++) {
    const [px] = squarePixel(file);
    g.fillText(FILES[file], px, TEX - edge);
    g.save();
    g.translate(px, edge);
    g.rotate(Math.PI);
    g.fillText(FILES[file], 0, 0);
    g.restore();
  }
  for (let rank = 0; rank < 8; rank++) {
    const [, py] = squarePixel(rank * 8);
    g.save();
    g.translate(edge, py);
    g.rotate(-Math.PI / 2);
    g.fillText(String(rank + 1), 0, 0);
    g.restore();
    g.save();
    g.translate(TEX - edge, py);
    g.rotate(Math.PI / 2);
    g.fillText(String(rank + 1), 0, 0);
    g.restore();
  }
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
}

function paintSquare(g, sq, fill) {
  const [px, py] = squarePixel(sq);
  g.fillStyle = fill;
  g.fillRect(Math.round(px - PX / 2), Math.round(py - PX / 2), Math.ceil(PX), Math.ceil(PX));
}

/**
 * FORCES — who controls each square, painted under the men.
 *
 * Three marks, each a fact counted by `squareControl`:
 *   - a WASH in the colour of the side with more men bearing on the square,
 *     stronger the bigger the majority (one, two, three or more);
 *   - an amber OUTLINE where both sides bear on a square — the squares being
 *     fought over, which is where the next capture usually happens;
 *   - a dashed red RING round a man attacked by more of the other side than
 *     defend it, or attacked and not defended at all. Counting is not a full
 *     exchange (a pawn attacking a queen is worse than the count says), and the
 *     ring claims no more than the count: outnumbered.
 *
 * `mode` 'w' or 'b' shows one side's control, and only the men that side is
 * bearing down on. Painted into the board canvas like everything else here, so
 * it costs one repaint a half-move and looks the same in 3D and on the flat board.
 */
function paintForces(g, board, mode) {
  // Blue for White, red for Black. Declared in here, not beside the function:
  // the scene starts drawing near the top of this module, before a `const` down
  // here has been initialised, and a page that opens with the view already on
  // draws this on its very first frame.
  const FORCE_W = '40,140,255';
  const FORCE_B = '235,70,70';
  const { w, b } = squareControl(board);
  for (let sq = 0; sq < 64; sq++) {
    const cw = mode === 'b' ? 0 : w[sq];
    const cb = mode === 'w' ? 0 : b[sq];
    if (!cw && !cb) continue;
    const net = cw - cb;
    if (net) {
      const alpha = (net > 0 ? 0.2 : 0.16) + 0.13 * (Math.min(3, Math.abs(net)) - 1);
      paintSquare(g, sq, `rgba(${net > 0 ? FORCE_W : FORCE_B},${alpha.toFixed(2)})`);
    }
    if (cw && cb) {
      const [px, py] = squarePixel(sq);
      const inset = PX * 0.08;
      g.strokeStyle = 'rgba(255,196,60,0.85)';
      g.lineWidth = PX * (0.03 + 0.015 * Math.min(3, Math.min(cw, cb)));
      g.strokeRect(px - PX / 2 + inset, py - PX / 2 + inset, PX - inset * 2, PX - inset * 2);
    }
  }
  for (let sq = 0; sq < 64; sq++) {
    const man = board[sq];
    if (!man || typeOf(man) === 'k') continue;
    const side = colorOf(man);
    if (mode !== 'both' && mode === side) continue;
    const own = (side === 'w' ? w : b)[sq];
    const theirs = (side === 'w' ? b : w)[sq];
    if (!theirs || (own && own >= theirs)) continue;
    const [px, py] = squarePixel(sq);
    g.save();
    g.strokeStyle = 'rgba(255,55,55,0.95)';
    g.lineWidth = PX * 0.06;
    g.setLineDash([PX * 0.12, PX * 0.08]);
    g.beginPath();
    g.arc(px, py, PX * 0.4, 0, Math.PI * 2);
    g.stroke();
    g.restore();
  }
}

/** A checked king gets a ring rather than a wash: a filled square under a piece
 *  is mostly hidden by the piece standing on it. */
function paintCheck(g, sq, colour) {
  const [px, py] = squarePixel(sq);
  g.strokeStyle = colour;
  g.lineWidth = PX * 0.075;
  g.beginPath();
  g.arc(px, py, PX * 0.42, 0, Math.PI * 2);
  g.stroke();
}

/**
 * A tapered arrow from one square to another, drawn on the board itself.
 *
 * It lives under the pieces, which is the right place for it: the piece is the
 * subject and the arrow is a note about where it came from. Anything drawn over
 * the top of the men would have to be a 3D object updated every frame, for a
 * mark that does not move once the move has been made.
 */
function paintArrow(g, from, to, alpha) {
  const [x0, y0] = squarePixel(from);
  const [x1, y1] = squarePixel(to);
  const len = Math.hypot(x1 - x0, y1 - y0);
  if (len < 1) return;
  const tail = PX * 0.16;
  const start = PX * 0.30;

  // A KNIGHT'S ARROW IS AN L. A straight one from b1 to c3 says the knight slid
  // along a diagonal it cannot use, and a knight's move is the one move a
  // beginner needs drawn as the shape it is. Two squares along the long leg, then
  // the turn, then one — the way the move is taught. Nothing else moves one by
  // two, so the geometry is enough to know it is a knight.
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  const knight = Math.abs(df) * Math.abs(dr) === 2;
  // The corner, where there is one: level with the target along the long leg.
  const corner = knight ? (Math.abs(df) === 2 ? [x1, y0] : [x0, y1]) : null;

  // The shaft as legs, each a start, a unit direction and a length.
  const bend = corner || [x1, y1];
  const leg = (ax, ay, bx, by) => {
    const l = Math.hypot(bx - ax, by - ay);
    return { ax, ay, ux: (bx - ax) / l, uy: (by - ay) / l, l };
  };
  const first = leg(x0, y0, bend[0], bend[1]);
  const last = corner ? leg(corner[0], corner[1], x1, y1) : first;
  const head = Math.min(PX * 0.42, (corner ? last.l : len) * 0.36);

  // The outline of the whole arrow as one polygon, so the fill and the edge are
  // one shape with no seam at the corner. Left side out to the tip, right side
  // back. At a right angle the two offset edges of the legs meet at the corner
  // offset along BOTH normals, which is the mitre.
  const n = (d) => [-d.uy, d.ux];
  const [n1x, n1y] = n(first);
  const [n2x, n2y] = n(last);
  const h = tail * 0.5;
  const w = head * 0.42;
  const sx = x0 + first.ux * start;
  const sy = y0 + first.uy * start;
  const ex = x1 - last.ux * head;
  const ey = y1 - last.uy * head;
  const pts = [[sx + n1x * h, sy + n1y * h]];
  if (corner) pts.push([corner[0] + (n1x + n2x) * h, corner[1] + (n1y + n2y) * h]);
  pts.push(
    [ex + n2x * h, ey + n2y * h],
    [ex + n2x * w, ey + n2y * w],
    [x1, y1],
    [ex - n2x * w, ey - n2y * w],
    [ex - n2x * h, ey - n2y * h],
  );
  if (corner) pts.push([corner[0] - (n1x + n2x) * h, corner[1] - (n1y + n2y) * h]);
  pts.push([sx - n1x * h, sy - n1y * h]);

  g.save();
  g.fillStyle = `rgba(255,218,95,${Math.min(1, alpha * 1.3)})`;
  g.beginPath();
  pts.forEach(([px, py], i) => (i ? g.lineTo(px, py) : g.moveTo(px, py)));
  g.closePath();
  g.fill();
  // AN OUTLINE, because the fill alone is a pale yellow at half strength, and on
  // the light squares of most sets — cream, boxwood, ivory — that is very nearly
  // the colour already there, so the arrow was only visible where it crossed a
  // dark square. A dark edge shows on both. Brown rather than black, so it reads
  // as the arrow's own rim and not a line drawn round it, and at the fill's
  // strength scaled up, so the fading arrow of a move in progress fades with it.
  // Three times the fill's strength: the fill is 0.28 while a move is under way
  // and 0.5 once it has landed, and even the lower of those has to give an edge
  // that holds on cream.
  g.strokeStyle = `rgba(52,32,6,${Math.min(0.92, alpha * 3)})`;
  g.lineWidth = PX * 0.0325;
  g.lineJoin = 'round';
  g.stroke();
  g.restore();
}

/**
 * The mate, drawn as the argument it is.
 *
 * Red ring on the mated king, a line from each guarding piece to the square it
 * takes away, a cross on every square that is covered, and a bar on the two
 * that are blocked by Black's own men. Every element comes out of `mateNet()`,
 * so this is a picture of the position rather than an illustration of a caption.
 *
 * IT TAKES THE SET BECAUSE HALF THE SQUARES ARE PALE. The cross and the guard
 * rings were one fixed near-white each — 255,215,205 and 255,210,140 — which is
 * a fine mark on walnut and no mark at all on boxwood: the light squares of the
 * twenty-five sets measure 0.58 to 0.84 in luminance against 0.03 to 0.26 for
 * the dark ones, so a cream cross drawn on a light square is a cream cross on
 * cream — 1.2:1 against the square under it, measured — and the argument the
 * whole closing shot exists to make had holes in it wherever the king's flight
 * squares happened to be light. It is 5.7:1 now.
 *
 * `drawCoordinates` already solves this problem by taking its ink off the
 * set rather than naming a colour; this does the same, one square at a time,
 * because a net crosses both colours by construction.
 */
function paintNet(g, net, u = 1, set = null) {
  const [kx, ky] = squarePixel(net.king);

  // Which ink a mark on THIS square is drawn in. The board alternates from a1,
  // so the parity of file plus rank is which of the two colours is underneath,
  // and `paleMan` — relative luminance, the same measure the outlines use — is
  // what decides whether a light mark or a dark one shows on it. No set means
  // the old behaviour: a caller that has not been given one is drawing on
  // something this cannot measure.
  const onPale = (sq) => {
    if (!set) return false;
    const under = (fileOf(sq) + rankOf(sq)) % 2 === 1 ? set.light : set.dark;
    return paleMan(under);
  };

  // EACH SQUARE PUTS ITSELF OUT IN ITS OWN SLOT, in the order `mateNet` walks
  // the king's neighbours, and the ring lands on him once the last one has gone.
  // Drawn all at once — which is what this did, and what it still does with the
  // closing shot off — the net is a diagram of a finished argument. Closed one
  // square at a time it is the argument: here is a square he cannot use, and
  // here is the piece that takes it from him, and here is the next.
  //
  // The slots overlap by half, so it reads as a net drawing tight rather than as
  // a metronome, and they are all done by 0.85 to leave the last of the beat for
  // the ring closing on the king.
  const n = net.squares.length || 1;
  const step = 0.85 / n;
  const shut = (i) => clamp01((u - i * step) / (step * 1.5));
  const closed = clamp01((u - 0.8) / 0.2);

  net.squares.forEach((cell, i) => {
    const k = shut(i);
    if (k <= 0) return;
    const [px, py] = squarePixel(cell.square);
    if (cell.reason === 'own') {
      // A wash under a square that by definition has one of Black's own pieces
      // standing on it is a wash nobody can see. A ring sits round the piece's
      // foot instead, where the base does not cover it.
      g.fillStyle = `rgba(120,170,255,${(0.22 * k).toFixed(3)})`;
      g.fillRect(Math.round(px - PX / 2), Math.round(py - PX / 2), Math.ceil(PX), Math.ceil(PX));
      g.strokeStyle = `rgba(140,185,255,${(0.85 * k).toFixed(3)})`;
      g.lineWidth = PX * 0.06;
      g.beginPath();
      g.arc(px, py, PX * 0.43, 0, Math.PI * 2);
      g.stroke();
      return;
    }
    g.fillStyle = `rgba(255,90,70,${(0.20 * k).toFixed(3)})`;
    g.fillRect(Math.round(px - PX / 2), Math.round(py - PX / 2), Math.ceil(PX), Math.ceil(PX));
    // The line is DRAWN, from the piece towards the square it takes: at a tenth
    // of the way through its slot it has left the guard and not arrived, which is
    // the difference between a diagram of a net and a net being pulled shut.
    for (const guard of cell.by || []) {
      const [gx, gy] = squarePixel(guard);
      g.strokeStyle = `rgba(255,150,110,${(0.55 * k).toFixed(3)})`;
      g.lineWidth = PX * 0.035;
      g.beginPath();
      g.moveTo(gx, gy);
      g.lineTo(gx + (px - gx) * k, gy + (py - gy) * k);
      g.stroke();
    }
    // And the cross lands only once the line has got there.
    const hit = clamp01((k - 0.7) / 0.3);
    if (hit > 0) {
      // Deep red on a light square, near-white on a dark one — the same mark
      // either way, and the red keeps the net's own colour rather than going to
      // a neutral ink that would read as a coordinate.
      g.strokeStyle = onPale(cell.square)
        ? `rgba(92,14,6,${(0.88 * hit).toFixed(3)})`
        : `rgba(255,215,205,${(0.85 * hit).toFixed(3)})`;
      g.lineWidth = PX * 0.055;
      const r = PX * 0.20 * (1.6 - 0.6 * hit);
      g.beginPath();
      g.moveTo(px - r, py - r); g.lineTo(px + r, py + r);
      g.moveTo(px + r, py - r); g.lineTo(px - r, py + r);
      g.stroke();
    }
  });

  // The pieces doing the work, ringed — each as its own line arrives, so the
  // ring is the answer to "what closed that square" rather than a legend.
  for (const guard of net.guards) {
    const k = Math.max(
      ...net.squares.map((cell, i) => ((cell.by || []).includes(guard) ? shut(i) : 0)),
      net.checkers.includes(guard) ? closed : 0,
    );
    if (k <= 0) continue;
    const [px, py] = squarePixel(guard);
    // The checker's ring is red enough to carry on either colour and the blue
    // one round a blocking man is blue enough — both differ from a cream square
    // in HUE, which is what a mark needs when it cannot differ in value. The
    // amber ring on a plain guard did not, so it gets the same treatment as the
    // cross above.
    g.strokeStyle = net.checkers.includes(guard)
      ? `rgba(255,120,95,${(0.95 * k).toFixed(3)})`
      : onPale(guard)
        ? `rgba(122,66,4,${(0.85 * k).toFixed(3)})`
        : `rgba(255,210,140,${(0.80 * k).toFixed(3)})`;
    g.lineWidth = PX * 0.065;
    g.beginPath();
    g.arc(px, py, PX * 0.44, 0, Math.PI * 2);
    g.stroke();
  }

  // And last of all, the square he is standing on.
  if (closed > 0) {
    g.fillStyle = `rgba(255,70,55,${(0.34 * closed).toFixed(3)})`;
    g.fillRect(Math.round(kx - PX / 2), Math.round(ky - PX / 2), Math.ceil(PX), Math.ceil(PX));
  }
}

// ------------------------------------------------------------------------ hud

function drawHud(ctx, view, t) {
  const p = ctx.params;
  const tl = ctx.timeline;
  const plies = ctx.game.plies;
  const game = ctx.g;
  const ply = view.ply >= 0 ? plies[view.ply] : null;
  const note = ply ? game.notes[ply.index] : null;
  // Null for every game that was played to mate at the board, which is most of
  // them — see `codaOf`. Where it is not null, two cards below say so.
  const coda = codaOf(game);

  // ---- the card ---------------------------------------------------------
  let card = null;
  if (view.phase === 'intro') {
    card = {
      kind: 'intro',
      eyebrow: `${game.event} · ${game.date}`,
      title: game.title,
      lead: `${game.white} — ${game.black}`,
      note: `${game.opening}. ${game.blurb}`,
    };
  } else if (view.phase === 'done') {
    const mat = material(ctx.game.positions, plies.length);
    const ending = endingOf(game);
    card = {
      kind: 'done',
      // A game whose finish was published rather than played says so HERE as
      // well as on the move cards, because the closing card is the one a viewer
      // reads twice and the one a still gets taken from. The mate itself is not
      // hedged — it is on the board and `describeNet` proves it below; what the
      // eyebrow is honest about is whose hand made the last twenty half-moves.
      eyebrow: [`${plies.length} half-moves`, game.result,
        coda && `last ${coda.count} not played`].filter(Boolean).join(' · '),
      // What happened, in one word, and it is the one word a viewer takes away
      // from the still. See `endingOf`: a game that ends in mate says Checkmate,
      // one the loser resigned says Resigned, one that was drawn says Drawn.
      title: ending === 'resigned' ? 'Resigned' : ending === 'drawn' ? 'Drawn' : 'Checkmate',
      // Who won comes off the result, not off an assumption that White does.
      // Four of the eighteen games in the list are Black wins, and this line read
      // "Donald Byrne wins" over a board where Byrne had just been mated.
      //
      // And whether the winner was AHEAD or BEHIND is read off the count rather
      // than assumed either: Anderssen finishes the Immortal Game a queen and
      // two rooks down, but Morphy finishes his queen sacrifice up on material,
      // and "a queen down" would be wrong there.
      // A drawn game has no winner to name, so it names what it has instead:
      // what each side gave up getting there. `winnerLine` reads the result to
      // find the winner and there is nobody to find.
      lead: ending === 'drawn' ? drawnLine(mat) : winnerLine(game, mat),
      // Generated from the final position rather than written per game — see
      // `describeNet` and `describeFinish`. The claim the card makes is then
      // checked by construction either way: for a mate it is the net, and for a
      // resignation it is what the last move attacks.
      note: ending === 'mate'
        ? describeNet(ctx.game.final, ctx.net)
        : describeFinish(ctx.game.final, ctx.net),
    };
  } else if (ply) {
    card = {
      kind: 'move',
      // NOT PLAYED rides in the eyebrow for every half-move of a coda, not just
      // its first one. A viewer who joins the video late, or who is looking at a
      // still of move 30, has no way to know the sitting ended at move 25 unless
      // the frame in front of them says it — and one card at the boundary is a
      // card most viewers scroll past. It is in the paint key below, so the
      // overlay repaints on the move that crosses over.
      eyebrow: [`MOVE ${ply.moveNo}`, ply.side === 'w' ? 'WHITE' : 'BLACK',
        coda && ply.index >= coda.from && 'NOT PLAYED'].filter(Boolean).join(' · '),
      title: `${ply.side === 'w' ? `${ply.moveNo}.` : `${ply.moveNo}…`} ${ply.san}`,
      lead: note?.lead || pieceSentence(ctx, ply),
      note: note?.note || '',
    };
  }
  // ---- the opening title -------------------------------------------------
  // It holds the frame to itself: the corner card is suppressed while the title
  // is up, so the first seconds say one thing and the middle of the frame is not
  // competing with the top left of it. The fade is on the MATERIAL rather than in
  // the canvas, because `paintOverlay` repaints only when the text changes — a
  // fade painted into the canvas would be a repaint a frame.
  const open = openerAt(ctx, t);
  paintOverlay(ctx.hudOpen, open, (g, W, H) => drawOpener(g, W, H, open),
    open && [open.eyebrow, open.title, open.lead].join('|'));
  ctx.hudOpen.mat.opacity = open ? open.alpha : 0;

  // 9:16 is a Short or a Reel, and both draw their own controls over the top of
  // the frame. `SAFE_TOP` of the FRAME's height, not the band's — less a little,
  // which lifts the move card and the website mark clear of Black's nameplate
  // over the lowered 2D board (`FLAT_DROP`). The card's eyebrow is what goes
  // under the apps' controls; the move itself stays below them.
  const L = ctx.layout ?? frameLayout(ctx);
  const portrait = L.portrait;
  const shape = { portrait, topInset: portrait ? SAFE_TOP * ctx.height - 50 * (ctx.height / 1920) : 0 };

  // ---- the strip --------------------------------------------------------
  // Assembled before the branch below because both layouts draw it — over the
  // board's near edge in one, down the column in the other.
  const mat = material(ctx.game.positions, view.phase === 'intro' ? 0 : view.done);
  const strip = {
    rows: scoreWindow(plies, view.ply),
    at: view.ply,
    // THE PHASE, INCLUDING BEFORE THE FIRST MOVE. This used to read the game's
    // OPENING during the intro, which was unreachable code as long as the strip
    // was the only reader — it is not drawn during the intro at all — and wrong
    // the moment the column was: the intro card's note already opens with the
    // opening, so the list ended up headed by a line it had just printed. The
    // `find` handles a playhead of -1 by itself, and what it answers is the phase
    // the game is about to start in, which is what a heading over the score
    // should say.
    phase: game.phases.find((ph) => (view.ply < 0 ? 0 : view.ply) <= ph.through).title,
    phases: game.phases,
    // The VIDEO's clock. Under a cold open the board is drawn at a source instant
    // that runs out of order and is shorter than the video by the open and the
    // rewind, so `t` would read 2:38 on the first frame and stop at 2:55.
    clock: clockText(tl.reel ? ctx.frame / ctx.fps : t),
    total: clockText(tl.reel ? tl.reel.duration : tl.length),
    // While the puzzle asks for the move, the score must not print it.
    hideAhead: ctx.reelNow?.kind === 'puzzle',
    // And while it asks, the bar must not say who is winning by how much: a
    // bar at "M4" is the answer to "find the mate" with the working shown.
    eval: ctx.params.evalBar && ctx.reelNow?.kind !== 'puzzle' ? evalAt(ctx, view, t) : null,
    balance: mat.balance,
    whiteLost: mat.whiteLost,
    blackLost: mat.blackLost,
    fill: phaseFill(ctx, t),
  };
  const cardKey = card && [
    // `portrait` is in the key because it changes what gets drawn — the note is
    // dropped — and a key that ignored it would keep a stale card on a page
    // that changed orientation without the text changing.
    card.kind, card.eyebrow, card.title, card.lead, portrait ? '' : card.note, portrait,
  ].join('|');

  if (L.panel) {
    // The column carries the mark, the card and the score at once, so it is one
    // paint and one key.
    //
    // AND IT CARRIES THEM FROM THE FIRST FRAME. Both of the rules that used to
    // keep it empty during the intro are rules about the BOARD, and neither
    // survives the writing moving off it:
    //
    //   the card is suppressed under the opening title — because in the old
    //   layout the title held the middle of the frame and the card sat in the top
    //   left of the same picture, and two pieces of writing dissolving past each
    //   other in one frame reads as a fault. The title is now confined to the
    //   board's region and the card is in a column beside it. Nothing dissolves
    //   past anything, and the intro card is exactly what a viewer wants beside
    //   the board while the title holds: the event, the game's name, the two
    //   players, the opening and the sentence saying what the game is known for.
    //
    //   the score does not start until the intro is over — because it was a
    //   WINDOW that ended at the playhead, and before the first move there was no
    //   window to draw. It is the whole score now, so at the intro it is the game
    //   about to be played, listed, with nothing lit up yet.
    //
    // What is still true is that the closing card's result is not in the intro
    // card, so none of this says who won.
    //
    // THE SCORE IS THE WHOLE GAME HERE, not the four-move window the strip
    // takes. It is the same rows on every frame of the render, so it is built
    // once and hung on `ctx` — 136 rows for the longest game in the library, and
    // rebuilding them per frame to throw them away would be the one wasteful
    // thing in this function.
    ctx.scoreAll ??= scoreRows(plies);
    const panel = {
      site: p.site || '',
      card: p.card && card ? card : null,
      strip: (p.moveList || p.progressBar) ? strip : null,
      rows: ctx.scoreAll,
      frameW: ctx.width,
    };
    // NOT `stripKey`, which spells out the window's four moves. Those rows are
    // constant now, and where the list is scrolled to is a function of `at` and
    // a band height fixed for the render — so `at` is the whole of what moving
    // the list depends on, and joining 271 tokens into a string every frame to
    // say so would cost more than the repaint it guards.
    const scrollKey = panel.strip
      ? [strip.at, strip.phase, strip.clock, strip.balance, strip.hideAhead,
        strip.eval ? `${strip.eval.share.toFixed(3)}${strip.eval.text}` : '',
        strip.whiteLost.join(''), strip.blackLost.join(''),
        strip.fill.map((v) => v.toFixed(3)).join(',')].join('|')
      : '';
    // UNDER THE THUMBNAIL OVERLAY THE COLUMN IS THE COVER'S. A YouTube thumbnail
    // is a tile, and a column of 21px type at that size is grey noise; what goes
    // there instead is the question, set as large as the column takes, beside
    // the board it is about. See `drawCoverColumn`.
    const cover = ctx.reelNow?.kind === 'cover' ? ctx.reelNow : null;
    if (cover) {
      paintOverlay(ctx.hudPanel, true, (g, W, H) => drawCoverColumn(g, W, H, cover),
        ['cover', cover.chip, cover.title, cover.ask].join('|'));
      return;
    }
    paintOverlay(ctx.hudPanel, !!(panel.site || panel.card || panel.strip),
      (g, W, H) => drawPanel(g, W, H, panel, p),
      [panel.site, panel.card ? cardKey : '', scrollKey].join('#'));
    return;
  }

  paintOverlay(ctx.hudTop, p.card && card && !open && !ctx.reelNow,
    (g, W, H) => drawCard(g, W, H, card, shape), cardKey);

  // ---- the website ------------------------------------------------------
  // Nothing in the key but the string and the shape, because nothing else can
  // change it: no phase, no ply, no card, no opener. That is the mark's whole
  // definition — it is the video's own footer rather than a piece of the
  // telling — and it costs one repaint for the length of a render.
  //
  // Not on the site's flat board, which fills its frame, so the corner is the
  // eighth rank and the wash would darken h8. The studio's 2D board — the Shorts
  // and Reels — stands lower in a tall frame (`FLAT_DROP`), and the corner above
  // it is clear. Down whenever the reel's writing is up — the hook, the puzzle
  // with its count, the close — which runs the width of the frame at that height.
  paintOverlay(ctx.hudSite, !!p.site && (p.dimension !== '2d' || STUDIO) && !ctx.reelNow,
    (g, W, H) => drawSite(g, W, H, p.site, {
      portrait, topInset: shape.topInset, frameW: ctx.width,
    }), [p.site, portrait].join('|'));

  // The window's four moves spelled out, which is the strip's whole content and
  // is built here rather than above because only the strip reads it: the panel
  // shows the score as a page and keys off the playhead instead. See the note on
  // `scrollKey`.
  const stripKey = [
    strip.rows.map((r) => r.text).join('/'), strip.at, strip.phase, strip.clock, strip.hideAhead,
    strip.balance, strip.whiteLost.join(''), strip.blackLost.join(''),
    strip.fill.map((v) => v.toFixed(3)).join(','),
  ].join('|');
  paintOverlay(ctx.hudBottom, (p.moveList || p.progressBar) && view.phase !== 'intro',
    (g, W, H) => drawStrip(g, W, H, strip, p), stripKey);
}

/** A plain description of a move, for the half of them with nothing to say. */
function pieceSentence(ctx, ply) {
  const name = NAMES[typeOf(ply.move.piece)];
  const side = ply.side === 'w' ? 'White' : 'Black';
  if (ply.move.capturedSq >= 0) {
    return `${side}'s ${name} takes the ${NAMES[typeOf(ply.move.captured)]}`;
  }
  return `${side}'s ${name} to ${squareName(ply.move.to)}`;
}

/**
 * The closing card's second line: who won, and what it cost them.
 *
 * `balance` is positive when Black is ahead on material, so the winner's own
 * deficit is it or its negation depending on which side won. A winner who
 * finished ahead did not win "down" anything, however much they gave away along
 * the way — which is the difference between Anderssen, mated-with-nothing, and
 * Morphy, who sacrificed a queen and still had more wood at the end.
 */
function winnerLine(game, mat) {
  const blackWon = game.result.trim().startsWith('0');
  const winner = blackWon ? game.black : game.white;
  const gave = blackWon ? mat.blackLost : mat.whiteLost;
  const behind = blackWon ? -mat.balance : mat.balance;
  if (!gave.length) return `${winner} wins`;
  return behind > 0
    ? `${winner} wins, ${describeLost(gave)} down`
    : `${winner} wins, having given up ${describeLost(gave)}`;
}

/**
 * The closing card's lead for a game nobody won.
 *
 * `winnerLine` names a player and says what he gave up to win. There is no such
 * player here, so this says the only thing about a drawn game that is true of
 * both sides at once: what it cost them. Level and expensive reads differently
 * from level and untouched, and both happen.
 */
function drawnLine(mat) {
  const w = mat.whiteLost.length;
  const b = mat.blackLost.length;
  if (!w && !b) return 'Drawn with the pieces untouched';
  if (mat.balance === 0) return `Drawn — each side gave up ${describeLost(mat.whiteLost)}`;
  return `Drawn — White gave up ${describeLost(mat.whiteLost)}, Black ${describeLost(mat.blackLost)}`;
}

/** "a queen, two rooks and a bishop" — for the final card. */
function describeLost(list) {
  const order = ['q', 'r', 'b', 'n', 'p'];
  const words = { q: 'queen', r: 'rook', b: 'bishop', n: 'knight', p: 'pawn' };
  const counts = {};
  for (const type of list) counts[type] = (counts[type] || 0) + 1;
  const parts = order.filter((type) => counts[type]).map((type) => {
    const n = counts[type];
    const noun = n > 1 ? `${words[type]}s` : `a ${words[type]}`;
    return n > 1 ? `${['', '', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'][n]} ${noun}` : noun;
  });
  if (parts.length <= 1) return parts[0] || 'nothing';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The last four full moves of the score, ENDING at the move being played.
 *
 * Not a window centred on the current move, which is what this was first: a
 * strip centred on move 11 also shows moves 12 and 13, and this game is watched
 * for four moves nobody sees coming. A score that is written as it happens
 * cannot spoil the next one.
 */
function scoreWindow(plies, ply) {
  const at = Math.max(0, ply);
  const moveNo = Math.floor(at / 2) + 1;
  const first = Math.max(1, moveNo - 3);
  const rows = [];
  for (let n = first; n <= moveNo; n++) {
    const white = plies[(n - 1) * 2];
    const black = plies[(n - 1) * 2 + 1];
    if (!white) break;
    rows.push({
      no: n,
      text: `${n}. ${white.san}${black && black.index <= at ? ` ${black.san}` : ''}`,
      white: { san: white.san, index: white.index },
      black: black && black.index <= at ? { san: black.san, index: black.index } : null,
    });
  }
  return rows;
}

/**
 * The whole score as rows, one full move each, for the panel's scrolling list.
 *
 * The other half of `scoreWindow` above rather than a replacement for it, and the
 * difference is the whole point of each: the strip over the board is a window
 * that ENDS at the move being played, and this is the score as a page, present
 * from the first frame, with the playhead running down it. Which one a layout
 * wants follows from where it puts the writing — see `SCORE_ANCHOR`.
 *
 * Both cells are always here, `at` is not consulted, and no text is built:
 * whether a move has been played is a comparison the painter makes per cell, and
 * it is what decides the cell's colour rather than whether it exists. That also
 * makes this constant for a game, which is why `drawHud` builds it once.
 */
function scoreRows(plies) {
  const rows = [];
  for (let i = 0; i < plies.length; i += 2) {
    const white = plies[i];
    const black = plies[i + 1];
    rows.push({
      no: i / 2 + 1,
      white: { san: white.san, index: white.index },
      black: black ? { san: black.san, index: black.index } : null,
    });
  }
  return rows;
}

/** How far into each phase we are, one number per phase. */
function phaseFill(ctx, t) {
  return ctx.timeline.phaseSpan.map((span) => clamp01((t - span.t0) / Math.max(1e-6, span.t1 - span.t0)));
}

/**
 * What is in the middle of the frame at `t`, and how opaque it is — or null once
 * the opening is done, which is what turns the corner card on.
 *
 * The same three lines the card is made of, from the same fields: the event and
 * the date, the title, the two players. Only the title differs, and it is the
 * one the video is PUBLISHED under rather than the game's name.
 *
 * It fades in over a fifth of a second and out over a quarter of one, and the
 * corner card is not drawn until it has gone: two pieces of writing dissolving
 * past each other in different parts of the frame reads as a fault in the render.
 *
 * Nothing here reads the frame number, so seeking to second 1 shows what second
 * 1 shows.
 */
function openerAt(ctx, t) {
  const hold = openerHold(ctx);
  if (!hold || t >= hold) return null;
  const IN = Math.min(0.20, hold * 0.16);
  const OUT = Math.min(0.26, hold * 0.20);
  return {
    eyebrow: `${ctx.g.event} · ${ctx.g.date}`,
    title: ctx.g.ytTitle,
    lead: `${ctx.g.white} — ${ctx.g.black}`,
    alpha: Math.min(smoothstep(t / IN), smoothstep((hold - t) / OUT)),
  };
}

/**
 * Seconds the title is held: what was asked for, or what the intro affords.
 *
 * The two are the same number now and this is still a function, which wants
 * saying. The intro USED to be a share of the length that the title took a
 * fraction of, and the leftover was dead board — 6.9 seconds of it in a
 * three-minute cut. It is the other way round: the hold is the input, the intro
 * is the hold plus `SETTLE`, and this reads that back off the timeline rather
 * than off the dial so the two clamps in `introFor` cannot be walked past. A
 * render asking for six seconds of title inside an intro the floor set to 2.5
 * gets 0.5, and the first move still lands two seconds after the writing goes.
 *
 * Nothing to hold is nothing to hold: no `ytTitle` and the dial at zero are the
 * same answer, and `timelineFor` passes the same zero on so the lead-in is the
 * settle by itself.
 */
function openerHold(ctx) {
  if (!ctx.g.ytTitle || ctx.params.opener <= 0) return 0;
  // A reel has no title card; the landscape cold open plays it after the rewind.
  if (ctx.timeline.reel && !ctx.timeline.reel.titled) return 0;
  // A whole analysis holds the title for as long as the opening lines are read —
  // the intro was sized to them — and the settle follows, as at any length.
  if (ctx.timeline.analysis) return Math.max(0, ctx.timeline.intro - SETTLE);
  return Math.min(ctx.params.opener, Math.max(0, ctx.timeline.intro - SETTLE));
}

/**
 * The opening: the card's three lines, centred, big, over the board.
 *
 * Scaled off the WIDTH alone, rather than off both axes the way the card is. A
 * portrait frame is not a landscape one with smaller writing in it — it is a
 * narrower column, and writing that holds four fifths of the frame's width in
 * landscape has to hold four fifths of it in portrait too, or the opening reads
 * as a caption.
 *
 * The scrim is a band rather than the card's corner wash, for the reason the
 * card has one at all: white writing over a lit cream square is not readable at
 * any shadow radius. It fades to nothing above and below the writing, so the
 * board is still the picture behind it.
 */
function drawOpener(g, W, H, open) {
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  const MONO = 'ui-monospace, Menlo, Consolas, monospace';
  const S = W / 1700;
  const maxW = W * 0.82;

  const head = headlineRows(g, open.title, maxW, 80 * S, '700', SANS, 3);
  const titleLead = head.size * 1.15;
  const eyebrow = 26 * S;
  const lead = 36 * S;
  // Baselines from the top of the block, so the whole composition can be centred
  // on the frame rather than the title being centred and the other two hanging
  // off it.
  const gapAbove = 46 * S;
  const gapBelow = 54 * S;
  const blockH = eyebrow + gapAbove + (head.rows.length - 1) * titleLead + head.size
    + gapBelow + lead;
  let y = H * 0.5 - blockH * 0.5 + eyebrow;

  // Held near full strength across the writing and taken to nothing at both
  // edges of the band: a gradient that peaks only at the centre line leaves the
  // top of a three-row title over bare board.
  const half = blockH * 0.5 + head.size * 1.3;
  const band = g.createLinearGradient(0, H * 0.5 - half, 0, H * 0.5 + half);
  band.addColorStop(0, 'rgba(6,8,12,0)');
  band.addColorStop(0.26, 'rgba(6,8,12,0.82)');
  band.addColorStop(0.74, 'rgba(6,8,12,0.82)');
  band.addColorStop(1, 'rgba(6,8,12,0)');
  g.fillStyle = band;
  g.fillRect(0, H * 0.5 - half, W, half * 2);

  shadow(g, 26 * S);
  g.textAlign = 'center';

  g.font = `${Math.round(eyebrow)}px ${MONO}`;
  g.fillStyle = 'rgba(226,236,245,0.62)';
  g.fillText(open.eyebrow, W / 2, y);

  y += gapAbove + head.size;
  g.fillStyle = '#ffffff';
  g.font = `700 ${Math.round(head.size)}px ${SANS}`;
  head.rows.forEach((row, i) => g.fillText(row, W / 2, y + i * titleLead));

  y += (head.rows.length - 1) * titleLead + gapBelow + lead;
  g.font = `500 ${Math.round(lead)}px ${SANS}`;
  g.fillStyle = 'rgba(226,236,245,0.85)';
  g.fillText(open.lead, W / 2, y);

  noShadow(g);
  g.textAlign = 'left';
}

/**
 * The reel's writing: the hook over the cold open, the puzzle with its count,
 * and the question it closes on. See `reelMoment` in `reel.js` for when.
 *
 * Hung from the top of the frame, below `SAFE_TOP`, because the board has the
 * middle and both apps put their own controls over the top and the bottom. The
 * wash runs from the top edge down past the writing and fades out over the
 * board's far rank, so the position stays readable under it — a puzzle whose
 * board is covered is not a puzzle.
 *
 * Scaled off whichever of the frame's two sides is shorter against a 1080x1920
 * Short, so it still fits in the studio's landscape preview.
 */
function drawReel(g, W, H, m) {
  if (m.kind === 'cover') return drawCover(g, W, H, m);
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  // Landscape is the cold open over the board region of the long cut: no app
  // furniture over the top of it, and a frame half as tall as a Short's.
  const land = W >= H;
  const S = land ? H / 1300 : Math.min(W / 1080, H / 1920);
  // In a tall frame it starts a little inside `SAFE_TOP` rather than below it:
  // the 2D board stands lower there (`FLAT_DROP`), with Black's nameplate over
  // it, and a two-line title hung below the safe line ran into the plate. The
  // eyebrow is what goes under the apps' controls, and it repeats the title.
  const top = land ? 44 * S : SAFE_TOP * H - 84 * S;
  // The count sits beside the title rather than under it: under it was over the
  // board's far rank, and that is the rank a puzzle is most often about.
  const ring = m.count ? 50 * S : 0;
  const ringGap = m.count ? 34 * S : 0;
  const maxW = W * 0.86 - (ring * 2 + ringGap);
  const head = headlineRows(g, m.title, maxW, 84 * S, '800', SANS, 2);
  const eyebrow = 30 * S;
  const sub = 34 * S;
  const lead = head.size * 1.08;

  const y = top + eyebrow;
  const titleY = y + 24 * S + head.size;
  const titleEnd = titleY + (head.rows.length - 1) * lead;
  const subY = titleEnd + 22 * S + sub;
  const bottom = (m.sub ? subY : titleEnd) + 34 * S;

  const wash = g.createLinearGradient(0, 0, 0, bottom + 60 * S);
  wash.addColorStop(0, 'rgba(6,8,12,0.9)');
  wash.addColorStop(0.75, 'rgba(6,8,12,0.8)');
  wash.addColorStop(1, 'rgba(6,8,12,0)');
  g.fillStyle = wash;
  g.fillRect(0, 0, W, bottom + 60 * S);

  shadow(g, 18 * S);
  g.textAlign = 'center';
  g.fillStyle = m.kind === 'puzzle' ? '#ffcf5a' : 'rgba(226,236,245,0.72)';
  fitText(g, m.eyebrow, W / 2, y, W * 0.86, eyebrow, '700', SANS);

  g.font = `800 ${Math.round(head.size)}px ${SANS}`;
  const widest = Math.max(...head.rows.map((row) => g.measureText(row).width));
  // With a count, the title and the ring are centred as one group.
  const cx = W / 2 - (ring ? (ring * 2 + ringGap) / 2 : 0);
  g.fillStyle = '#ffffff';
  head.rows.forEach((row, i) => g.fillText(row, cx, titleY + i * lead));

  if (m.sub) {
    g.fillStyle = m.sub.startsWith('◀') ? '#ffcf5a' : 'rgba(226,236,245,0.85)';
    fitText(g, m.sub, W / 2, subY, W * 0.86, sub, '600', SANS);
  }

  if (m.count) {
    // The second is the number; the ring is how much of it is left.
    const rx = cx + widest / 2 + ringGap + ring;
    const ry = titleY - head.size * 0.36 + (head.rows.length - 1) * lead / 2;
    noShadow(g);
    g.lineWidth = 7 * S;
    g.strokeStyle = 'rgba(255,255,255,0.2)';
    g.beginPath();
    g.arc(rx, ry, ring, 0, Math.PI * 2);
    g.stroke();
    g.strokeStyle = '#ffcf5a';
    g.lineCap = 'round';
    g.beginPath();
    g.arc(rx, ry, ring, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (1 - m.progress));
    g.stroke();
    g.lineCap = 'butt';
    shadow(g, 10 * S);
    g.fillStyle = '#ffffff';
    g.font = `800 ${Math.round(ring * 1.2)}px ${SANS}`;
    g.textBaseline = 'middle';
    g.fillText(String(m.count), rx, ry + ring * 0.05);
    g.textBaseline = 'alphabetic';
  }

  noShadow(g);
  g.textAlign = 'left';
}

/**
 * Where on the frame the cover's ring goes, as fractions of the frame: the piece
 * the payoff takes, or, for a mate, the king it mates — projected through the
 * camera this frame was placed with, at about the middle of the piece's height.
 */
function coverTarget(ctx, m, view) {
  let sq = m.target;
  if (sq == null && m.mateKing) {
    const pos = ctx.game.positions[Math.max(0, view.done)];
    const loser = pos.turn === 'w' ? 'b' : 'w';
    sq = pos.kingSquare(loser);
  }
  if (sq == null || sq < 0) return null;
  const { x, z } = squarePos(sq);
  const v = new ctx.THREE.Vector3(x, TOP + 0.45, z).project(ctx.camera);
  const near = new ctx.THREE.Vector3(x + 0.5, TOP + 0.45, z).project(ctx.camera);
  return {
    x: (v.x + 1) / 2,
    y: (1 - v.y) / 2,
    // Half a square, measured on screen, so the ring fits the square at any zoom.
    r: Math.abs(near.x - v.x) / 2,
  };
}

/**
 * The reel's thumbnail, over the puzzle position.
 *
 * Built for a tile the size of a thumbnail: three lines at most, the biggest of
 * them five words, heavy type with a dark edge so it holds over the light
 * squares, and one thing on the board marked — the piece there is to win.
 * Higher than the captions sit: a thumbnail is seen in a grid or a search
 * result, where nothing is drawn over its top, so the headline can go above
 * the board instead of on it. The question goes under the board, in the space
 * a portrait frame leaves there. Nothing names the move.
 */
function drawCover(g, W, H, m) {
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  if (m.ringOnly) {
    drawCoverRing(g, W, H, m, Math.min(W, H) / 1080);
    return;
  }
  // Landscape is the long cut's YouTube thumbnail, over the board's region of a
  // 1920x1080 frame: scaled off the height, since that region is wider than tall.
  const S = W >= H ? H / 1150 : Math.min(W / 1080, H / 1920);

  // A vignette pulls the eye to the middle, where the board is.
  const vig = g.createRadialGradient(W / 2, H * 0.48, H * 0.18, W / 2, H * 0.48, H * 0.75);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.55)');
  g.fillStyle = vig;
  g.fillRect(0, 0, W, H);

  const wash = g.createLinearGradient(0, 0, 0, H * 0.27);
  wash.addColorStop(0, 'rgba(6,8,12,0.92)');
  wash.addColorStop(0.75, 'rgba(6,8,12,0.7)');
  wash.addColorStop(1, 'rgba(6,8,12,0)');
  g.fillStyle = wash;
  g.fillRect(0, 0, W, H * 0.27);

  g.textAlign = 'center';

  // The chip: who is to play.
  const chipPx = 40 * S;
  g.font = `800 ${Math.round(chipPx)}px ${SANS}`;
  const chipW = g.measureText(m.chip).width + 56 * S;
  const chipH = chipPx * 1.7;
  const chipY = H * 0.055;
  g.fillStyle = '#ffcf5a';
  roundRect(g, W / 2 - chipW / 2, chipY, chipW, chipH, chipH / 2);
  g.fill();
  g.fillStyle = '#14100a';
  g.textBaseline = 'middle';
  g.fillText(m.chip, W / 2, chipY + chipH / 2 + 2 * S);
  g.textBaseline = 'alphabetic';

  // The headline, as big as the width allows, outlined so it reads over wood.
  const head = headlineRows(g, m.title, W * 0.92, 140 * S, '900', SANS, 1);
  let y = chipY + chipH + 34 * S + head.size * 0.86;
  g.font = `900 ${Math.round(head.size)}px ${SANS}`;
  g.lineJoin = 'round';
  head.rows.forEach((row) => {
    g.lineWidth = head.size * 0.12;
    g.strokeStyle = 'rgba(0,0,0,0.9)';
    g.strokeText(row, W / 2, y);
    g.fillStyle = '#ffffff';
    g.fillText(row, W / 2, y);
    y += head.size * 1.02;
  });

  // The question.
  const askPx = 84 * S;
  g.font = `900 ${Math.round(askPx)}px ${SANS}`;
  const askY = H * 0.815;
  g.lineWidth = askPx * 0.14;
  g.strokeStyle = 'rgba(0,0,0,0.9)';
  g.strokeText(m.ask, W / 2, askY);
  g.fillStyle = '#ffcf5a';
  g.fillText(m.ask, W / 2, askY);

  drawCoverRing(g, W, H, m, S);
  g.textAlign = 'left';
}

/** The ring on what there is to win, with a glow so it reads at tile size. */
function drawCoverRing(g, W, H, m, S) {
  if (!m.at) return;
  const cx = m.at.x * W;
  const cy = m.at.y * H;
  const r = Math.max(46 * S, m.at.r * W * 1.25);
  g.save();
  g.shadowColor = 'rgba(255,40,40,0.95)';
  g.shadowBlur = 34 * S;
  g.lineWidth = 11 * S;
  g.strokeStyle = '#ff3b3b';
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.stroke();
  g.restore();
}

/**
 * The long cut's YouTube thumbnail, the column half: who is to play, what there
 * is to win, and the question, stacked and centred in the third of the frame the
 * score usually has. The board and the ring are the other two thirds.
 */
function drawCoverColumn(g, W, H, m) {
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  const S = H / 1080;
  const maxW = W - 2 * 44 * S;
  g.fillStyle = GROUND;
  g.fillRect(0, 0, W, H);
  g.fillStyle = 'rgba(255,255,255,0.09)';
  g.fillRect(0, 0, Math.max(1, 2 * S), H);

  const chipPx = 44 * S;
  g.font = `800 ${Math.round(chipPx)}px ${SANS}`;
  const chipW = Math.min(maxW, g.measureText(m.chip).width + 60 * S);
  const chipH = chipPx * 1.7;
  const head = headlineRows(g, m.title, maxW, 150 * S, '900', SANS, 2);
  const askPx = 72 * S;
  const lead = head.size * 1.02;
  const block = chipH + 50 * S + head.rows.length * lead + 40 * S + askPx;
  let y = (H - block) / 2;

  g.textAlign = 'center';
  g.fillStyle = '#ffcf5a';
  roundRect(g, W / 2 - chipW / 2, y, chipW, chipH, chipH / 2);
  g.fill();
  g.fillStyle = '#14100a';
  g.textBaseline = 'middle';
  // Again: measuring the headline above left its font set.
  g.font = `800 ${Math.round(chipPx)}px ${SANS}`;
  g.fillText(m.chip, W / 2, y + chipH / 2 + 2 * S);
  g.textBaseline = 'alphabetic';

  y += chipH + 50 * S + head.size * 0.86;
  g.font = `900 ${Math.round(head.size)}px ${SANS}`;
  g.fillStyle = '#ffffff';
  head.rows.forEach((row) => { g.fillText(row, W / 2, y); y += lead; });

  y += 40 * S - lead + head.size * 0.14 + askPx;
  g.font = `900 ${Math.round(askPx)}px ${SANS}`;
  g.fillStyle = '#ffcf5a';
  fitText(g, m.ask, W / 2, y, maxW, askPx, '900', SANS);
  g.textAlign = 'left';
}

/**
 * A headline broken over as many rows as it needs, shrinking until it needs no
 * more than `maxRows`, and then balanced.
 *
 * The card's three headlines are a game's name, a move and the word "Checkmate",
 * and `fitText` handles all of them: one line, shrunk a little if it has to be.
 * The opening one is a YouTube title — 53 to 74 characters, written to be clicked
 * on rather than to fit a frame — and one line of that at any size a viewer could
 * read is off both edges of the frame.
 *
 * BALANCED, not filled: wrapping at the widest width that fits packs the top
 * rows and leaves the remainder on the last one — "…and the Queen —" over "Given
 * Away" — which reads as a line that ran out rather than as a title set in three.
 * Re-wrapping at the narrowest width that still gives the same number of rows
 * squares them up, which is what `text-wrap: balance` does.
 */
function headlineRows(g, text, maxW, px, weight, family, maxRows) {
  let size = px;
  let rows = breakRows(g, text, maxW, size, weight, family);
  for (let i = 0; i < 10 && rows.length > maxRows; i++) {
    size *= 0.93;
    rows = breakRows(g, text, maxW, size, weight, family);
  }
  for (let f = 0.95; f > 0.45 && rows.length > 1; f -= 0.02) {
    const tighter = breakRows(g, text, maxW * f, size, weight, family);
    if (tighter.length !== rows.length) break;
    rows = tighter;
  }
  return { rows, size };
}

/** The line broken to fit, at this size. Leaves the font set for the caller. */
function breakRows(g, text, maxW, px, weight, family) {
  g.font = `${weight} ${Math.round(px)}px ${family}`;
  const rows = [];
  let line = '';
  for (const word of String(text).split(/\s+/)) {
    const test = line ? `${line} ${word}` : word;
    if (line && g.measureText(test).width > maxW) { rows.push(line); line = word; }
    else line = test;
  }
  if (line) rows.push(line);
  return rows;
}

/**
 * `portrait` is not cosmetic here — see the two branches below.
 *
 * A landscape frame is watched on something bigger than a hand and has no app
 * furniture over it, so its card is sized off a 1900-wide reference and starts
 * near the top edge. A portrait frame is the opposite on both counts, and using
 * the landscape rule on it made the text SMALLER (S 0.568 against 0.779) on the
 * frame where it has to be bigger, and put it under the app's controls as well.
 */
function drawCard(g, W, H, card, { portrait = false, topInset = 0 } = {}) {
  // Off the frame's own width in portrait, which comes out at 1.0 — the same
  // pixel size as landscape on a frame 56% as wide, so 1.8x the size relative to
  // what a viewer is looking at.
  const S = portrait
    ? Math.min(W / 1080, H / 560)
    : Math.min(W / 1900, H / 430);
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  const MONO = 'ui-monospace, Menlo, Consolas, monospace';
  const x = 52 * S;
  const maxW = Math.min(W - 2 * x, 880 * S);

  // A corner scrim rather than a panel. 206 gets away with text and a drop
  // shadow and nothing else because its subject is a dark cube on black; a third
  // of this frame is cream squares in full key light, and white text on a light
  // square is not readable at any shadow radius.
  //
  // Two gradients rather than one, and that is the whole trick: a single radial
  // gradient anchored in the corner is still 30% opaque where it meets the far
  // edge of its own plane, so it draws the rectangle it was there to avoid. A
  // horizontal fade, then the SAME fade run vertically through
  // `destination-out`, gives a wash that reaches zero on both axes inside the
  // frame and has no edge anywhere.
  const across = g.createLinearGradient(0, 0, W * 0.55, 0);
  across.addColorStop(0, 'rgba(6,8,12,0.88)');
  across.addColorStop(0.55, 'rgba(6,8,12,0.62)');
  across.addColorStop(1, 'rgba(6,8,12,0)');
  g.fillStyle = across;
  g.fillRect(0, 0, W, H);
  g.save();
  g.globalCompositeOperation = 'destination-out';
  const down = g.createLinearGradient(0, H * 0.42, 0, H);
  down.addColorStop(0, 'rgba(0,0,0,0)');
  down.addColorStop(1, 'rgba(0,0,0,1)');
  g.fillStyle = down;
  g.fillRect(0, 0, W, H);
  g.restore();

  shadow(g, 22 * S);

  // Clear of the app's controls in portrait; hard against the top otherwise.
  // `topInset` is worked out by the caller, which is the only place that knows
  // the frame's height — this canvas is a band of it and cannot recover the
  // fraction it was cut at without being told what that fraction was.
  let y = (portrait ? topInset + 26 * S : 0) + 58 * S;
  g.font = `${Math.round(21 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(226,236,245,0.55)';
  g.fillText(card.eyebrow, x, y);

  y += 66 * S;
  g.fillStyle = '#ffffff';
  fitText(g, card.title, x, y, maxW, (card.kind === 'move' ? 72 : 58) * S, '700',
    card.kind === 'move' ? MONO : SANS);

  y += 42 * S;
  g.fillStyle = 'rgba(226,236,245,0.80)';
  fitText(g, card.lead, x, y, maxW, 29 * S, '500', SANS);

  // NO PARAGRAPH IN PORTRAIT. The note is two or three lines of prose, written to
  // be read beside a board on a screen somebody is sitting at. On a phone, inside
  // a clip that gives this move a few seconds, it is a block of grey nobody
  // finishes — and it is what pushed the card deep enough to need this band in
  // the first place. The lead above it is the same point in six words.
  if (card.note && !portrait) {
    y += 26 * S;
    g.fillStyle = 'rgba(255,255,255,0.20)';
    g.fillRect(x, y, 96 * S, 2 * S);
    y += 40 * S;
    g.fillStyle = 'rgba(214,226,240,0.86)';
    wrapText(g, card.note, x, y, maxW, 25 * S, 33 * S, SANS, 3);
  }
  noShadow(g);
}

/**
 * The website, in the top right corner.
 *
 * THE WASH IS THE CARD'S, MIRRORED, and that is the one decision here worth
 * writing down. The obvious mark is a rounded box with the address in it, and a
 * box in the corner of a picture reads as an ident stuck onto somebody else's
 * work. The card on the other side of the frame already solved the same problem
 * — white type over cream squares in full key light, which no shadow radius
 * makes readable — with two gradients that reach zero on both axes and so draw
 * no rectangle anywhere. Using it again is what makes the two corners one design
 * rather than two.
 *
 * It is run in from the right edge and cut away downwards from just under the
 * baseline, so it is at full strength behind the type in both orientations. Its
 * width follows the STRING rather than the plane: the plane is sized for 32
 * characters and the address is eleven, and a wash cut for the plane would mean
 * a third of the frame gone dark for the sake of one word.
 *
 * The type is a fraction of the FRAME's width and not of this canvas's, for
 * `shared/slides.js`'s reason — a scale taken from the band would size the mark
 * differently in the two orientations, and the number that has to be readable is
 * the one on the phone. 0.019 is 36px on a 1920 render; portrait gets 0.030,
 * which is 32px on a frame 56% as wide, so it is the same size in the hand.
 *
 * `topInset` is the caller's, for `drawCard`'s reason: this canvas is a band of
 * the frame and cannot recover the fraction it was cut at. In portrait it puts
 * the baseline at 300 against the card's 304 — the two marks sit on one line
 * across the frame, which is what both being worked out from the frame's width
 * buys.
 */
function drawSite(g, W, H, text, { portrait = false, topInset = 0, frameW = W } = {}) {
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  const px = frameW * (portrait ? 0.030 : 0.019);
  const pad = px * 1.4;
  // Clear of the app's controls in portrait; on the card's eyebrow line either way.
  const y = (portrait ? topInset + px * 0.9 : 0) + px * 1.6;

  g.font = `700 ${Math.round(px)}px ${SANS}`;
  const span = Math.min(W, Math.min(g.measureText(text).width, W - 2 * pad) + pad * 2.4);

  const across = g.createLinearGradient(W, 0, W - span, 0);
  across.addColorStop(0, 'rgba(6,8,12,0.82)');
  across.addColorStop(0.55, 'rgba(6,8,12,0.52)');
  across.addColorStop(1, 'rgba(6,8,12,0)');
  g.fillStyle = across;
  g.fillRect(0, 0, W, H);
  g.save();
  g.globalCompositeOperation = 'destination-out';
  const down = g.createLinearGradient(0, y + px * 0.4, 0, H);
  down.addColorStop(0, 'rgba(0,0,0,0)');
  down.addColorStop(1, 'rgba(0,0,0,1)');
  g.fillStyle = down;
  g.fillRect(0, 0, W, H);
  g.restore();

  shadow(g, px * 0.6);
  // `paintOverlay` hands every canvas over left-aligned, so this puts it back
  // rather than leaving the next painter to find out.
  g.textAlign = 'right';
  g.fillStyle = 'rgba(255,255,255,0.94)';
  fitText(g, text, W - pad, y, W - 2 * pad, px, '700', SANS);
  g.textAlign = 'left';
  noShadow(g);
}

function drawStrip(g, W, H, s, p) {
  const S = Math.min(W / 1300, H / 250);
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  const MONO = 'ui-monospace, Menlo, Consolas, monospace';
  const x = 40 * S;
  const maxW = W - 2 * x;

  // Same argument as the card's vignette, one axis instead of two: the strip
  // crosses the near edge of the board, which is light wood in the middle and
  // dark table at both ends.
  const scrim = g.createLinearGradient(0, H, 0, 0);
  scrim.addColorStop(0, 'rgba(6,8,12,0.80)');
  scrim.addColorStop(1, 'rgba(6,8,12,0)');
  g.fillStyle = scrim;
  g.fillRect(0, 0, W, H);

  shadow(g, 20 * S);

  // ---- progress: one segment per phase of the game -----------------------
  let y = H - 30 * S;
  if (p.progressBar) {
    const gap = 8 * S;
    const total = maxW - gap * (s.phases.length - 1);
    let bx = x;
    s.fill.forEach((f, i) => {
      const w = total / s.phases.length;
      g.fillStyle = 'rgba(255,255,255,0.13)';
      roundRect(g, bx, y, w, 7 * S, 3.5 * S);
      g.fill();
      if (f > 0) {
        g.fillStyle = f >= 1 ? 'rgba(120,220,170,0.80)' : 'rgba(255,255,255,0.92)';
        roundRect(g, bx, y, Math.max(3 * S, w * f), 7 * S, 3.5 * S);
        g.fill();
      }
      bx += w + gap;
    });
  }

  const rowY = y - 34 * S;

  // ---- the score, with the move that is happening lit up ------------------
  if (p.moveList) {
    g.font = `600 ${Math.round(24 * S)}px ${SANS}`;
    g.fillStyle = 'rgba(226,236,245,0.62)';
    g.fillText(s.phase, x, rowY - 62 * S);

    const size = 30 * S;
    g.font = `600 ${Math.round(size)}px ${MONO}`;
    let tx = x;
    for (const row of s.rows) {
      g.fillStyle = 'rgba(226,236,245,0.42)';
      const label = `${row.no}.`;
      g.fillText(label, tx, rowY);
      tx += g.measureText(label).width + 10 * S;
      for (const cell of [row.white, row.black]) {
        if (!cell) continue;
        // Under the cold open's "find the move" the next move IS the answer.
        if (s.hideAhead && cell.index > s.at) continue;
        const w = g.measureText(cell.san).width;
        if (cell.index === s.at) {
          g.fillStyle = 'rgba(255,232,150,0.95)';
          roundRect(g, tx - 9 * S, rowY - size * 0.88, w + 18 * S, size * 1.24, 8 * S);
          g.fill();
          g.fillStyle = '#12100a';
        } else {
          g.fillStyle = cell.index < s.at ? 'rgba(226,236,245,0.44)' : 'rgba(226,236,245,0.78)';
        }
        g.fillText(cell.san, tx, rowY);
        tx += w + 18 * S;
      }
      tx += 16 * S;
    }
  }

  // ---- the count nobody watching this game should trust -------------------
  g.textAlign = 'right';
  const lead = s.balance > 0 ? `Black +${s.balance}` : s.balance < 0 ? `White +${-s.balance}` : 'Level';
  g.font = `${Math.round(22 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(226,236,245,0.55)';
  g.fillText(`${lead}   ·   White has lost ${s.whiteLost.map(glyph).join(' ') || '—'}`, W - x, rowY - 62 * S);
  g.font = `600 ${Math.round(32 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(255,255,255,0.88)';
  g.fillText(`${s.clock} / ${s.total}`, W - x, rowY);
  g.textAlign = 'left';
  noShadow(g);
}

/**
 * Everything written, in the column beside the board.
 *
 * WHAT CHANGES AND WHAT DOES NOT. The words are the card's and the strip's
 * exactly — same fields, same source, same repaint keys — so there is one place
 * a move is described and one place a score is windowed, and this is a third
 * arrangement of them rather than a third copy. What changes is that none of it
 * is over the picture any more, and that is worth more than it sounds: `drawCard`
 * and `drawStrip` both spend a two-axis gradient wash on being readable over
 * cream squares in key light, and a wash over the board is a cost paid on every
 * frame of the render. Here the ground is opaque because it is a panel, the type
 * needs no drop shadow to hold, and the board is never darkened at all.
 *
 * IT IS LAID OUT FROM BOTH ENDS. The card runs down from the top, because a
 * viewer reads the move first and its position must not jump when the note under
 * it is two lines instead of four. The score, the count and the bar are pinned
 * up from the bottom, because the bar belongs on the frame's edge and the clock
 * belongs next to it. What is left in the middle is what the note is allowed —
 * `wrapText` takes a row count, so the note is given the rows that actually fit
 * rather than a fixed three, which in a column this tall is usually six.
 *
 * A column is `PANEL_FRACTION` of the frame, which is 640 pixels of a 1920x1080
 * one, and every size below is on that reference. It was 840 while the column was
 * whatever a square board left over, and the narrowing is why the note is set at
 * 20 over 26 rather than 23 over 30 and why the material count is three short
 * lines rather than two: what changed is the MEASURE, so what had to give was the
 * two blocks whose longest strings were already close to it. See
 * `PANEL_MIN_ASPECT` for what happens when there is less width than a card and a
 * score can use at all.
 */
function drawPanel(g, W, H, panel, p) {
  const S = Math.min(W / 640, H / 1080);
  const SANS = 'system-ui, "Helvetica Neue", Arial, sans-serif';
  const MONO = 'ui-monospace, Menlo, Consolas, monospace';
  const pad = 46 * S;
  const maxW = W - 2 * pad;

  // ---- the ground -------------------------------------------------------
  // Flat rather than a gradient: this is furniture beside the picture, not a
  // wash over it, and the one thing it has to do is be the same from top to
  // bottom so that type anywhere down it reads the same. The seam is a hairline
  // rather than a butt join — at 1080 tall a hard edge against the table catches
  // the eye more than the panel does.
  //
  // AND OPAQUE, which it was not: 0.96 let a little of the table through, which
  // was invisible and cost the score list its fade. The list is a viewport onto a
  // score longer than the band, and what says so is the rows going soft at both
  // edges — painted as this same colour over them, which only disappears into the
  // ground if the ground is solid. See `GROUND` and the fades below.
  g.fillStyle = GROUND;
  g.fillRect(0, 0, W, H);
  g.fillStyle = 'rgba(255,255,255,0.09)';
  g.fillRect(0, 0, Math.max(1, 2 * S), H);

  const rule = (y, w, alpha) => {
    g.fillStyle = `rgba(255,255,255,${alpha})`;
    g.fillRect(pad, y, w, Math.max(1, S));
  };

  // ---- the mark ---------------------------------------------------------
  // The type is a fraction of the FRAME's width, not this canvas's, for
  // `drawSite`'s reason: what has to be readable is the size on the screen the
  // frame is watched on, and a scale taken from the column would size the
  // address differently on every aspect ratio the column can be.
  let head = pad;
  if (panel.site) {
    const px = panel.frameW * 0.019;
    g.textAlign = 'right';
    g.fillStyle = 'rgba(255,255,255,0.90)';
    fitText(g, panel.site, W - pad, pad + px, maxW, px, '700', SANS);
    g.textAlign = 'left';
    head = pad + px + 30 * S;
    rule(head, maxW, 0.10);
  }

  // ---- the card, and the line under it that never moves ------------------
  // THE DIVIDER IS AT A FIXED HEIGHT, and that is the layout's one real
  // decision. The card's own depth changes every half-move — a note is two rows
  // or five or absent — so a score list stacked directly under it would shift up
  // and down all render, and at a move a second that reads as the panel
  // twitching rather than as the game moving. Everything below the divider
  // therefore has a fixed home, and what varies is how much of the card's
  // region is used. Empty space above a divider is what a column looks like;
  // a score that will not hold still is not.
  //
  // 0.55 of the height, and the number is measured rather than chosen. Above the
  // line: eyebrow, title, two rows of lead, the short rule and SIX rows of
  // paragraph — six because the longest note in the library is 386 characters and
  // 90 of them run past 200, so anything less truncates the writing the library
  // exists for, mid-sentence, with no ellipsis to admit it. Below it: seven rows
  // of score, which is fourteen half-moves of context to follow the game by.
  // Every gap on both sides of the line was tightened to buy that pair; see the
  // arithmetic laid out in `SCORE_ANCHOR` and in the note budget below.
  const split = Math.round(H * 0.55);

  const card = panel.card;
  if (card) {
    let y = head + 78 * S;
    g.font = `${Math.round(21 * S)}px ${MONO}`;
    g.fillStyle = 'rgba(226,236,245,0.55)';
    g.fillText(card.eyebrow, pad, y);

    y += 70 * S;
    g.fillStyle = '#ffffff';
    // A move's title is "136. Ng7" and fits at any width this column has. The
    // opening card's is the GAME'S NAME — "The Hundred and Thirty-Six Move Game"
    // — which at 52 needed more than the 548 pixels a third of the frame leaves,
    // and `fitText` will only shrink to 55% before it gives up and overflows. So
    // it starts smaller, and the shrink it has left is enough for the longest
    // name in the library.
    fitText(g, card.title, pad, y, maxW, (card.kind === 'move' ? 68 : 44) * S, '700',
      card.kind === 'move' ? MONO : SANS);

    y += 40 * S;
    g.fillStyle = 'rgba(226,236,245,0.82)';
    // Wrapped rather than shrunk to fit. The lead is a phrase and the longest in
    // the library is 52 characters — "e4 — a pawn moves, for the first time in
    // fifty moves" — and in a column `fitText` would answer that by making the
    // sentence a viewer is meant to read first the smallest type on the panel.
    y = wrapText(g, card.lead, pad, y, maxW, 29 * S, 34 * S, SANS, 2, '500');

    // THE NOTE IS DROPPED IN PORTRAIT AND KEPT HERE, which is the same judgement
    // as `drawCard`'s read the other way round: the paragraph is written to be
    // read beside a board by somebody sitting at one, and a column beside the
    // board is exactly that. What it gets is the room down to the divider, at
    // three rows or better, or nothing — two rows of a five-row paragraph ends
    // mid-sentence, which is worse than not starting it.
    if (card.note) {
      const at = y + 18 * S;
      const from = at + 38 * S;
      // 20 over 26, which is the one place this layout trades type size for
      // words, and the trade got dearer when the column went to a third of the
      // width: the measure is 548 pixels rather than 748, and at 20px it takes
      // about 56 characters a row, so the seven rows the divider affords hold 392
      // — past the 386 of the longest note in the library. At 23px it would be 48
      // a row and 336 in seven, and the three longest notes in the library would
      // end mid-sentence with no ellipsis to admit it.
      //
      // AND AS LARGE AS IT WILL GO. Twenty pixels is what the longest note needs;
      // most are a third of that, and set at twenty they left half the column
      // empty under a paragraph a viewer watching on a phone, or with the sound
      // off, could not read. So the note takes the largest size, from 30 down,
      // at which ALL of it fits above the divider — never a size that cuts it.
      let px = 20;
      for (const size of [30, 27, 24, 22]) {
        const avail = Math.floor((split - 16 * S - from) / (size * 1.3 * S));
        if (rowsFor(g, card.note, maxW, size * S, SANS) <= avail) { px = size; break; }
      }
      const rows = Math.floor((split - 16 * S - from) / (px * 1.3 * S));
      if (rows >= 3) {
        g.fillStyle = 'rgba(255,255,255,0.20)';
        g.fillRect(pad, at, 96 * S, Math.max(1, 2 * S));
        g.fillStyle = 'rgba(214,226,240,0.86)';
        wrapText(g, card.note, pad, from, maxW, px * S, px * 1.3 * S, SANS, rows);
      }
    }
  }

  if (!panel.strip) return;
  const s = panel.strip;
  rule(split, maxW, 0.10);

  // ---- the phase, then the score, ONE MOVE A LINE ------------------------
  let y = split + 42 * S;
  g.font = `600 ${Math.round(24 * S)}px ${SANS}`;
  g.fillStyle = 'rgba(226,236,245,0.62)';
  fitText(g, s.phase, pad, y, maxW, 24 * S, '600', SANS);

  // ---- the blocks pinned to the bottom, measured before anything is drawn --
  // The count, the clock and the bar have fixed homes, and the score list gets
  // what is left between the phase line and them. Measured up here because the
  // list is painted first and has to know where it must stop.
  const barH = p.progressBar ? 33 * S : 0;
  const clockY = H - pad - barH;
  const countY = clockY - 34 * S;
  const bandTo = countY - 3 * 28 * S - 10 * S;

  // ---- the score: the whole game, scrolled to the move being played -------
  // THE LIST IS THE SCORE, NOT A WINDOW ON IT. `scoreWindow` — which the strip
  // over the board still takes — is the last four moves and stops at the
  // playhead. A column has the room to be the page instead, and the page is what
  // a viewer can follow: it says where the position came from rather than only
  // what has just happened.
  //
  // WHICH MEANS IT SCROLLS, and the arithmetic is the whole of it. `visible` is
  // how many rows the band holds, the playing move's row is held `SCORE_ANCHOR`
  // of the way down it, and `first` is that clamped to both ends of the score.
  // The clamp is what makes the opening and the finish read properly: the list
  // does not move at all until the playhead has walked down to the anchor, and
  // it stops once the last move reaches the foot of the band, so the playhead
  // walks the rest of the way down to meet it.
  //
  // IT STEPS A ROW AT A TIME rather than gliding, which is not a compromise.
  // `paintOverlay` repaints when its key changes and the key changes on the
  // half-move, so a list interpolated between two frames of the SAME move would
  // read as a wobble rather than as travel. A score follows a game in rows.
  // 26 over 32. The rows were 36 apart while the column was 840 wide; the third
  // count line the narrower measure forced took the seventh row's room, and this
  // is where it comes back — a monospaced score at 1.23 line spacing is a list,
  // where prose at that spacing would be a wall. Seven rows is fourteen
  // half-moves of context, which is what the list is for.
  const lineH = 32 * S;
  const size = 26 * S;
  const bandFrom = y + 42 * S;
  const rows = panel.rows;
  if (p.moveList && rows.length) {
    const visible = Math.max(1, Math.floor((bandTo - bandFrom) / lineH) + 1);
    // NOT clamped to 0, which is the difference between "the game has not started"
    // and "the game is on its first move". `view.ply` is -1 through the intro, and
    // clamping put the highlight on 1.d4 for three seconds before anybody had
    // played it. Left at -1 nothing equals it and everything is ahead of it, so
    // the list draws as the score about to be played.
    const at = s.at;
    const playing = Math.min(Math.max(0, Math.floor(at / 2)), rows.length - 1);
    const first = clamp(playing - Math.round((visible - 1) * SCORE_ANCHOR),
      0, Math.max(0, rows.length - visible));

    g.font = `600 ${Math.round(size)}px ${MONO}`;
    // The widest number in the WHOLE score sets the column the moves start in,
    // read off the last row rather than off the rows on screen: a width measured
    // from what is visible would step the moves sideways at move 10 and again at
    // move 100, which inside one game in this library happens twice.
    const numW = g.measureText(`${rows[rows.length - 1].no}.`).width;
    for (let i = first; i < Math.min(rows.length, first + visible); i++) {
      const row = rows[i];
      const ry = bandFrom + (i - first) * lineH;
      g.font = `600 ${Math.round(size)}px ${MONO}`;
      // Dimmer behind the playhead and dimmer still ahead of it, so the eye finds
      // the row without the highlight having to do all the work.
      g.fillStyle = row.white.index > at
        ? 'rgba(226,236,245,0.20)' : 'rgba(226,236,245,0.40)';
      g.fillText(`${row.no}.`, pad, ry);
      let tx = pad + numW + 16 * S;
      for (const cell of [row.white, row.black]) {
        if (!cell) continue;
        if (s.hideAhead && cell.index > at) continue;
        const w = g.measureText(cell.san).width;
        if (cell.index === at) {
          g.fillStyle = 'rgba(255,232,150,0.95)';
          roundRect(g, tx - 9 * S, ry - size * 0.88, w + 18 * S, size * 1.24, 8 * S);
          g.fill();
          g.fillStyle = '#12100a';
        } else {
          // FAINT AHEAD OF THE PLAYHEAD, which is where the strip's rule about not
          // spoiling the next move ends up once the next move is on the page
          // anyway. In a list of the score it cannot be hidden; it can be set so
          // that reading ahead is a choice rather than the first thing seen.
          g.fillStyle = cell.index > at
            ? 'rgba(226,236,245,0.24)' : 'rgba(226,236,245,0.58)';
        }
        g.fillText(cell.san, tx, ry);
        tx += w + 18 * S;
      }
    }

    // The soft edges that say the band is a viewport rather than the whole score.
    // Only at an end the score actually runs past — a fade at the top on move one
    // would have the list pretending to hide something that is not there.
    const fade = 30 * S;
    if (first > 0) {
      const top = bandFrom - size;
      const grad = g.createLinearGradient(0, top, 0, top + fade);
      grad.addColorStop(0, GROUND);
      grad.addColorStop(1, 'rgba(10,13,18,0)');
      g.fillStyle = grad;
      g.fillRect(0, top, W, fade);
    }
    if (first + visible < rows.length) {
      const bot = bandFrom + (visible - 1) * lineH + size * 0.4;
      const grad = g.createLinearGradient(0, bot - fade, 0, bot);
      grad.addColorStop(0, 'rgba(10,13,18,0)');
      grad.addColorStop(1, GROUND);
      g.fillStyle = grad;
      g.fillRect(0, bot - fade, W, fade);
    }
  }

  // ---- the count nobody watching this game should trust, then the clock ---
  // THREE SHORT LINES, and the number of them is the measure's doing rather than
  // a preference. The strip over the board says this on one line; a column two
  // thirds as wide as it used to be will not take even two — "White has lost
  // Q R B B N P P P P P P" with the balance on the end of it is 51 characters,
  // which at 21px mono is 643 pixels against the 548 there are. So the balance
  // gets its own line, and the two heaps get one each. The rows above are
  // measured to leave room for all three; see `bandTo`.
  const lead = s.balance > 0 ? `Black +${s.balance}` : s.balance < 0 ? `White +${-s.balance}` : 'Level';
  g.font = `${Math.round(21 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(226,236,245,0.55)';
  if (s.eval) drawEvalBar(g, pad, countY - 2 * 28 * S, maxW, S, s.eval, MONO);
  else g.fillText(lead, pad, countY - 2 * 28 * S);
  g.font = `${Math.round(21 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(226,236,245,0.55)';
  g.fillText(`White has lost ${s.whiteLost.map(glyph).join(' ') || '—'}`, pad, countY - 28 * S);
  g.fillText(`Black has lost ${s.blackLost.map(glyph).join(' ') || '—'}`, pad, countY);

  // ---- the bar, on the frame's own edge ----------------------------------
  // Pinned to the bottom rather than following the stack, because it is the
  // render's progress and not the game's: it belongs where a scrubber belongs.
  // The clock sits just above it for the same reason — the two say the same
  // thing, one in seconds and one as a picture.
  if (p.progressBar) {
    const gap = 8 * S;
    const bar = 7 * S;
    const total = maxW - gap * (s.phases.length - 1);
    const w = total / s.phases.length;
    const by = H - pad - bar;
    let bx = pad;
    s.fill.forEach((f) => {
      g.fillStyle = 'rgba(255,255,255,0.13)';
      roundRect(g, bx, by, w, bar, bar / 2);
      g.fill();
      if (f > 0) {
        g.fillStyle = f >= 1 ? 'rgba(120,220,170,0.80)' : 'rgba(255,255,255,0.92)';
        roundRect(g, bx, by, Math.max(3 * S, w * f), bar, bar / 2);
        g.fill();
      }
      bx += w + gap;
    });
  }
  g.font = `600 ${Math.round(32 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(255,255,255,0.88)';
  g.fillText(`${s.clock} / ${s.total}`, pad, clockY);
}

/**
 * The engine's verdict at `t`: the share of the bar that is White's, and the
 * score as it is printed. It changes when a piece LANDS, not when it sets off —
 * the position the number is about does not exist until then — and eases over
 * half a second so a swing reads as a swing rather than a cut. (A local rather
 * than a module constant: `run()` above starts drawing before this part of the
 * module has been evaluated.)
 */
function evalAt(ctx, view, t) {
  const EVAL_EASE = 0.5;
  const cp = EVALS[ctx.g.id]?.cp;
  if (!cp || cp.length !== ctx.game.plies.length + 1) return null;
  const tl = ctx.timeline;
  let from;
  let to;
  let u = 1;
  if (view.phase === 'intro') {
    from = to = cp[Math.max(0, view.done)];
  } else if (view.phase === 'done') {
    from = to = cp[view.done];
  } else {
    const k = view.k;
    const land = tl.start[k] + tl.moveSec[k];
    from = cp[k];
    to = cp[k + 1];
    u = clamp01((t - land) / EVAL_EASE);
    u = u * u * (3 - 2 * u);
  }
  const share = whiteShare(from) + (whiteShare(to) - whiteShare(from)) * u;
  return { share, text: evalText(u < 0.5 ? from : to), white: (u < 0.5 ? from : to) >= 0 };
}

/**
 * Centipawns to White's share of the bar, on the curve Lichess publishes for its
 * own — a pawn up is about 59%, a rook up about 87% — and a mate fills it.
 */
function whiteShare(cp) {
  if (Math.abs(cp) >= MATE - 1000) return cp > 0 ? 1 : 0;
  return 0.5 + 0.5 * (2 / (1 + Math.exp(-0.00368208 * cp)) - 1);
}

/** "+1.4", "−0.3", "0.0", "M3", "#". */
function evalText(cp) {
  if (Math.abs(cp) >= MATE - 1000) {
    const n = MATE - Math.abs(cp);
    return n === 0 ? '#' : `M${n}`;
  }
  const v = cp / 100;
  if (Math.abs(v) < 0.05) return '0.0';
  return `${v > 0 ? '+' : '−'}${Math.abs(v).toFixed(1)}`;
}

/** The bar on one line of the column: a label, the bar, the score at its end. */
function drawEvalBar(g, x, y, w, S, e, MONO) {
  g.font = `${Math.round(21 * S)}px ${MONO}`;
  g.fillStyle = 'rgba(226,236,245,0.55)';
  const label = 'Engine';
  g.fillText(label, x, y);
  g.font = `700 ${Math.round(26 * S)}px ${MONO}`;
  const numW = g.measureText('M10').width;
  const bx = x + g.measureText(`${label}  `).width;
  const bw = w - (bx - x) - numW - 14 * S;
  // As tall as the line allows: it is the one thing in the column meant to be
  // read from across the room.
  const bh = 22 * S;
  const by = y - bh + 4 * S;
  // Black's side is the ground of the bar and White's is laid over it, so the
  // seam between them is the verdict.
  g.fillStyle = '#272c36';
  roundRect(g, bx, by, bw, bh, bh / 2);
  g.fill();
  if (e.share > 0.002) {
    g.fillStyle = '#ece4d0';
    roundRect(g, bx, by, Math.max(bh, bw * e.share), bh, bh / 2);
    g.fill();
  }
  g.fillStyle = 'rgba(255,255,255,0.35)';
  g.fillRect(bx + bw / 2 - 0.5 * S, by - 3 * S, Math.max(1, S), bh + 6 * S);
  g.textAlign = 'right';
  g.fillStyle = e.white ? '#ffffff' : 'rgba(226,236,245,0.80)';
  g.fillText(e.text, x + w, y);
  g.textAlign = 'left';
}

/** How many rows `text` wraps to at this size — `wrapText`'s rule, not drawn. */
function rowsFor(g, text, maxW, px, family) {
  g.font = `${Math.round(px)}px ${family}`;
  let line = '';
  let n = 1;
  for (const word of String(text || '').split(/\s+/)) {
    const test = line ? `${line} ${word}` : word;
    if (g.measureText(test).width > maxW && line) { n++; line = word; } else line = test;
  }
  return n;
}

/** Piece letters for the material line: figurine would need a font we do not have. */
function glyph(type) { return type === 'p' ? 'P' : type.toUpperCase(); }

// ------------------------------------------------------------- hud plumbing

/**
 * The camera-space point a frame fraction lands on, at depth `d`.
 *
 * WHY THE MATRIX AND NOT THE HALF-ANGLES. 206's version of `makeOverlay` sized
 * its planes as `halfH = d·tan(fov/2)` and `halfW = halfH · camera.aspect`,
 * which is the frustum's cross-section only while the frustum is SYMMETRIC. The
 * panel layout's is not: `setViewOffset` gives the camera a square frustum
 * extended to the right, so its aspect reads 1 while the frame is 16:9 and its
 * horizontal centre is nowhere near the view axis. Both of those would put every
 * overlay in the wrong place, and the second one silently.
 *
 * So the projection is asked instead. `applyMatrix4` on a Vector3 divides
 * through by w, so pushing a point down the matrix and back up its inverse is
 * exact for any frustum three can build — offset, skewed or plain — and there is
 * no second copy of `updateProjectionMatrix`'s arithmetic to keep in step.
 *
 * `fx`/`fy` are fractions of the FRAME from its top left, which is how a layout
 * reads and how a canvas is measured. The probe is what turns the depth into the
 * ndc z that lands on it.
 */
function framePoint(THREE, camera, fx, fy, d) {
  const probe = new THREE.Vector3(0, 0, -d).applyMatrix4(camera.projectionMatrix);
  return new THREE.Vector3(fx * 2 - 1, 1 - fy * 2, probe.z)
    .applyMatrix4(camera.projectionMatrixInverse);
}

/**
 * A canvas over a rectangle of the frame, hung on the camera.
 *
 * CSS would be easier and is not available: the capture path reads pixels back
 * out of the WebGL canvas, so anything drawn in the DOM is simply absent from
 * the video.
 *
 * `rect` is `{x0, y0, x1, y1}` as fractions of the frame from its top left —
 * an anchor-and-inset spelling was here first and said the same things less
 * directly, and it had no way to name a rectangle that touches neither the top
 * nor the bottom, which is what the writing column is.
 */
function makeOverlay(ctx, rect) {
  const { THREE, camera } = ctx;
  const { x0, y0, x1, y1 } = rect;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(2, Math.round(ctx.width * (x1 - x0)));
  canvas.height = Math.max(2, Math.round(ctx.height * (y1 - y0)));
  const g2 = canvas.getContext('2d');
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  const mat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, depthTest: false, depthWrite: false, toneMapped: false,
  });
  const d = Math.max(camera.near * 3, 0.05);
  const a = framePoint(THREE, camera, x0, y0, d);
  const b = framePoint(THREE, camera, x1, y1, d);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(Math.abs(b.x - a.x), Math.abs(a.y - b.y)), mat);
  mesh.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, -d);
  mesh.renderOrder = 100;
  camera.add(mesh);
  return { canvas, g2, tex, mesh, mat, last: null };
}

/** Redraw an overlay only when what it says has changed. */
function paintOverlay(ov, show, draw, key) {
  ov.mesh.visible = !!show;
  if (!show) return;
  if (ov.last === key) return;
  ov.last = key;
  const g = ov.g2;
  g.clearRect(0, 0, ov.canvas.width, ov.canvas.height);
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  draw(g, ov.canvas.width, ov.canvas.height);
  ov.tex.needsUpdate = true;
}

/** Writing over a picture needs a shadow, not a box. */
function shadow(g, blur) {
  g.shadowColor = 'rgba(0,0,0,0.85)';
  g.shadowBlur = blur;
  g.shadowOffsetY = blur * 0.12;
}
function noShadow(g) {
  g.shadowColor = 'rgba(0,0,0,0)';
  g.shadowBlur = 0;
  g.shadowOffsetY = 0;
}

function roundRect(g, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.arcTo(x + w, y, x + w, y + h, rr);
  g.arcTo(x + w, y + h, x, y + h, rr);
  g.arcTo(x, y + h, x, y, rr);
  g.arcTo(x, y, x + w, y, rr);
  g.closePath();
}

/** One line, shrunk until it fits. */
function fitText(g, text, x, y, maxW, px, weight, family) {
  let size = px;
  for (let i = 0; i < 24; i++) {
    g.font = `${weight ? `${weight} ` : ''}${Math.round(size)}px ${family}`;
    if (g.measureText(text).width <= maxW || size < px * 0.55) break;
    size *= 0.94;
  }
  g.fillText(text, x, y);
  return size;
}

/**
 * A paragraph, wrapped, capped at `lines` — the note is a sentence, not an essay.
 *
 * Returns the baseline of the LAST line it drew, which is what a caller stacking
 * something underneath needs and cannot work out for itself: how many lines a
 * string takes at a given measure is this function's business. The panel stacks;
 * the card and the opener do not and ignore it.
 *
 * `weight` is optional and there because the panel wraps its LEAD as well as its
 * note, and a lead is 500 rather than book weight. Left off, the font is set the
 * way it always was.
 */
function wrapText(g, text, x, y, maxW, px, lead, family, lines, weight = '') {
  g.font = `${weight ? `${weight} ` : ''}${Math.round(px)}px ${family}`;
  const words = String(text || '').split(/\s+/);
  let line = '', n = 0, last = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (g.measureText(test).width > maxW && line) {
      last = y + n * lead;
      g.fillText(line, x, last);
      n++;
      line = word;
      if (n >= lines) { line = ''; break; }
    } else {
      line = test;
    }
  }
  if (n < lines && line) {
    last = y + n * lead;
    g.fillText(line, x, last);
  }
  return last;
}

// ---------------------------------------------------------------- helpers

/** Seeded PRNG — the same seed gives the same sequence on every capture run, so
 *  the sparks are identical in the preview and in all eight render browsers. */
function makeRng(seed = 1) {
  let x = seed >>> 0;
  return function rng() {
    x = (x + 0x6d2b79f5) >>> 0;
    let n = Math.imul(x ^ (x >>> 15), 1 | x);
    n = (n + Math.imul(n ^ (n >>> 7), 61 | n)) ^ n;
    return ((n ^ (n >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(x, a, b) { return Math.min(b, Math.max(a, x)); }
function clamp01(x) { return Math.min(1, Math.max(0, x)); }
function smoothstep(t) { const u = clamp01(t); return u * u * (3 - 2 * u); }
/** The signed shortest way round from one bearing to another. */
function wrapDeg(d) { return ((d + 180) % 360 + 360) % 360 - 180; }

function clockText(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * What the timeline came out as.
 *
 * The lines worth reading are the last two: the game has to finish inside the
 * video, and if `Length` cannot hold the requested pace then everything is
 * compressed to fit and this says so rather than letting the mate fall off the
 * end. The move-by-move table is there to show where the time actually went —
 * four moves take a fifth of the game between them, which is the intent.
 */
function report(ctx) {
  const p = ctx.params;
  const tl = ctx.timeline;
  const plies = ctx.game.plies;
  // A GAME STILL BEING PLAYED, which is every game in `chess-app/play/` until
  // somebody wins one, and a state no video has ever been in. `*` is the result
  // a score in progress carries — the same token PGN uses, and what
  // `registerLiveGame` sets — as against the `1-0` every game in `games.js` has.
  //
  // Everything below is about a FINISHED game: how long the mate is held for at
  // this video length, what each side gave up by the end, and the assertion at
  // the foot that the mating net really is mate. Against a game with eleven moves
  // played and no result yet, all of it is either meaningless or a complaint that
  // the game is not over — which it is not, and which is not news.
  if (!plies.length || ctx.g.result === '*') return;
  const net = ctx.net;
  const mat = material(ctx.game.positions, plies.length);
  const sway = cameraSway(ctx);

  const biggest = plies
    .map((ply, k) => ({ ply, k, slot: tl.slot[k] }))
    .sort((a, b) => b.slot - a.slot)
    .slice(0, 5)
    .map((row) => `${row.ply.moveNo}${row.ply.side === 'w' ? '.' : '…'}${row.ply.san} ${row.slot.toFixed(1)}s`);

  console.log([
    `207 ${ctx.g.title.toLowerCase()} — ${ctx.g.white} vs ${ctx.g.black}, ${ctx.g.event} ${ctx.g.date}`,
    `  ${ctx.g.opening} · played on ${p.board === 'custom' ? 'a custom set' : (SETS[p.board] || SETS[ctx.g.board]).label}`
      + ` with ${MEN_WORDS[p.men] || MEN_WORDS.club}`,
    `  ${plies.length} half-moves, ${plies.filter((x) => x.move.capturedSq >= 0).length} captures, ${plies.filter((x) => x.check).length} checks`,
    endingOf(ctx.g) === 'mate'
      ? `  mate confirmed: ${plies[plies.length - 1].mate} — king ${squareName(net.king)}, checked by ${net.checkers.map(squareName).join('+')}`
      : `  ${endingOf(ctx.g)} on ${squareName(net.king)} — ${plies[plies.length - 1].san}, ${net.squares.length} square(s) drawn`,
    `  ${endingOf(ctx.g) === 'mate' ? describeNet(ctx.game.final, net) : describeFinish(ctx.game.final, net)}`,
    `  White gave up ${describeLost(mat.whiteLost)}; Black lost ${describeLost(mat.blackLost)} (count says Black +${mat.balance})`,
    `  a plain move gets ${tl.unit.toFixed(2)}s · longest: ${biggest.join(' · ')}`,
    // "mate" only when the clip actually reaches it. A phase rendered on its own
    // ends on whatever its last half-move is, and a report calling that the mate
    // is a report lying about the one fact this file exists to confirm.
    `  intro ${tl.intro.toFixed(1)}s, ${tl.clipped ? 'the last move of the clip' : 'mate'} lands at ${clockText(tl.gameEnd)}, held for ${tl.hold.toFixed(1)}s of a ${clockText(tl.length)} video`
      + (openerHold(ctx)
        ? ` — opening on the video's title for ${openerHold(ctx).toFixed(1)}s`
        : ' — no opening title'),
    `  camera turns ${sway.median.toFixed(1)} deg/s typical · ${sway.p99.toFixed(1)} p99 · ${sway.peak.toFixed(1)} worst`
      + ` · aim slides at most ${sway.shunt.toFixed(2)} squares/s`
      + `  (smoothed ${p.steady.toFixed(2)}s, slew limit ${MAX_SLEW})`,
    // The count, beside the smoothness, because they are different questions and
    // this is the one a viewer answers with "the board will not sit still". One
    // move every ten seconds or so is a shot that holds; one per half-move, which
    // is what this was, is a shot that never lands.
    `  ${ctx.camTrack.moves} camera moves over ${plies.length} half-moves`
      + ` — one every ${(tl.length / Math.max(1, ctx.camTrack.moves)).toFixed(1)}s`
      + ` (holding the frame under ${p.hold}°, drift ${p.drift.toFixed(2)})`,
  ].join('\n'));

  // 205 warns above 70 on a chase camera; a board is a static subject and a
  // shot that turns faster than this is a shot that has stopped being readable.
  if (sway.peak > 55) {
    console.warn(`207: the camera peaks at ${sway.peak.toFixed(0)} deg/s — raise Steadiness or Length.`);
  }
  // Now that Length is the only time control, the useful thing to say is not
  // "it is too fast" but WHAT LENGTH THIS GAME WANTS — the Game of the Century's
  // 82 half-moves need well over twice the Immortal Game's, and there is no
  // second dial left to reach for. Scaled off the measured pace: the play window
  // has to grow by 0.8/unit, and the bookends come back on top.
  if (tl.unit < 0.8) {
    const play = tl.length - tl.intro - tl.outro;
    const wanted = play * (0.8 / tl.unit) + tl.intro + tl.outro;
    console.warn(`207: ${tl.unit.toFixed(2)}s per move is faster than a viewer can read the card`
      + ` — ${ctx.g.title} wants a Length of about ${Math.ceil(wanted / 30) * 30}s.`);
  }
  // ONLY FOR A SCORE THAT CLAIMS ONE. A game in `games.js` that says it ends in
  // mate has to actually end in one — see `endingOf` — and an open square in that
  // net is a broken promise worth an error. Games that say 'resigned' or 'drawn'
  // make no such claim and get `finishNet` instead, which has no open squares to
  // report. But this array also holds games that arrived
  // at runtime: a game being played on the play pages (`play/live-game.js`) and a
  // visitor's imported PGN (`chess-app/data/imported.js`), and most real games
  // end in a resignation or a draw. Three console errors saying "this is not
  // mate" about a game that never said it was is the diagnostic crying wolf, and
  // a channel that cries wolf is a channel nobody reads when it is right.
  if (plies[plies.length - 1].mate) {
    for (const cell of net.squares) {
      if (cell.reason === 'open') console.error(`207: ${squareName(cell.square)} is open — this is not mate.`);
    }
  }
}
